import RoutingImportWarning from "../components/routing/RoutingImportWarning";
import LoadHistoryDialog from '../components/routing/LoadHistoryDialog';
import EdiStatusBadges, {EDI_STATUS_KEY, withEdiStatusColumn} from '../components/routing/EdiStatusBadges';
import useRoutingEdiSummary from '../hooks/useRoutingEdiSummary';
import {
  strideObservations,
  preserveObservedOperationalRows,
  workbookBytesBase64,
  workbookBytesSha256,
  sendRoutingSave,
} from '../utils/routingLoadReconciliation';
// src/pages/RoutingStatus.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getCurrentUser } from "../utils/auth";
import { usePermissions } from "../utils/permissions";
import * as XLSX from "xlsx";
import ConfirmModal from "../components/ConfirmModal";
import { Menu } from "@headlessui/react";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import DateRangeFilter from "../components/common/DateRangeFilter";
import BolPreviewModal from "../components/bol/BolPreviewModal";
import {
  LegacyIlsPendingVerification,
  LegacyIlsUpdateDialog,
} from "../components/routing/LegacyIlsUpdateDialog";
import {
  executeLegacyIlsUpdate,
  classifyHistoricalUpdateFallback,
  loadPendingLegacyIlsUpdates,
  previewLegacyIlsUpdate,
  verifyLegacyIlsUpdate,
} from "../utils/ilsLegacyUpdate";
import { filterRowsByAccess, getUserAccessInfo } from "../utils/accessFilters";
import useAccessOverrideVersion from "../hooks/useAccessOverrideVersion";
import { buildAccessHeaders } from "../utils/accessHeaders";
import {
  adaptRoutingStatusRows,
  canonicalizeRoutingWorkbookIdentityRows,
  detectRoutingStatusFormat,
  LEGACY_ROUTING_STATUS_HEADERS,
  normalizeRoutingLoadId,
  STRIDE_INFORMATION_HEADERS,
} from "../utils/routingStatusStrideAdapter";
import { shouldCarryForwardUnmatchedHistoryRow } from "../utils/routingStatusHistoryMerge";
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsLeft,
  ChevronsRight,
  ChevronsUpDown,
  Info,
  RotateCw,
  X,
} from "lucide-react";

const STORAGE_DATA_KEY = "pp_routing_status_dataset";
const STORAGE_META_KEY = "pp_routing_status_meta";
const STORAGE_WIDTHS_KEY = "pp_routing_status_column_widths";
const STORAGE_VISIBLE_COLUMNS_KEY = "pp_routing_status_visible_columns";
const VISIBLE_COLUMNS_VERSION = "v4";
const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const ROUTING_STATUS_LABEL = "Routing Status";
const PREFERRED_SHEET_NAMES = ["Routing Status", "Routing_Status", "Routing"];
const PAGE_OPTIONS = [25, 50, 100, "All"];
const MIN_COL_WIDTH = 80;
const MAX_COL_WIDTH = 300;
const DEFAULT_COL_WIDTH = 140;
const SELECT_COLUMN_KEY = "__row_select__";
const SELECT_COLUMN_DEFAULT_WIDTH = 56;
const EXPORT_SHEET_NAME = "Routing Status";
const PO_META_STORAGE_KEY = "pp_po_tracker_meta";

const STATUS_LABEL = "Status";
const FIRST_UPDATE_LABEL = "FirstUpdatePT";
const LAST_UPDATE_LABEL = "LastUpdatePT";
const D2M_LABEL = "D2M";
const PO_NUMBER_LABEL = "PO Number";
const SHIPMENT_NUMBER_LABEL = "Shipment #";
const OUTBOUND_CODE_LABEL = "Outbound Code";
const LOAD_ID_LABEL = "Load ID";
const FLEET_LOAD_ID_LABEL = "Fleet Load ID";
const CUST_CODE_LABEL = "Cust Code";
const WMS_LABEL = "WMS";
const SHIP_POINT_LABEL = "Ship Point";
const SHIPPED_DATE_LABEL = "Shipped Date";
const FEDEX_STATUS_LABEL = "FedEx";
const FEDEX_TRACKING_LABEL = "Tracking #";
const ROUTING_ROW_ID_KEY = "__routingRowId";
const ROUTING_UID_KEY = "__routingUid";
const ROUTING_ROW_SUPERSEDED_KEY = "__isSuperseded";
const ROUTING_ROW_SUPERSEDED_BY_UID_KEY = "__supersededByRoutingUid";
const ROUTING_ROW_SUPERSEDED_BY_OUTBOUND_KEY = "__supersededByOutboundCode";
const FEDEX_STATUS_KEY = "__fedexStatus";
const FEDEX_TRACKING_KEY = "__fedexTracking";
const FEDEX_SUMMARY_KEY = "__fedexLabelSummary";
const FEDEX_LABEL_STATUS_LABELS = {
  active: "Active",
  voided: "Voided",
  superseded: "Superseded",
  inactive: "Not Printable",
};
const FEDEX_MAIN_STATUS_STYLES = {
  NOT_CREATED: "border border-slate-300 bg-slate-100 text-slate-700",
  CREATED: "border border-emerald-300 bg-emerald-100 text-emerald-800",
  VOIDED: "border border-rose-300 bg-rose-100 text-rose-800",
  SUPERSEDED: "border border-amber-300 bg-amber-100 text-amber-900",
};
const FEDEX_ZPL_WIDTH_MM = 101.6;
const FEDEX_ZPL_HEIGHT_MM = 152.4;
const FEDEX_PDF_PAGE_WIDTH_PT = 288;
const FEDEX_PDF_PAGE_HEIGHT_PT = 432;
const STATUS_COLUMN = { key: "status", label: STATUS_LABEL, type: "text" };
const FIRST_UPDATE_COLUMN = { key: "firstupdatedt", label: FIRST_UPDATE_LABEL, type: "text" };
const LAST_UPDATE_COLUMN = { key: "lastupdatedt", label: LAST_UPDATE_LABEL, type: "text" };
const D2M_COLUMN = { key: "d2m", label: D2M_LABEL, type: "number" };
const OUTBOUND_CODE_COLUMN = { key: "outbound-code", label: OUTBOUND_CODE_LABEL, type: "text" };
const CUST_CODE_COLUMN = { key: "cust-code", label: CUST_CODE_LABEL, type: "text" };
const WMS_COLUMN = { key: "wms", label: WMS_LABEL, type: "text" };
const SHIPPED_DATE_COLUMN = { key: "shipped-date", label: SHIPPED_DATE_LABEL, type: "date" };
const ON_HOLD_COLUMN = { key: "__onHold", label: "On Hold", type: "text" };
const ROW_ID_COLUMN = { key: ROUTING_ROW_ID_KEY, label: "Row ID", type: "text" };
const AUDIT_COLUMNS = [STATUS_COLUMN, FIRST_UPDATE_COLUMN, LAST_UPDATE_COLUMN];
const AUDIT_KEY_SET = new Set(AUDIT_COLUMNS.map((col) => col.key));
const DERIVED_COLUMNS = [...AUDIT_COLUMNS, D2M_COLUMN, CUST_CODE_COLUMN, ON_HOLD_COLUMN, WMS_COLUMN, SHIPPED_DATE_COLUMN];
const STATUS_KEY = STATUS_COLUMN.key;
const FIRST_UPDATE_KEY = FIRST_UPDATE_COLUMN.key;
const LAST_UPDATE_KEY = LAST_UPDATE_COLUMN.key;
const D2M_KEY = D2M_COLUMN.key;
const CUST_CODE_KEY = CUST_CODE_COLUMN.key;
const ON_HOLD_KEY = ON_HOLD_COLUMN.key;
const WMS_KEY = WMS_COLUMN.key;
const SHIPPED_DATE_KEY = SHIPPED_DATE_COLUMN.key;
const HIDDEN_COLUMN_KEYS = new Set([ROUTING_ROW_ID_KEY]);
const ROUTING_STATUS_DISPLAY_ORDER = [
  { key: ROUTING_ROW_ID_KEY },
  { label: OUTBOUND_CODE_LABEL },
  { label: PO_NUMBER_LABEL },
  { label: SHIPMENT_NUMBER_LABEL },
  { label: LOAD_ID_LABEL },
  { label: FLEET_LOAD_ID_LABEL },
  { label: FEDEX_STATUS_LABEL },
  { label: FEDEX_TRACKING_LABEL },
  { label: STATUS_LABEL },
  { label: D2M_LABEL },
  { key: ON_HOLD_KEY },
  { label: WMS_LABEL },
  { label: SHIPPED_DATE_LABEL },
  { label: CUST_CODE_LABEL },
  { label: "MABD" },
  { label: "Carrier PU Date" },
  { label: "Cases" },
  { label: "Event Code" },
  { label: "Carrier Name" },
  { label: "Ship Point" },
  { label: "Confirmed Date" },
  { label: "Held/Suspended" },
  { label: "PO Dest" },
  { label: FIRST_UPDATE_LABEL },
  { label: LAST_UPDATE_LABEL },
  { label: "Load P/U Window Start" },
  { label: "Load P/U Window End" },
  { label: "Carrier Due Date" },
  { label: "Industry SCAC" },
  { label: "Mode" },
  { label: "Load Dest" },
  { label: "Load Dest Type" },
  { label: "Load Dest Address" },
  { label: "Loading Method" },
  { label: "Weight" },
  { label: "Cube" },
  { label: "Pallets" },
  { label: "PO Type" },
  { label: "Department" },
];

function formatFedexLabelStatus(status) {
  const key = typeof status === "string" ? status.trim().toLowerCase() : "";
  return FEDEX_LABEL_STATUS_LABELS[key] || (key ? key.replace(/_/g, " ") : "-");
}

function getFedexLabelErrorMessage(code, fallback) {
  const key = typeof code === "string" ? code.trim() : "";
  const text = typeof fallback === "string" ? fallback.trim() : "";
  const mapped = {
    label_not_printable: "This label is not printable. Voided or superseded labels cannot be downloaded.",
    label_already_voided: "This label was already voided.",
    label_superseded: "This label was superseded by a newer label and can no longer be used.",
    label_not_voidable: "Only the current active label can be voided.",
    direct_print_not_available:
      "Direct Print is not available for this label. Open the 4x6 PDF preview and print from the browser instead.",
    pdf_download_generated_client_side:
      "This label is stored as ZPL. PortPilot will generate the PDF fallback from the saved thermal output.",
    no_active_printable_label:
      "No active printable label exists for this BOL. The previous label may have been voided or superseded.",
    existing_label_missing:
      "No active printable label exists for this BOL. The previous label may have been voided or superseded.",
  };
  return mapped[key] || text || "Unable to process FedEx labels.";
}

function formatFedexFailureDetail(row, includeDiagnosticPath = false) {
  const base = getFedexLabelErrorMessage(row?.errorCode, row?.error || row?.message || "Failed");
  const extras = [];
  if (row?.fedexErrorCode) extras.push(`Code: ${row.fedexErrorCode}`);
  if (row?.httpStatus) extras.push(`HTTP: ${row.httpStatus}`);
  if (row?.fedexTransactionId) extras.push(`Transaction ID: ${row.fedexTransactionId}`);
  if (row?.diagnosticPayloadPath) {
    extras.push(includeDiagnosticPath ? `Diagnostic: ${row.diagnosticPayloadPath}` : "Diagnostic payload saved.");
  }
  return extras.length ? `${base} (${extras.join(" | ")})` : base;
}

function hasFedexPdfFallback(row) {
  return !!((row?.downloadUrl || row?.directPrintUrl) && row?.labelStatus === "active");
}
const DEFAULT_VISIBLE_LABELS = [
  OUTBOUND_CODE_LABEL,
  PO_NUMBER_LABEL,
  SHIPMENT_NUMBER_LABEL,
  LOAD_ID_LABEL,
  FLEET_LOAD_ID_LABEL,
  "Walmart Load ID", "iLS Load (Last Verified)", "Load Sync", "Verified At", "History",
  FEDEX_STATUS_LABEL,
  FEDEX_TRACKING_LABEL,
  STATUS_LABEL,
  D2M_LABEL,
  ON_HOLD_COLUMN.label,
  WMS_LABEL,
  SHIPPED_DATE_LABEL,
  "MABD",
  "Carrier PU Date",
  "Cases",
  "Event Code",
  "Carrier Name",
  "Ship Point",
  "Confirmed Date",
  "Held/Suspended",
  LAST_UPDATE_LABEL,
];

const STATUS_ROW_CLASSES = {
  New: "bg-green-100",
  Update: "bg-rose-50",
  "Not Found": "bg-purple-100",
  Open: "",
};
const CHANGED_CELL_CLASS = "bg-red-100 text-red-900 font-semibold";

const PST_TIMEZONE = "America/Los_Angeles";
const PST_LOCALE = "en-US";
const PST_FORMAT_OPTIONS = {
  timeZone: PST_TIMEZONE,
  month: "2-digit",
  day: "2-digit",
  year: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
};
const PST_SHORT_DATE_FORMATTER = new Intl.DateTimeFormat(PST_LOCALE, {
  timeZone: PST_TIMEZONE,
  month: "2-digit",
  day: "2-digit",
});
const PST_SHORT_TIME_FORMATTER = new Intl.DateTimeFormat(PST_LOCALE, {
  timeZone: PST_TIMEZONE,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const D2M_SKIP_STATUSES = new Set(["not found"]);
const D2M_TERMINAL_WMS_STATUSES = new Set(["shipped", "cancelled"]);
const CORRECTION_IMPORT_MODE_SKIP_MISMATCHED_CASES = "skip_mismatched_cases";
const CORRECTION_IMPORT_MODE_PRESERVE_CURRENT_CASES = "preserve_current_cases";

const isRoutingCorrectionCasesConflict = (payload) =>
  payload?.error === "routing_correction_cases_mismatch" ||
  payload?.conflictType === "correction_protected_cases_mismatch";

const parseTimestampValue = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (value instanceof Date) {
    const ts = value.getTime();
    return Number.isNaN(ts) ? null : new Date(ts);
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value);
  }
  const text = String(value).trim();
  if (!text) return null;
  const utcLikeMatch = text.match(
    /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/
  );
  if (utcLikeMatch) {
    const normalizedUtc = text.replace(" ", "T");
    const parsedUtc = new Date(`${normalizedUtc}Z`);
    if (!Number.isNaN(parsedUtc.getTime())) return parsedUtc;
  }
  let parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  const normalized = text.replace(" ", "T");
  parsed = new Date(normalized.endsWith("Z") ? normalized : `${normalized}Z`);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  return null;
};

const formatValueToPst = (value) => {
  const parsed = parseTimestampValue(value);
  if (!parsed) return "";
  return parsed.toLocaleString(PST_LOCALE, PST_FORMAT_OPTIONS);
};

const formatValueToShortPst = (value) => {
  const parsed = parseTimestampValue(value);
  if (!parsed) return "";
  const datePart = PST_SHORT_DATE_FORMATTER.format(parsed);
  const timePart = PST_SHORT_TIME_FORMATTER.format(parsed);
  return `${datePart} ${timePart}`;
};

const canonicalizeHeaderLabel = (value) =>
  (value === undefined || value === null ? "" : String(value))
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const OUTBOUND_CODE_CANONICAL = canonicalizeHeaderLabel(OUTBOUND_CODE_LABEL);
const WMS_CANONICAL = canonicalizeHeaderLabel(WMS_LABEL);

const STICKY_HEADER_SET = new Set([
  canonicalizeHeaderLabel(OUTBOUND_CODE_LABEL),
  canonicalizeHeaderLabel(PO_NUMBER_LABEL),
  canonicalizeHeaderLabel(LOAD_ID_LABEL),
]);

const findHeaderKeyByCanonicalLabel = (headers, label, fallbackKey = "") => {
  const canonical = canonicalizeHeaderLabel(label);
  const match = headers.find((header) => canonicalizeHeaderLabel(header.label) === canonical);
  return match?.key || fallbackKey || "";
};

const normalizeNumberValue = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeComparisonValue = (value) => {
  if (value === undefined || value === null) return "";
  if (typeof value === "number") return Number.isFinite(value) ? value : "";
  if (value instanceof Date) return value.getTime();
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  const numericCandidate = Number(trimmed.replace(/,/g, ""));
  if (!Number.isNaN(numericCandidate) && /^-?[0-9.,]+$/.test(trimmed)) {
    return numericCandidate;
  }
  return trimmed;
};

const diffRecords = (newRecord, oldRecord, comparisonPlan) => {
  const diff = {};
  let hasChanges = false;
  comparisonPlan.forEach(({ newKey, oldKey }) => {
    const newValue = newRecord?.[newKey];
    const oldValue = oldRecord?.[oldKey];
    if (normalizeComparisonValue(newValue) !== normalizeComparisonValue(oldValue)) {
      hasChanges = true;
      diff[newKey] = { previous: oldValue, changed: true };
    }
  });
  return { hasChanges, diff };
};

const buildZeroPoolKey = (cust, po, casesValue) => `${cust}::${po}::${casesValue}`;

const FORCE_TEXT_LABELS = new Set(
  [
    OUTBOUND_CODE_LABEL,
    PO_NUMBER_LABEL,
    LOAD_ID_LABEL,
    "Load Dest",
    "Load Dest Type",
    "Load Dest Address",
    "Carrier Name",
    "Industry SCAC",
    "Mode",
    "Ship Point",
    "Loading Method",
    "Cases",
    "Weight",
    "Cube",
    "Pallets",
    "Event Code",
    "PO Type",
    "Department",
    "Held/Suspended",
    "PO Dest",
    CUST_CODE_LABEL,
    STATUS_LABEL,
    ON_HOLD_COLUMN.label,
    WMS_COLUMN.label,
  ].map(canonicalizeHeaderLabel)
);

const FORCE_NUMBER_LABELS = new Set(["Cases", "Weight", "Cube", "Pallets"].map(canonicalizeHeaderLabel));

const FORCE_DATE_LABELS = new Set(
  [
    "Load P/U Window Start",
    "Load P/U Window End",
    "MABD",
    "Carrier PU Date",
    "Carrier Due Date",
    "Confirmed Date",
    SHIPPED_DATE_LABEL,
  ].map(canonicalizeHeaderLabel)
);
const FORCE_TYPE_MAP = new Map();
FORCE_TEXT_LABELS.forEach((label) => FORCE_TYPE_MAP.set(label, "text"));
FORCE_NUMBER_LABELS.forEach((label) => FORCE_TYPE_MAP.set(label, "number"));
FORCE_DATE_LABELS.forEach((label) => FORCE_TYPE_MAP.set(label, "date"));
const MULTI_SELECT_LABELS = new Set(
  [
    "Status",
    "Event Code",
    "Mode",
    "Ship Point",
    "PO Type",
    "Department",
    CUST_CODE_LABEL,
    WMS_LABEL,
    OUTBOUND_CODE_LABEL,
  ].map(canonicalizeHeaderLabel)
);
['Load Sync', 'Walmart State', 'Verification Freshness'].forEach(label => MULTI_SELECT_LABELS.add(canonicalizeHeaderLabel(label)));

const MULTI_VALUE_TEXT_LABELS = new Set(["PO Number", "Load ID"].map(canonicalizeHeaderLabel));
const ROUTING_FILENAME_DATE_REGEX = /_(\d{1,2})-(\d{1,2})-(\d{4})(?:_(\d{6}))?/i;
const PO_FILENAME_DATE_REGEX = /_(\d{1,2})(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)(\d{4})_/i;
const PO_FILENAME_MONTH_INDEX = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};
const VERSION_MS_PER_DAY = 24 * 60 * 60 * 1000;
const VERSION_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

const EXPECTED_ROUTING_HEADERS = LEGACY_ROUTING_STATUS_HEADERS;

const parseRoutingFilenameVersionDate = (filename = "") => {
  if (!filename) return null;
  const match = filename.match(ROUTING_FILENAME_DATE_REGEX);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  if (!Number.isFinite(month) || !Number.isFinite(day) || !Number.isFinite(year)) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeVersionDateValue = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
};

const diffVersionDates = (incoming, current) => {
  const normalizedIncoming = normalizeVersionDateValue(incoming);
  const normalizedCurrent = normalizeVersionDateValue(current);
  if (!normalizedIncoming || !normalizedCurrent) return null;
  const diff = normalizedIncoming.getTime() - normalizedCurrent.getTime();
  return Math.round(diff / VERSION_MS_PER_DAY);
};

const formatVersionDateLabel = (date) => {
  const normalized = normalizeVersionDateValue(date);
  if (!normalized) return "";
  return VERSION_DATE_FORMATTER.format(normalized);
};

const parsePoFilenameVersionDate = (filename = "") => {
  if (!filename) return null;
  const match = filename.match(PO_FILENAME_DATE_REGEX);
  if (!match) return null;
  const day = Number(match[1]);
  const monthKey = match[2]?.toLowerCase();
  const year = Number(match[3]);
  if (!Number.isFinite(day) || !Number.isFinite(year)) return null;
  if (!Object.prototype.hasOwnProperty.call(PO_FILENAME_MONTH_INDEX, monthKey)) return null;
  const date = new Date(Date.UTC(year, PO_FILENAME_MONTH_INDEX[monthKey], day));
  return Number.isNaN(date.getTime()) ? null : date;
};

const DUPLICATE_HEADER_RENAMES = {};

const FILTER_MODES = {
  text: "text",
  date: "date",
  range: "range",
  multiSelect: "multiSelect",
  multiText: "multiText",
  fedexStatus: "fedexStatus",
  fedexTracking: "fedexTracking",
};

const FEDEX_FILTER_OPTIONS = [
  { value: "NOT_CREATED", label: "Not Created" },
  { value: "CREATED", label: "Created" },
  { value: "VOIDED", label: "Voided" },
  { value: "SUPERSEDED", label: "Superseded" },
];

const getFilterModeForHeader = (header) => {
  if (!header) return FILTER_MODES.text;
  if (header.type === "date") return FILTER_MODES.date;
  if (header.key === ON_HOLD_KEY) return FILTER_MODES.multiSelect;
  if (header.key === D2M_KEY) return FILTER_MODES.range;
  if (header.key === FEDEX_STATUS_KEY) return FILTER_MODES.fedexStatus;
  if (header.key === FEDEX_TRACKING_KEY) return FILTER_MODES.fedexTracking;
  const canonical = canonicalizeHeaderLabel(header.label);
  if (MULTI_SELECT_LABELS.has(canonical)) return FILTER_MODES.multiSelect;
  if (MULTI_VALUE_TEXT_LABELS.has(canonical)) return FILTER_MODES.multiText;
  return FILTER_MODES.text;
};

const normaliseTextValue = (value) => String(value ?? "").trim().toLowerCase();
const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : 0;
};
const formatQty = (value) => toFiniteNumber(value).toLocaleString(undefined, { maximumFractionDigits: 2 });

const parseMultiValueQuery = (value) =>
  Array.from(
    new Set(
      String(value || "")
        .split(/[\s,]+/)
        .map((token) => token.trim().toLowerCase())
        .filter(Boolean)
    )
  );

const parsePoFilterTokens = (value) => {
  const tokens = String(value || "")
    .split(/[\s,;]+/)
    .map((token) => token.trim())
    .filter(Boolean);
  const seen = new Set();
  const ordered = [];
  tokens.forEach((token) => {
    const normalized = token.toLowerCase();
    if (seen.has(normalized)) return;
    seen.add(normalized);
    ordered.push({ raw: token, normalized });
  });
  return ordered;
};

const normalizeDrilldownIsoDate = (value) => {
  const text = String(value || "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
};

const normalizeDrilldownNumeric = (value) => {
  const text = String(value ?? "").replace(/,/g, "").trim();
  if (!text) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseIsoDateOnly = (isoText) => {
  const text = String(isoText || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
  const [yearRaw, monthRaw, dayRaw] = text.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  const parsed = new Date(year, month - 1, day);
  if (Number.isNaN(parsed.getTime())) return null;
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) return null;
  return parsed;
};

const isValidWalmartWeekRange = (fromIso, toIso) => {
  const fromDate = parseIsoDateOnly(fromIso);
  const toDate = parseIsoDateOnly(toIso);
  if (!fromDate || !toDate) return false;
  const fromDay = fromDate.getDay();
  const toDay = toDate.getDay();
  if (fromDay !== 6 || toDay !== 5) return false; // Saturday -> Friday
  const diffDays = Math.round((toDate.getTime() - fromDate.getTime()) / MS_PER_DAY);
  return diffDays === 6;
};

const parseRoutingDrilldownParams = (searchText = "") => {
  const params = new URLSearchParams(searchText);
  const drillRaw = String(params.get("drill") || "").trim().toLowerCase();
  const dateFieldRaw = String(params.get("date_field") || "").trim().toLowerCase();
  const walmartItemNo = String(params.get("walmart_item_no") || "").trim();
  const shipPoint = String(params.get("ship_point") || "").trim();
  const dateFromRaw = String(params.get("date_from") || "").trim();
  const dateToRaw = String(params.get("date_to") || "").trim();
  const dateFrom = normalizeDrilldownIsoDate(dateFromRaw);
  const dateTo = normalizeDrilldownIsoDate(dateToRaw);
  const dateField = dateFieldRaw === "shipped_date" ? dateFieldRaw : "";
  const drill = drillRaw === "mrp_shipped" ? drillRaw : "";
  const cellValueRaw = params.get("cell_value");
  const mrpValueRaw = params.get("mrp_value");
  const mrpCellValue =
    cellValueRaw !== null
      ? normalizeDrilldownNumeric(cellValueRaw)
      : mrpValueRaw !== null
        ? normalizeDrilldownNumeric(mrpValueRaw)
        : null;
  if (!dateField && !walmartItemNo && !shipPoint && !drill) return null;
  const cellValueProvided = cellValueRaw !== null || mrpValueRaw !== null;
  const hasDateRangeInputs = Boolean(dateFromRaw || dateToRaw);
  const hasInvalidDateFormat = (dateFromRaw && !dateFrom) || (dateToRaw && !dateTo);
  return {
    drill,
    dateField,
    dateFrom,
    dateTo,
    walmartItemNo,
    shipPoint,
    mrpCellValue,
    cellValueProvided,
    hasDateRangeInputs,
    hasInvalidDateFormat,
  };
};

const buildSkuScopedRoutingSets = (payload, targetSkuUpper) => {
  const matchedRowRefs = new Set();
  const matchedRoutingUids = new Set();
  if (!targetSkuUpper) {
    return { matchedRowRefs, matchedRoutingUids };
  }
  const rows = Array.isArray(payload?.dataset?.data) ? payload.dataset.data : [];
  const itemsByRoutingRowId =
    payload?.itemsByRoutingRowId && typeof payload.itemsByRoutingRowId === "object"
      ? payload.itemsByRoutingRowId
      : {};
  const uidByRowRef = new Map();

  rows.forEach((row) => {
    if (!row || typeof row !== "object") return;
    const rowRef = String(row?.[ROUTING_ROW_ID_KEY] ?? row?.routing_row_id ?? row?.id ?? "").trim();
    const routingUid = String(row?.[ROUTING_UID_KEY] ?? row?.routing_uid ?? "").trim();
    if (!rowRef || !routingUid) return;
    if (!uidByRowRef.has(rowRef)) uidByRowRef.set(rowRef, routingUid);
  });

  Object.entries(itemsByRoutingRowId).forEach(([rowRefRaw, itemsRaw]) => {
    const rowRef = String(rowRefRaw || "").trim();
    const items = Array.isArray(itemsRaw) ? itemsRaw : [];
    if (!rowRef || !items.length) return;
    const skuMatched = items.some((item) => {
      const sku = String(item?.sku || "").trim().toUpperCase();
      if (!sku || sku !== targetSkuUpper) return false;
      return toFiniteNumber(item?.allocQty) > 0;
    });
    if (!skuMatched) return;
    matchedRowRefs.add(rowRef);
    const routingUid = uidByRowRef.get(rowRef);
    if (routingUid) matchedRoutingUids.add(routingUid);
  });

  return { matchedRowRefs, matchedRoutingUids };
};

const DATE_LABEL_RE = /(date|ship|eta|etd|arrive|mabd|dnrb|delivery)/i;
const ALWAYS_TEXT_RE = /(po#|po no|overdue)/i;
const ISO_DATE_RE = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/;
const SHORT_DATE_RE = /^\s*(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s*$/;

const normaliseHeaderLabel = (value) => (value === undefined || value === null ? "" : String(value).trim());

const validateHeaderRow = (headerRow = []) => {
  const normalized = headerRow.map(normaliseHeaderLabel);
  while (normalized.length && normalized[normalized.length - 1] === "") {
    normalized.pop();
  }
  if (
    normalized.length < EXPECTED_ROUTING_HEADERS.length ||
    normalized.length > EXPECTED_ROUTING_HEADERS.length + STRIDE_INFORMATION_HEADERS.length
  ) {
    throw new Error(
      `Import failed: expected ${EXPECTED_ROUTING_HEADERS.length} legacy columns with up to ${STRIDE_INFORMATION_HEADERS.length} Stride information columns, but found ${normalized.length}.`
    );
  }
  for (let i = 0; i < EXPECTED_ROUTING_HEADERS.length; i += 1) {
    if (normalized[i] !== EXPECTED_ROUTING_HEADERS[i]) {
      const expected = EXPECTED_ROUTING_HEADERS[i];
      const found = normalized[i] || "(blank)";
      throw new Error(`Import failed: column ${i + 1} should be "${expected}", but found "${found}".`);
    }
  }
  const informationHeaders = normalized.slice(EXPECTED_ROUTING_HEADERS.length);
  const informationIndexes = informationHeaders.map((header) => STRIDE_INFORMATION_HEADERS.indexOf(header));
  if (
    informationIndexes.some((index) => index < 0) ||
    new Set(informationHeaders).size !== informationHeaders.length ||
    informationIndexes.some((index, position) => position > 0 && index <= informationIndexes[position - 1])
  ) {
    throw new Error("Import failed: unsupported Stride information columns.");
  }
  return normalized;
};

const renameDuplicateHeaders = (headers) => {
  const counts = {};
  return headers.map((label) => {
    const canonical = label.toLowerCase();
    if (!DUPLICATE_HEADER_RENAMES[canonical]) return label;
    counts[canonical] = (counts[canonical] || 0) + 1;
    if (counts[canonical] >= 2) {
      return DUPLICATE_HEADER_RENAMES[canonical];
    }
    return label;
  });
};

export const prepareRowsForImport = (rows) => {
  if (!rows?.length) {
    throw new Error("Import failed: file does not contain any rows.");
  }
  const normalized = validateHeaderRow(rows[0] || []);
  const adjustedHeaders = renameDuplicateHeaders(normalized);
  const nextRows = [...rows];
  nextRows[0] = adjustedHeaders;
  return nextRows;
};

const normalizeLabelKey = (value) => canonicalizeHeaderLabel(value);

const insertAuditHeaders = (headers) => {
  const withoutAudit = headers.filter(
    (header) => !AUDIT_KEY_SET.has(header.key) && header.key !== CUST_CODE_KEY
  );
  const loadIndex = withoutAudit.findIndex(
    (header) => normalizeLabelKey(header.label) === canonicalizeHeaderLabel(LOAD_ID_LABEL)
  );
  const insertIndex = loadIndex >= 0 ? loadIndex + 1 : 2;
  const nextHeaders = [...withoutAudit];
  nextHeaders.splice(
    insertIndex,
    0,
    ...AUDIT_COLUMNS.map((column) => ({ ...column })),
    { ...CUST_CODE_COLUMN }
  );
  return nextHeaders;
};

const normalizeAuditHeaderLabels = (headers = []) =>
  headers.map((header) => {
    if (!header) return header;
    if (header.key === FIRST_UPDATE_KEY && header.label !== FIRST_UPDATE_LABEL) {
      return { ...header, label: FIRST_UPDATE_LABEL, type: "text" };
    }
    if (header.key === LAST_UPDATE_KEY && header.label !== LAST_UPDATE_LABEL) {
      return { ...header, label: LAST_UPDATE_LABEL, type: "text" };
    }
    return header;
  });

const ensureOnHoldHeader = (headers) => {
  const hasHold = headers.some((header) => header.key === ON_HOLD_COLUMN.key);
  if (hasHold) return headers;
  const statusIndex = headers.findIndex((header) => header.key === STATUS_KEY);
  const insertIndex = statusIndex >= 0 ? statusIndex + 1 : headers.length;
  const nextHeaders = [...headers];
  nextHeaders.splice(insertIndex, 0, { ...ON_HOLD_COLUMN });
  return nextHeaders;
};

const ensureD2mHeader = (headers = []) => {
  if (!Array.isArray(headers) || !headers.length) return headers;
  const canonicalD2m = canonicalizeHeaderLabel(D2M_LABEL);
  const existingIndex = headers.findIndex(
    (header) => header.key === D2M_KEY || canonicalizeHeaderLabel(header.label) === canonicalD2m
  );
  const nextHeaders = [...headers];
  let d2mHeader;
  if (existingIndex >= 0) {
    [d2mHeader] = nextHeaders.splice(existingIndex, 1);
    d2mHeader = { ...d2mHeader, key: d2mHeader.key || D2M_KEY, label: D2M_LABEL, type: "number" };
  } else {
    d2mHeader = { ...D2M_COLUMN };
  }
  const statusIndex = nextHeaders.findIndex((header) => header.key === STATUS_KEY);
  const insertIndex = statusIndex >= 0 ? statusIndex + 1 : Math.min(nextHeaders.length, 1);
  nextHeaders.splice(insertIndex, 0, d2mHeader);
  return nextHeaders;
};

const ensureOutboundColumnHeader = (headers, fallbackHeader = null) => {
  if (!Array.isArray(headers) || !headers.length) return headers;
  const outboundCanonical = canonicalizeHeaderLabel(OUTBOUND_CODE_LABEL);
  const hasOutbound = headers.some(
    (header) => canonicalizeHeaderLabel(header.label) === outboundCanonical
  );
  if (hasOutbound) return headers;
  const poCanonical = canonicalizeHeaderLabel(PO_NUMBER_LABEL);
  const insertIndex = headers.findIndex(
    (header) => canonicalizeHeaderLabel(header.label) === poCanonical
  );
  const baseHeader =
    fallbackHeader && canonicalizeHeaderLabel(fallbackHeader.label) === outboundCanonical
      ? { ...fallbackHeader }
      : { ...OUTBOUND_CODE_COLUMN };
  if (!baseHeader.key) baseHeader.key = OUTBOUND_CODE_COLUMN.key;
  baseHeader.label = OUTBOUND_CODE_LABEL;
  const nextHeaders = [...headers];
  nextHeaders.splice(insertIndex >= 0 ? insertIndex : 0, 0, baseHeader);
  return nextHeaders;
};

const ensureOnHoldColumnPosition = (headers = []) => {
  const statusIndex = headers.findIndex((header) => header.key === STATUS_KEY);
  const holdIndex = headers.findIndex((header) => header.key === ON_HOLD_COLUMN.key);
  if (holdIndex === -1 || statusIndex === -1 || holdIndex === statusIndex + 1) {
    return headers;
  }
  const nextHeaders = [...headers];
  const [holdHeader] = nextHeaders.splice(holdIndex, 1);
  nextHeaders.splice(statusIndex + 1, 0, holdHeader);
  return nextHeaders;
};

const ensureWmsColumn = (headers = []) => {
  if (!Array.isArray(headers) || !headers.length) return headers;
  const canonicalWms = canonicalizeHeaderLabel(WMS_LABEL);
  const existingIndex = headers.findIndex(
    (header) => header.key === WMS_KEY || canonicalizeHeaderLabel(header.label) === canonicalWms
  );
  const nextHeaders = [...headers];
  let wmsHeader;
  if (existingIndex >= 0) {
    [wmsHeader] = nextHeaders.splice(existingIndex, 1);
    wmsHeader = { ...wmsHeader, key: wmsHeader.key || WMS_KEY, label: WMS_LABEL, type: "text" };
  } else {
    wmsHeader = { ...WMS_COLUMN };
  }
  const holdIndex = nextHeaders.findIndex((header) => header.key === ON_HOLD_KEY);
  const firstUpdateIndex = nextHeaders.findIndex((header) => header.key === FIRST_UPDATE_KEY);
  let insertIndex = nextHeaders.length;
  if (holdIndex >= 0) {
    insertIndex = holdIndex + 1;
  } else if (firstUpdateIndex >= 0) {
    insertIndex = firstUpdateIndex;
  }
  nextHeaders.splice(insertIndex, 0, wmsHeader);
  return nextHeaders;
};

const ensureShippedDateColumn = (headers = []) => {
  if (!Array.isArray(headers) || !headers.length) return headers;
  const canonicalShippedDate = canonicalizeHeaderLabel(SHIPPED_DATE_LABEL);
  const existingIndex = headers.findIndex(
    (header) => header.key === SHIPPED_DATE_KEY || canonicalizeHeaderLabel(header.label) === canonicalShippedDate
  );
  const nextHeaders = [...headers];
  let shippedDateHeader;
  if (existingIndex >= 0) {
    [shippedDateHeader] = nextHeaders.splice(existingIndex, 1);
    shippedDateHeader = {
      ...shippedDateHeader,
      key: shippedDateHeader.key || SHIPPED_DATE_KEY,
      label: SHIPPED_DATE_LABEL,
      type: "date",
    };
  } else {
    shippedDateHeader = { ...SHIPPED_DATE_COLUMN };
  }
  const wmsIndex = nextHeaders.findIndex((header) => header.key === WMS_KEY);
  const insertIndex = wmsIndex >= 0 ? wmsIndex + 1 : nextHeaders.length;
  nextHeaders.splice(insertIndex, 0, shippedDateHeader);
  return nextHeaders;
};

const ensureRowIdHeader = (headers = []) => {
  if (!Array.isArray(headers) || !headers.length) return headers;
  const existingIndex = headers.findIndex((header) => header.key === ROW_ID_COLUMN.key);
  const baseHeader = { ...ROW_ID_COLUMN };
  if (existingIndex === 0) return headers;
  const nextHeaders = [...headers];
  if (existingIndex >= 0) {
    const [existing] = nextHeaders.splice(existingIndex, 1);
    nextHeaders.unshift({ ...baseHeader, label: existing.label || baseHeader.label });
    return nextHeaders;
  }
  nextHeaders.unshift(baseHeader);
  return nextHeaders;
};

const getOutboundHeaderKey = (headers) =>
  findHeaderKeyByCanonicalLabel(headers || [], OUTBOUND_CODE_LABEL, OUTBOUND_CODE_COLUMN.key);

const formatLocalDateTime = () => formatValueToPst(new Date());

const normalizeCustCode = (value) => String(value ?? '').trim().toUpperCase();
const normalizePoNumber = (value) => String(value ?? '').trim().toUpperCase();
const normalizeLoadId = normalizeRoutingLoadId;

const buildCompositeKeyParts = (custValue, poValue, loadValue) => {
  if (!custValue || !poValue) return '';
  return `${custValue}::${poValue}::${loadValue}`;
};

const buildReferenceDiff = (referenceRecord, headers) => {
  if (!referenceRecord || !headers?.length) return undefined;
  const diff = {};
  headers.forEach((header) => {
    diff[header.key] = { previous: referenceRecord[header.key], changed: false };
  });
  return diff;
};

export const mergeDatasetWithHistory = (sourceDataset, previousDataset, options = {}) => {
  if (!sourceDataset?.headers || !sourceDataset?.data) return sourceDataset;

  let baseNewHeaders = sourceDataset.headers.filter(
    (header) => !AUDIT_KEY_SET.has(header.key) && header.key !== CUST_CODE_KEY
  );
  const baseOldHeaders = (previousDataset?.headers || []).filter(
    (header) => !AUDIT_KEY_SET.has(header.key) && header.key !== CUST_CODE_KEY
  );
  const sourceInformationLabels = new Set(
    STRIDE_INFORMATION_HEADERS.filter((label) =>
      baseNewHeaders.some(
        (header) => canonicalizeHeaderLabel(header.label) === canonicalizeHeaderLabel(label)
      )
    ).map(canonicalizeHeaderLabel)
  );
  STRIDE_INFORMATION_HEADERS.forEach((label) => {
    const canonical = canonicalizeHeaderLabel(label);
    const alreadyPresent = baseNewHeaders.some(
      (header) => canonicalizeHeaderLabel(header.label) === canonical
    );
    const storedHeader = baseOldHeaders.find(
      (header) => canonicalizeHeaderLabel(header.label) === canonical
    );
    if (!alreadyPresent && storedHeader) baseNewHeaders.push({ ...storedHeader });
  });
  const outboundHeaderOld = baseOldHeaders.find(
    (header) => canonicalizeHeaderLabel(header.label) === canonicalizeHeaderLabel(OUTBOUND_CODE_LABEL)
  );
  baseNewHeaders = ensureOutboundColumnHeader(baseNewHeaders, outboundHeaderOld);

  const poKeyNew = findHeaderKeyByCanonicalLabel(baseNewHeaders, PO_NUMBER_LABEL);
  const loadKeyNew = findHeaderKeyByCanonicalLabel(baseNewHeaders, LOAD_ID_LABEL);
  if (!poKeyNew || !loadKeyNew) {
    throw new Error('Import failed: required PO Number or Load ID column is missing.');
  }
  const custKeyNew =
    findHeaderKeyByCanonicalLabel(baseNewHeaders, CUST_CODE_LABEL, CUST_CODE_KEY) || CUST_CODE_KEY;
  const casesKeyNew = findHeaderKeyByCanonicalLabel(baseNewHeaders, 'Cases');
  const outboundKeyNew = getOutboundHeaderKey(baseNewHeaders);

  const poKeyOld = findHeaderKeyByCanonicalLabel(baseOldHeaders, PO_NUMBER_LABEL, poKeyNew);
  const loadKeyOld = findHeaderKeyByCanonicalLabel(baseOldHeaders, LOAD_ID_LABEL, loadKeyNew);
  const custKeyOld =
    findHeaderKeyByCanonicalLabel(baseOldHeaders, CUST_CODE_LABEL, custKeyNew) ||
    (previousDataset?.headers || []).find((header) => header.key === CUST_CODE_KEY)?.key ||
    custKeyNew;
  const casesKeyOld = findHeaderKeyByCanonicalLabel(baseOldHeaders, 'Cases', casesKeyNew);
  const outboundKeyOld = findHeaderKeyByCanonicalLabel(baseOldHeaders, OUTBOUND_CODE_LABEL, outboundKeyNew);
  const shippedDateKeyNew = findHeaderKeyByCanonicalLabel(baseNewHeaders, SHIPPED_DATE_LABEL);
  const shippedDateKeyOld = findHeaderKeyByCanonicalLabel(
    baseOldHeaders,
    SHIPPED_DATE_LABEL,
    SHIPPED_DATE_KEY
  );
  const shippedDateCanonical = canonicalizeHeaderLabel(SHIPPED_DATE_LABEL);
  const informationCanonicals = new Set(STRIDE_INFORMATION_HEADERS.map(canonicalizeHeaderLabel));
  const workbookOwnedHeaders = baseNewHeaders.filter(
    (header) => {
      const canonical = canonicalizeHeaderLabel(header.label);
      return (
        canonical !== shippedDateCanonical &&
        (!informationCanonicals.has(canonical) || sourceInformationLabels.has(canonical))
      );
    }
  );

  const comparisonPlan = workbookOwnedHeaders.map((header) => {
    const canonical = canonicalizeHeaderLabel(header.label);
    const match = baseOldHeaders.find(
      (oldHeader) => canonicalizeHeaderLabel(oldHeader.label) === canonical
    );
    return {
      newKey: header.key,
      oldKey: match?.key || header.key,
    };
  });

  const timestamp = formatLocalDateTime();
  const assignOutboundValue = (target, source) => {
    if (!outboundKeyNew) return;
    if (source) {
      if (outboundKeyOld && source[outboundKeyOld] !== undefined) {
        target[outboundKeyNew] = source[outboundKeyOld];
        return;
      }
      if (source[outboundKeyNew] !== undefined) {
        target[outboundKeyNew] = source[outboundKeyNew];
        return;
      }
    }
    if (target[outboundKeyNew] === undefined) {
      target[outboundKeyNew] = "";
    }
  };
  const clearWorkbookShippedDate = (target) => {
    if (!shippedDateKeyNew) return;
    target[shippedDateKeyNew] = "";
    target[SHIPPED_DATE_KEY] = "";
  };
  const assignServerShippedDate = (target, source) => {
    if (!source) return;
    const serverValue = source[shippedDateKeyOld] ?? source[SHIPPED_DATE_KEY];
    const authoritativeValue = serverValue === undefined || serverValue === null ? "" : serverValue;
    target[shippedDateKeyNew || SHIPPED_DATE_KEY] = authoritativeValue;
    target[SHIPPED_DATE_KEY] = authoritativeValue;
  };
  const informationMappings = STRIDE_INFORMATION_HEADERS.map((label) => {
    const canonical = canonicalizeHeaderLabel(label);
    return {
      sourceProvided: sourceInformationLabels.has(canonical),
      newKey: findHeaderKeyByCanonicalLabel(baseNewHeaders, label),
      oldKey: findHeaderKeyByCanonicalLabel(baseOldHeaders, label),
    };
  }).filter((mapping) => mapping.newKey);
  const preserveOmittedInformation = (target, source) => {
    informationMappings.forEach(({ sourceProvided, newKey, oldKey }) => {
      if (sourceProvided) return;
      if (source && oldKey && Object.prototype.hasOwnProperty.call(source, oldKey)) {
        target[newKey] = source[oldKey];
      } else if (!Object.prototype.hasOwnProperty.call(target, newKey)) {
        target[newKey] = null;
      }
    });
  };

  const previousComboMap = new Map();
  const comboEntryMap = new Map();
  const zeroPoolMap = new Map();
  const zeroPoolQueue = new Map();
  const custPoFallbackMap = new Map(); // tracks any historical rows sharing Cust+PO for load-change fallback
  const allOldEntries = [];

  (previousDataset?.data || []).forEach((record, index) => {
    const custValue = normalizeCustCode(record?.[custKeyOld] || record?.[CUST_CODE_KEY]);
    const poValue = normalizePoNumber(record?.[poKeyOld]);
    if (!custValue || !poValue) return;
    const loadValue = normalizeLoadId(record?.[loadKeyOld]);
    const comboKey = buildCompositeKeyParts(custValue, poValue, loadValue);
    const uid = `${comboKey || `${custValue}::${poValue}`}::${index}`;
    const casesValue = normalizeNumberValue(record?.[casesKeyOld]);
    const entry = { comboKey, record, uid, custValue, poValue, loadValue, casesValue }; // keep cases for fallback matching
    if (comboKey) {
      previousComboMap.set(comboKey, record);
      comboEntryMap.set(comboKey, entry);
    }
    const custPoKey = `${custValue}::${poValue}`;
    if (!custPoFallbackMap.has(custPoKey)) custPoFallbackMap.set(custPoKey, []);
    custPoFallbackMap.get(custPoKey).push(entry);
    if (loadValue === "0") {
      const poolKey = buildZeroPoolKey(custValue, poValue, casesValue);
      if (!zeroPoolMap.has(poolKey)) zeroPoolMap.set(poolKey, []);
      zeroPoolMap.get(poolKey).push({ ...entry, casesValue });
      const queueKey = `${custValue}::${poValue}`;
      if (!zeroPoolQueue.has(queueKey)) zeroPoolQueue.set(queueKey, []);
      zeroPoolQueue.get(queueKey).push({ ...entry, casesValue });
    }
    allOldEntries.push(entry);
  });

  const usedOldEntries = new Set();
  const mergedData = [];
  const incomingLoadsByCustPo = new Map();

  sourceDataset.data.forEach((record) => {
    const custValue = normalizeCustCode(record?.[custKeyNew] || options.custCode || '');
    const poValue = normalizePoNumber(record?.[poKeyNew]);
    if (!custValue || !poValue) return;
    const custPoKey = `${custValue}::${poValue}`;
    if (!incomingLoadsByCustPo.has(custPoKey)) incomingLoadsByCustPo.set(custPoKey, new Set());
    incomingLoadsByCustPo.get(custPoKey).add(normalizeLoadId(record?.[loadKeyNew]));
  });

  sourceDataset.data.forEach((rawRecord) => {
    const custValue = normalizeCustCode(rawRecord?.[custKeyNew] || options.custCode || '');
    const poValue = normalizePoNumber(rawRecord?.[poKeyNew]);
    if (!custValue || !poValue) return;
    const loadValue = normalizeLoadId(rawRecord?.[loadKeyNew]);
    const comboKey = buildCompositeKeyParts(custValue, poValue, loadValue);
    const casesValue = normalizeNumberValue(rawRecord?.[casesKeyNew]);

    const nextRecord = { ...rawRecord, [custKeyNew]: custValue, [CUST_CODE_KEY]: custValue };
    clearWorkbookShippedDate(nextRecord);
    assignOutboundValue(nextRecord);
    const previousRecord = comboKey ? previousComboMap.get(comboKey) : null;

    if (previousRecord) {
      assignOutboundValue(nextRecord, previousRecord);
      assignServerShippedDate(nextRecord, previousRecord);
      preserveOmittedInformation(nextRecord, previousRecord);
      const entry = comboEntryMap.get(comboKey);
      if (entry) usedOldEntries.add(entry.uid);
      const { hasChanges, diff } = diffRecords(nextRecord, previousRecord, comparisonPlan);
      if (hasChanges) {
        nextRecord[STATUS_KEY] = 'Update';
        nextRecord[FIRST_UPDATE_KEY] = previousRecord[FIRST_UPDATE_KEY] || timestamp;
        nextRecord[LAST_UPDATE_KEY] = timestamp;
        nextRecord.__diff = diff;
      } else {
        nextRecord[STATUS_KEY] = 'Open';
        nextRecord[FIRST_UPDATE_KEY] = previousRecord[FIRST_UPDATE_KEY] || timestamp;
        nextRecord[LAST_UPDATE_KEY] = previousRecord[LAST_UPDATE_KEY] || '';
        if (nextRecord.__diff) delete nextRecord.__diff;
      }
      mergedData.push(nextRecord);
      return;
    }

    const zeroKey = buildZeroPoolKey(custValue, poValue, casesValue);
    let replacement = (zeroPoolMap.get(zeroKey) || []).find((entry) => !usedOldEntries.has(entry.uid));
    if (!replacement) {
      const queueKey = `${custValue}::${poValue}`;
      replacement = (zeroPoolQueue.get(queueKey) || []).find((entry) => !usedOldEntries.has(entry.uid));
    }
    if (!replacement) {
      const custPoKey = `${custValue}::${poValue}`;
      const pool = custPoFallbackMap.get(custPoKey) || [];
      replacement = pool.find((entry) => {
        if (usedOldEntries.has(entry.uid)) return false;
        const prevCases = normalizeNumberValue(entry.record?.[casesKeyOld]);
        return prevCases === casesValue;
      });
    }
    if (replacement) {
      usedOldEntries.add(replacement.uid);
      assignOutboundValue(nextRecord, replacement.record);
      assignServerShippedDate(nextRecord, replacement.record);
      preserveOmittedInformation(nextRecord, replacement.record);
      nextRecord[STATUS_KEY] = "Update";
      nextRecord[FIRST_UPDATE_KEY] = replacement.record[FIRST_UPDATE_KEY] || timestamp;
      nextRecord[LAST_UPDATE_KEY] = timestamp;
      const referenceDiff = buildReferenceDiff(replacement.record, workbookOwnedHeaders) || {};
      comparisonPlan.forEach(({ newKey, oldKey }) => {
        const previousValue = replacement.record?.[oldKey];
        const currentValue = nextRecord?.[newKey];
        if (normalizeComparisonValue(previousValue) === normalizeComparisonValue(currentValue)) {
          if (referenceDiff[newKey] && !referenceDiff[newKey].changed) {
            delete referenceDiff[newKey];
          }
          return;
        }
        referenceDiff[newKey] = {
          previous: previousValue,
          changed: true,
        };
      });
      if (Object.keys(referenceDiff).length) {
        nextRecord.__diff = referenceDiff;
      } else if (nextRecord.__diff) {
        delete nextRecord.__diff;
      }
      mergedData.push(nextRecord);
      return;
    }

    preserveOmittedInformation(nextRecord, null);
    nextRecord[STATUS_KEY] = 'New';
    nextRecord[FIRST_UPDATE_KEY] = timestamp;
    nextRecord[LAST_UPDATE_KEY] = '';
    if (nextRecord.__diff) delete nextRecord.__diff;
    mergedData.push(nextRecord);
  });

  if (options.carryForwardUnmatchedHistory !== false) allOldEntries.forEach((entry) => {
    if (usedOldEntries.has(entry.uid)) return;
    const incomingLoads = incomingLoadsByCustPo.get(`${entry.custValue}::${entry.poValue}`);
    if (!shouldCarryForwardUnmatchedHistoryRow(entry.loadValue, incomingLoads)) return;
    const cloned = { ...entry.record };
    assignOutboundValue(cloned, entry.record);
    cloned[STATUS_KEY] = 'Not Found';
    cloned[FIRST_UPDATE_KEY] = cloned[FIRST_UPDATE_KEY] || timestamp;
    cloned[LAST_UPDATE_KEY] = timestamp;
    if (cloned.__diff) delete cloned.__diff;
    mergedData.push(cloned);
  });

  const nextHeaders = ensureOutboundColumnHeader(insertAuditHeaders(baseNewHeaders));
  mergedData.forEach((record) => {
    if (!record[STATUS_KEY]) record[STATUS_KEY] = 'Open';
    if (!record[FIRST_UPDATE_KEY]) record[FIRST_UPDATE_KEY] = timestamp;
    if (record[LAST_UPDATE_KEY] === undefined) record[LAST_UPDATE_KEY] = '';
    if (!record[CUST_CODE_KEY]) record[CUST_CODE_KEY] = options.custCode || '';
  });

  return enforceForcedColumnTypes({ headers: nextHeaders, data: mergedData });
};const isBlankValue = (value) => value === undefined || value === null || value === "";

export const ensureAuditColumnsForDisplay = (dataset, options = {}) => {
  if (!dataset?.headers) return dataset;
  const hasAllAuditColumns = DERIVED_COLUMNS.every((column) =>
    dataset.headers.some((header) => header.key === column.key)
  );
  const headersWithAudit = normalizeAuditHeaderLabels(
    hasAllAuditColumns ? dataset.headers : insertAuditHeaders(dataset.headers)
  );
  const headersWithExtras = ensureRowIdHeader(
    ensureShippedDateColumn(
      ensureWmsColumn(
        ensureOnHoldColumnPosition(
          ensureOutboundColumnHeader(ensureOnHoldHeader(ensureD2mHeader(headersWithAudit)))
        )
      )
    )
  );
  const outboundKey = getOutboundHeaderKey(headersWithExtras);
  const shippedDateKey = findHeaderKeyByCanonicalLabel(
    headersWithExtras,
    SHIPPED_DATE_LABEL,
    SHIPPED_DATE_KEY
  );
  const buildRowId = buildRowIdGenerator(headersWithExtras);
  const mabdKey = findHeaderKeyByCanonicalLabel(headersWithExtras, "MABD", "mabd");
  const todayStartTs = getStartOfTodayTs();
  const data = (dataset.data || []).map((record, index) => {
    const next = { ...record };
    const serverShippedDate = next[shippedDateKey] ?? next[SHIPPED_DATE_KEY];
    DERIVED_COLUMNS.forEach((column) => {
      if (next[column.key] === undefined) next[column.key] = "";
    });
    if (serverShippedDate !== undefined && serverShippedDate !== null && serverShippedDate !== "") {
      next[shippedDateKey] = serverShippedDate;
      next[SHIPPED_DATE_KEY] = serverShippedDate;
    }
    if (outboundKey && next[outboundKey] === undefined) {
      next[outboundKey] = "";
    }
    if (isBlankValue(next[FIRST_UPDATE_KEY])) {
      next[FIRST_UPDATE_KEY] = "";
    } else {
      next[FIRST_UPDATE_KEY] = formatValueToPst(next[FIRST_UPDATE_KEY]) || "";
    }
    next[LAST_UPDATE_KEY] = isBlankValue(next[LAST_UPDATE_KEY]) ? "" : formatValueToPst(next[LAST_UPDATE_KEY]) || "";
    if (mabdKey && isBlankValue(next[D2M_KEY])) {
      const computed = computeD2mFromFields(next[mabdKey], next[STATUS_KEY], next[WMS_KEY], todayStartTs);
      if (computed !== null) {
        next[D2M_KEY] = computed;
      }
    }
    if (!next._id) {
      next._id = buildRowId(next, index);
    }
    return next;
  });
  const orderedHeaders = reorderHeaders(headersWithExtras);
  return enforceForcedColumnTypes({ ...dataset, headers: orderedHeaders, data });
};

const coerceValueToForcedType = (value, type) => {
  if (type === "date") return normaliseCell(value, "date");
  if (type === "text") return value === undefined || value === null ? "" : String(value);
  return value;
};

const enforceForcedColumnTypes = (dataset) => {
  if (!dataset?.headers) return dataset;
  let headersChanged = false;
  const headerForceMeta = dataset.headers.map((header) => {
    const canonical = canonicalizeHeaderLabel(header.label);
    const forcedType = FORCE_TYPE_MAP.get(canonical);
    let nextHeader = header;
    if (forcedType && header.type !== forcedType) {
      headersChanged = true;
      nextHeader = { ...header, type: forcedType };
    }
    return { header: nextHeader, forcedType };
  });
  const forcedTargets = headerForceMeta.filter((item) => item.forcedType);
  const headers = headerForceMeta.map((item) => item.header);
  if (!forcedTargets.length) {
    return headersChanged ? { ...dataset, headers } : dataset;
  }
  const originalData = Array.isArray(dataset.data) ? dataset.data : [];
  let dataChanged = false;
  const data = originalData.map((record) => {
    let mutated = false;
    const next = { ...record };
    forcedTargets.forEach(({ header, forcedType }) => {
      const currentValue = next[header.key];
      const coercedValue = coerceValueToForcedType(currentValue, forcedType);
      if (coercedValue !== currentValue) {
        next[header.key] = coercedValue;
        mutated = true;
      }
    });
    if (mutated) {
      dataChanged = true;
      return next;
    }
    return record;
  });
  if (!headersChanged && !dataChanged) return dataset;
  return {
    ...dataset,
    headers: headersChanged ? headers : dataset.headers,
    data: dataChanged ? data : dataset.data,
  };
};

const sanitizeKey = (value, index) =>
  (value && value.toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")) || `column-${index}`;

const padNumber = (value) => String(value).padStart(2, "0");

const formatTimestampForFilename = (date) =>
  `${date.getFullYear()}${padNumber(date.getMonth() + 1)}${padNumber(date.getDate())}-${padNumber(
    date.getHours()
  )}${padNumber(date.getMinutes())}${padNumber(date.getSeconds())}`;

const normaliseValueForExport = (value, type) => {
  if (value === undefined || value === null) return "";
  if (type === "number") {
    if (typeof value === "number") return value;
    const parsed = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : String(value);
  }
  return value;
};

const buildRowIdGenerator = (headers = []) => {
  const poKey = findHeaderKeyByCanonicalLabel(headers, PO_NUMBER_LABEL, "po-number");
  const loadKey = findHeaderKeyByCanonicalLabel(headers, LOAD_ID_LABEL, "load-id");
  const custKey = findHeaderKeyByCanonicalLabel(headers, CUST_CODE_LABEL, CUST_CODE_KEY);
  return (record, index) => {
    const normalize = (value) => (value === undefined || value === null ? "" : String(value).trim());
    const cust = normalize(custKey ? record[custKey] : "").toUpperCase();
    const po = normalize(poKey ? record[poKey] : record[PO_NUMBER_LABEL]);
    const load = normalize(loadKey ? record[loadKey] : "");
    const fallback = `${cust}|${po}|${load}|${index}`;
    return record._id || fallback || `${index}`;
  };
};

const toISODateInputValue = (value) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  const isoMatch = ISO_DATE_RE.exec(trimmed);
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    return `${yyyy}-${mm}-${dd}`;
  }
  const shortMatch = SHORT_DATE_RE.exec(trimmed);
  if (!shortMatch) return "";
  const [, mm, dd, yy] = shortMatch;
  const year = Number(yy.length === 2 ? Number(yy) + 2000 : yy);
  return `${year}-${padNumber(mm)}-${padNumber(dd)}`;
};

const toShortDate = (date) =>
  date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });

const formatShortDate = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (value instanceof Date) return toShortDate(value);
  if (typeof value === "number") {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      const js = new Date(Date.UTC(parsed.y, parsed.m - 1, parsed.d));
      return toShortDate(js);
    }
  }
  const isoMatch = ISO_DATE_RE.exec(String(value));
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    const js = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    if (!Number.isNaN(js.getTime())) return toShortDate(js);
  }
  const shortMatch = SHORT_DATE_RE.exec(String(value));
  if (shortMatch) {
    const [, mm, dd, yy] = shortMatch;
    const year = Number(yy.length === 2 ? Number(yy) + 2000 : yy);
    const js = new Date(year, Number(mm) - 1, Number(dd));
    if (!Number.isNaN(js.getTime())) return toShortDate(js);
  }
  const js = new Date(value);
  if (!Number.isNaN(js.getTime())) return toShortDate(js);
  return String(value);
};

const parseShortDate = (value) => {
  if (!value) return null;
  const isoMatch = ISO_DATE_RE.exec(String(value));
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    const js = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    const ts = js.getTime();
    return Number.isNaN(ts) ? null : ts;
  }
  const shortMatch = SHORT_DATE_RE.exec(String(value));
  if (!shortMatch) return null;
  const [, mm, dd, yy] = shortMatch;
  const year = Number(yy.length === 2 ? Number(yy) + 2000 : yy);
  const js = new Date(year, Number(mm) - 1, Number(dd));
  const ts = js.getTime();
  return Number.isNaN(ts) ? null : ts;
};

const getStartOfTodayTs = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.getTime();
};

const coerceDateToMidnightTs = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (value instanceof Date) {
    const normalized = new Date(value);
    normalized.setHours(0, 0, 0, 0);
    return normalized.getTime();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      const normalized = new Date(parsed.y, parsed.m - 1, parsed.d);
      normalized.setHours(0, 0, 0, 0);
      return normalized.getTime();
    }
  }
  const parsedShort = parseShortDate(value);
  if (parsedShort !== null) {
    const normalized = new Date(parsedShort);
    normalized.setHours(0, 0, 0, 0);
    return normalized.getTime();
  }
  const formatted = formatShortDate(value);
  if (formatted && formatted !== value) {
    const parsedFormatted = parseShortDate(formatted);
    if (parsedFormatted !== null) {
      const normalized = new Date(parsedFormatted);
      normalized.setHours(0, 0, 0, 0);
      return normalized.getTime();
    }
  }
  const fallback = new Date(value);
  if (!Number.isNaN(fallback.getTime())) {
    fallback.setHours(0, 0, 0, 0);
    return fallback.getTime();
  }
  return null;
};

const computeD2mFromFields = (mabdValue, statusValue, wmsValue, todayTs = getStartOfTodayTs()) => {
  const status = String(statusValue || "").trim().toLowerCase();
  if (status && D2M_SKIP_STATUSES.has(status)) return null;
  const wms = String(wmsValue || "").trim().toLowerCase();
  if (wms && D2M_TERMINAL_WMS_STATUSES.has(wms)) return null;
  const targetTs = coerceDateToMidnightTs(mabdValue);
  if (targetTs === null) return null;
  const diff = Math.round((targetTs - todayTs) / MS_PER_DAY);
  return Number.isFinite(diff) ? diff : null;
};

const guessType = (label, samples) => {
  const trimmed = (label || "").trim();
  if (DATE_LABEL_RE.test(trimmed)) return "date";
  if (ALWAYS_TEXT_RE.test(trimmed)) return "text";
  let numeric = true;
  for (const sample of samples) {
    if (sample === undefined || sample === null || sample === "") continue;
    const n = Number(String(sample).replace(/,/g, ""));
    if (Number.isNaN(n)) {
      numeric = false;
      break;
    }
  }
  return numeric ? "number" : "text";
};

const normaliseCell = (value, type) => {
  if (type === "date") return formatShortDate(value);
  if (type === "number") {
    if (value === undefined || value === null || value === "") return null;
    const n = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  return value === undefined || value === null ? "" : String(value);
};

const renderCellValue = (value, type) => {
  if (value === undefined || value === null) return "";
  if (type === "number") {
    if (Number.isFinite(value)) return value.toLocaleString();
    return String(value ?? "");
  }
  return value;
};

export const formatFleetLoadIdDisplay = (value) =>
  value === null || value === undefined ? "Historical data unavailable" : String(value);

const buildWmsTooltip = (updatedAt) => {
  const label = formatValueToShortPst(updatedAt);
  return label ? `Updated PT ${label}` : "Not updated";
};

const WMS_STATUS_STYLES = {
  NEW: "bg-sky-100 text-sky-900 border-sky-200",
  MSTCRTD: "bg-indigo-100 text-indigo-800 border-indigo-200",
  PICKING: "bg-blue-100 text-blue-900 border-blue-200",
  PICKED: "bg-cyan-100 text-cyan-900 border-cyan-200",
  PACKING: "bg-teal-100 text-teal-900 border-teal-200",
  PACKED: "bg-emerald-100 text-emerald-900 border-emerald-200",
  SHIPPED: "bg-green-600 text-white border-green-700",
  CREQC: "bg-amber-100 text-amber-900 border-amber-200",
  CANCELLED: "bg-rose-100 text-rose-900 border-rose-200",
  WREQC: "bg-orange-100 text-orange-900 border-orange-200",
  HOLD: "bg-yellow-100 text-yellow-900 border-yellow-200",
  ADDFAULT: "bg-red-100 text-red-900 border-red-200",
  DRAFT: "bg-slate-200 text-slate-900 border-slate-300",
  AWLABEL: "bg-purple-100 text-purple-900 border-purple-200",
};

const normalizeWmsStatusKey = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, "")
    .toUpperCase();

const renderWmsCellValue = (value, updatedAt) => {
  const tooltip = buildWmsTooltip(updatedAt);
  const normalized = String(value || "").trim();
  if (!normalized) {
    return (
      <span title={tooltip} className="text-slate-400">
        —
      </span>
    );
  }
  const styleKey = normalizeWmsStatusKey(normalized);
  const badgeClass =
    WMS_STATUS_STYLES[styleKey] || "bg-slate-100 text-slate-800 border-slate-200";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${badgeClass}`}
      title={tooltip}
    >
      {normalized}
    </span>
  );
};

const renderD2mCellValue = (value) => {
  const tooltip = "Days to MABD";
  if (value === undefined || value === null || value === "") {
    return (
      <span className="text-slate-400" title={tooltip}>
        —
      </span>
    );
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return (
      <span className="text-slate-400" title={tooltip}>
        —
      </span>
    );
  }
  let colorClass = "text-slate-900";
  if (num >= 16) {
    colorClass = "text-slate-900";
  } else if (num > 5) {
    colorClass = "text-blue-600";
  } else if (num >= 0) {
    colorClass = "text-orange-600";
  } else {
    colorClass = "text-red-600";
  }
  return (
    <span className={`font-semibold ${colorClass}`} title={tooltip}>
      {num}
    </span>
  );
};

const NumberRangeFilter = ({ value = {}, onChange, placeholder = "Select range" }) => {
  const current = value || {};
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef(null);
  const formatLabel = () => {
    const from = current.from;
    const to = current.to;
    if (String(from || "").trim() === "" && String(to || "").trim() === "") {
      return placeholder;
    }
    return `${from || "−∞"} to ${to || "∞"}`;
  };
  React.useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);
  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between rounded border border-slate-300 bg-white px-2 py-1 text-left text-xs text-slate-700 shadow-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
      >
        <span>{formatLabel()}</span>
        <span className="text-slate-400">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 z-50 mt-2 w-56 rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-700 shadow-xl">
          <p className="mb-2 font-semibold text-slate-600">Days to MABD</p>
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={current.from ?? ""}
              onChange={(event) => onChange({ from: event.target.value, to: current.to || "" })}
              placeholder="Min"
              className="w-full rounded border border-slate-300 px-2 py-1 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
            <span className="text-slate-400">-</span>
            <input
              type="number"
              value={current.to ?? ""}
              onChange={(event) => onChange({ from: current.from || "", to: event.target.value })}
              placeholder="Max"
              className="w-full rounded border border-slate-300 px-2 py-1 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
              onClick={() => {
                onChange({ from: "", to: "" });
                setOpen(false);
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="rounded bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700"
              onClick={() => setOpen(false)}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const arraysEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

const ensureWmsColumnInList = (keys = [], headerKeys = []) => {
  if (!Array.isArray(keys)) return [];
  const available = Array.isArray(headerKeys) ? headerKeys : [];
  const next = [...keys];
  if (available.includes(WMS_KEY) && !next.includes(WMS_KEY)) {
    const holdIndex = next.indexOf(ON_HOLD_KEY);
    const insertIndex = holdIndex >= 0 ? holdIndex + 1 : next.length;
    next.splice(insertIndex, 0, WMS_KEY);
  }
  return Array.from(new Set(next));
};

const ensureShippedDateColumnInList = (keys = [], headerKeys = []) => {
  if (!Array.isArray(keys)) return [];
  const available = Array.isArray(headerKeys) ? headerKeys : [];
  const next = [...keys];
  if (available.includes(SHIPPED_DATE_KEY) && !next.includes(SHIPPED_DATE_KEY)) {
    const wmsIndex = next.indexOf(WMS_KEY);
    const insertIndex = wmsIndex >= 0 ? wmsIndex + 1 : next.length;
    next.splice(insertIndex, 0, SHIPPED_DATE_KEY);
  }
  return Array.from(new Set(next));
};

const ensureD2mColumnInList = (keys = [], headerKeys = []) => {
  if (!Array.isArray(keys)) return [];
  const available = Array.isArray(headerKeys) ? headerKeys : [];
  const next = [...keys];
  if (available.includes(D2M_KEY) && !next.includes(D2M_KEY)) {
    const statusIndex = next.indexOf(STATUS_KEY);
    const insertIndex = statusIndex >= 0 ? statusIndex + 1 : Math.min(next.length, 3);
    next.splice(insertIndex, 0, D2M_KEY);
  }
  return Array.from(new Set(next));
};

const ensureRequiredColumnsInList = (keys = [], headerKeys = []) => {
  let next = ensureD2mColumnInList(keys, headerKeys);
  next = ensureWmsColumnInList(next, headerKeys);
  if (headerKeys.includes(EDI_STATUS_KEY) && !next.includes(EDI_STATUS_KEY)) {
    const wmsIndex = next.indexOf(WMS_KEY);
    next.splice(wmsIndex < 0 ? next.length : wmsIndex, 0, EDI_STATUS_KEY);
  }
  next = ensureShippedDateColumnInList(next, headerKeys);
  return next;
};

const computeDefaultVisibleKeys = (headers = []) => {
  if (!Array.isArray(headers) || !headers.length) return [];
  const preferredKeys = DEFAULT_VISIBLE_LABELS.map((label) =>
    findHeaderKeyByCanonicalLabel(headers, label)
  ).filter(Boolean);
  if (preferredKeys.length) {
    return Array.from(new Set(preferredKeys));
  }
  return headers.map((header) => header.key).filter((key) => !HIDDEN_COLUMN_KEYS.has(key));
};

const reorderHeaders = (headers = []) => {
  if (!Array.isArray(headers) || !headers.length) return headers;
  const remaining = [...headers];
  const ordered: Array<typeof headers[number]> = [];
  const matchHeader = (target, list) => {
    const { key, label } = target;
    return list.findIndex((header) => {
      if (key && header.key === key) return true;
      if (label) {
        return canonicalizeHeaderLabel(header.label) === canonicalizeHeaderLabel(label);
      }
      return false;
    });
  };
  ROUTING_STATUS_DISPLAY_ORDER.forEach((target) => {
    const index = matchHeader(target, remaining);
    if (index >= 0) {
      ordered.push(remaining.splice(index, 1)[0]);
    }
  });
  return [...ordered, ...remaining];
};

const STATUS_BADGE_STYLES = {
  New: "bg-green-200 text-green-900 border border-green-300",
  Update: "bg-rose-100 text-rose-800 border border-rose-200",
  "Not Found": "bg-purple-200 text-purple-900 border border-purple-300",
  Open: "bg-slate-200 text-slate-800 border border-slate-300",
};

const StatusBadge = ({ value }) => {
  const label = String(value || "").trim() || "Open";
  const classes = STATUS_BADGE_STYLES[label] || STATUS_BADGE_STYLES.Open;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${classes}`}>
      {label}
    </span>
  );
};

const isSupersededRoutingRow = (row) => Boolean(row?.[ROUTING_ROW_SUPERSEDED_KEY]);

const getSupersededReplacementLabel = (row) => {
  const replacementOutbound = String(row?.[ROUTING_ROW_SUPERSEDED_BY_OUTBOUND_KEY] || "").trim();
  if (replacementOutbound) return `Replaced by ${replacementOutbound}`;
  const replacementUid = String(row?.[ROUTING_ROW_SUPERSEDED_BY_UID_KEY] || "").trim();
  if (replacementUid) return `Superseded by ${replacementUid}`;
  return "Historical superseded row";
};

const OnHoldBadge = ({ value }) => {
  if (!value) return null;
  return (
    <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
      On Hold
    </span>
  );
};

const getFedexSummary = (row) => {
  if (row && typeof row[FEDEX_SUMMARY_KEY] === "object" && row[FEDEX_SUMMARY_KEY] !== null) {
    return row[FEDEX_SUMMARY_KEY];
  }
  return {
    labelStatus: "NOT_CREATED",
    labelStatusLabel: "Not Created",
    hasHistory: false,
    trackingNumber: "",
    trackingDisplay: "",
    routingRowId: String(row?.[ROUTING_ROW_ID_KEY] || "").trim(),
    routingUid: String(row?.[ROUTING_UID_KEY] || "").trim(),
    currentLabelId: "",
  };
};

const FedexStatusBadge = ({ summary, compact = false }) => {
  const statusCode = String(summary?.labelStatus || "NOT_CREATED").trim().toUpperCase();
  const statusLabel = String(summary?.labelStatusLabel || "Not Created").trim() || "Not Created";
  const classes = FEDEX_MAIN_STATUS_STYLES[statusCode] || FEDEX_MAIN_STATUS_STYLES.NOT_CREATED;
  return (
    <div className={`flex ${compact ? "flex-wrap gap-1" : "flex-wrap gap-2"}`}>
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${classes}`}>
        {statusLabel}
      </span>
    </div>
  );
};

const FedexTrackingText = ({ summary }) => {
  const trackingNumber = String(summary?.trackingNumber || "").trim();
  const statusCode = String(summary?.labelStatus || "NOT_CREATED").trim().toUpperCase();
  if (!trackingNumber) {
    return <span className="text-slate-400">-</span>;
  }
  if (statusCode === "VOIDED") {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="font-medium text-rose-700">{trackingNumber}</span>
        <span className="text-xs text-rose-600">Voided</span>
      </div>
    );
  }
  if (statusCode === "SUPERSEDED") {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="font-medium text-amber-800">{trackingNumber}</span>
        <span className="text-xs text-amber-700">Superseded</span>
      </div>
    );
  }
  return <span className="font-medium text-slate-800">{trackingNumber}</span>;
};

const formatFedexPayloadText = (payload) => {
  if (!payload?.available) return "Payload file is not available.";
  if (payload?.isJson && payload?.json !== undefined && payload?.json !== null) {
    try {
      return JSON.stringify(payload.json, null, 2);
    } catch (err) {
      return payload.text || "Payload file is not available.";
    }
  }
  return payload?.text || "Payload file is not available.";
};

const MultiSelectDropdown = ({
  values = [],
  options = [],
  placeholder = "Filter",
  onChange,
  disabled = false,
  buttonClassName = "",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const normalized = useMemo(() => new Set(values), [values]);
  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(term));
  }, [options, search]);

  const toggleValue = (next) => {
    const exists = normalized.has(next);
    if (exists) {
      onChange(values.filter((value) => value !== next));
    } else {
      onChange([...values, next]);
    }
  };

  const handleSelectAll = () => {
    onChange(options.map((opt) => opt.value));
  };

  const handleClear = () => {
    onChange([]);
  };

  const summary =
    values.length === 0
      ? placeholder
      : values.length === options.length
        ? "All selected"
        : `${values.length} selected`;

  return (
    <div className="relative text-xs" ref={containerRef}>
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        disabled={disabled}
        className={`w-full rounded border border-slate-300 bg-white px-2 py-1 text-left text-xs shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
          disabled ? "cursor-not-allowed opacity-60" : ""
        } ${buttonClassName}`}
      >
        {summary}
      </button>
      {open && (
        <div className="absolute left-0 z-40 mt-1 w-52 rounded-md border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-200 p-2">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search..."
              className="w-full rounded border border-slate-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <div className="mt-2 flex items-center justify-between text-[11px]">
              <button type="button" onClick={handleSelectAll} className="text-blue-600 hover:underline">
                Select all
              </button>
              <button type="button" onClick={handleClear} className="text-slate-600 hover:underline">
                Clear
              </button>
            </div>
          </div>
          <div className="max-h-56 overflow-auto p-2">
            {filteredOptions.length ? (
              filteredOptions.map((option) => (
                <label key={option.value} className="mb-1 flex items-center gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    checked={normalized.has(option.value)}
                    onChange={() => toggleValue(option.value)}
                  />
                  <span className="truncate">{option.label}</span>
                </label>
              ))
            ) : (
              <div className="py-4 text-center text-xs text-slate-500">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const loadWorkbookFromFile = async (file) => {
  const name = file?.name || "";
  const extension = name.split(".").pop()?.toLowerCase() || "";
  if (!["csv", "xlsx"].includes(extension)) {
    throw new Error("Only CSV and XLSX Routing Status workbooks are supported.");
  }
  const isCsv = extension === "csv";

  let workbook;
  const buffer = await file.arrayBuffer();
  if (isCsv) {
    let text;
    try {
      text = new TextDecoder("utf-8", { fatal: true }).decode(buffer);
    } catch (_err) {
      throw new Error("CSV Routing Status workbooks must use valid UTF-8 encoding.");
    }
    workbook = XLSX.read(text, { type: "string", raw: true });
  } else {
    workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  }

  const sheetName =
    PREFERRED_SHEET_NAMES.find((name) => workbook.Sheets[name]) || workbook.SheetNames?.[0];
  if (!sheetName) throw new Error("File is empty or has no sheets.");

  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, raw: true, defval: null });
  if (!rows.length) throw new Error("File does not contain any rows.");

  const format = detectRoutingStatusFormat(rows);
  if (["legacy", "stride"].includes(format) && sheet["!ref"]) {
    const headers = (rows[0] || []).map((value) => String(value ?? "").trim());
    const identityColumns = format === "legacy"
      ? [0, 1]
      : [headers.indexOf("PO"), headers.indexOf("Load #")];
    const range = XLSX.utils.decode_range(sheet["!ref"]);
    for (let rowIndex = 1; rowIndex <= range.e.r; rowIndex += 1) {
      for (const columnIndex of identityColumns) {
        const cell = sheet[XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })];
        if (cell?.f || cell?.t === "e") {
          throw new Error("Formula and error cells are not allowed in PO Number or Load ID.");
        }
      }
    }
  }

  return { rows: canonicalizeRoutingWorkbookIdentityRows(rows), sheetName, buffer };
};

export const buildDataset = (rows) => {
  const headersRaw = rows[0] || [];
  const headers = headersRaw.map((label, index) => ({
    key: sanitizeKey(label || `column-${index}`, index),
    label: label || `Column ${index + 1}`,
    type: "text",
  }));

  const samples = headers.map((_, colIndex) => []);
  rows.slice(1, 21).forEach((row) => {
    headers.forEach((_, idx) => {
      samples[idx].push(row?.[idx]);
    });
  });

  headers.forEach((header, index) => {
    const guessedType = guessType(header.label, samples[index]);
    const canonical = canonicalizeHeaderLabel(header.label);
    if (FORCE_DATE_LABELS.has(canonical)) {
      header.type = "date";
    } else if (FORCE_TEXT_LABELS.has(canonical)) {
      header.type = "text";
    } else {
      header.type = guessedType;
    }
  });

  const data = rows.slice(1).map((row, rowIndex) => {
    const record = { _id: `${rowIndex}-${Date.now()}` };
    headers.forEach((header, idx) => {
      record[header.key] = normaliseCell(row?.[idx], header.type);
    });
    return record;
  });

  return enforceForcedColumnTypes({ headers, data });
};

const loadStoredDataset = (key = STORAGE_DATA_KEY) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed?.headers || !parsed?.data) return null;
    return ensureAuditColumnsForDisplay(parsed);
  } catch (err) {
    console.warn("Failed to restore cached Routing Status dataset", err);
    return null;
  }
};

const persistDataset = (key = STORAGE_DATA_KEY, dataset) => {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(dataset));
  } catch (err) {
    console.warn("Unable to cache Routing Status dataset", err);
  }
};

const persistMeta = (key = STORAGE_META_KEY, meta) => {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(meta));
  } catch (err) {
    console.warn("Unable to cache Routing Status metadata", err);
  }
};

const loadStoredWidths = (key = STORAGE_WIDTHS_KEY) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    if (parsed && typeof parsed === "object") return parsed;
    return {};
  } catch (err) {
    console.warn("Failed to restore cached Routing Status column widths", err);
    return {};
  }
};

const loadStoredMeta = (key = STORAGE_META_KEY) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed) return null;
    return toMetaState(parsed);
  } catch (err) {
    console.warn("Failed to restore cached Routing Status metadata", err);
    return null;
  }
};

const persistWidths = (key = STORAGE_WIDTHS_KEY, widths = {}) => {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(widths));
  } catch (err) {
    console.warn("Unable to persist Routing Status column widths", err);
  }
};

const clearStoredWidths = (key = STORAGE_WIDTHS_KEY) => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.warn("Unable to clear Routing Status column widths cache", err);
  }
};

const loadStoredPoMeta = (key = PO_META_STORAGE_KEY) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed) return null;
    if (parsed.updatedAt && !(parsed.updatedAt instanceof Date)) {
      const parsedDate = new Date(parsed.updatedAt);
      parsed.updatedAt = Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
    return parsed;
  } catch (err) {
    console.warn("Failed to restore cached PO Details metadata", err);
    return null;
  }
};

const loadStoredVisibleColumns = (key = STORAGE_VISIBLE_COLUMNS_KEY) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) return parsed;
  } catch (err) {
    console.warn("Failed to restore Routing Status visible columns", err);
  }
  return null;
};

const persistVisibleColumns = (key = STORAGE_VISIBLE_COLUMNS_KEY, columns = []) => {
  try {
    localStorage.setItem(key, JSON.stringify(columns));
  } catch (err) {
    console.warn("Unable to persist Routing Status visible columns", err);
  }
};

const clearStoredVisibleColumns = (key = STORAGE_VISIBLE_COLUMNS_KEY) => {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    console.warn("Unable to clear Routing Status visible columns cache", err);
  }
};

const toMetaState = (meta, fallback = null) => {
  if (!meta && !fallback) return null;
  const merged = { ...(fallback || {}), ...(meta || {}) };
  const raw = merged.updatedAt;
  if (raw instanceof Date) return merged;
  if (typeof raw === "string") {
    const parsed = new Date(raw);
    merged.updatedAt = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    return merged;
  }
  if (fallback?.updatedAt instanceof Date) {
    merged.updatedAt = fallback.updatedAt;
  } else {
    merged.updatedAt = new Date();
  }
  return merged;
};

const cloneDataset = (data) => (data ? JSON.parse(JSON.stringify(data)) : null);
export const datasetForCustomerScope = (data, custCode) => {
  if (!data?.headers || !Array.isArray(data?.data)) return null;
  const normalizedCustCode = String(custCode || "").trim().toUpperCase();
  const custKey = findHeaderKeyByCanonicalLabel(data.headers, CUST_CODE_LABEL, CUST_CODE_KEY) || CUST_CODE_KEY;
  return {
    ...cloneDataset(data),
    data: data.data
      .filter((row) => !row?.__isSuperseded)
      .filter((row) => String(row?.[custKey] || row?.[CUST_CODE_KEY] || "").trim().toUpperCase() === normalizedCustCode),
  };
};
const cloneMeta = (meta) => {
  if (!meta) return null;
  const copy = { ...meta };
  if (copy.updatedAt instanceof Date) copy.updatedAt = new Date(copy.updatedAt);
  return copy;
};

const defaultFiltersFor = (headers) => {
  const filters = {};
  headers.forEach((header) => {
    const mode = getFilterModeForHeader(header);
    if (mode === FILTER_MODES.date || mode === FILTER_MODES.range) {
      filters[header.key] = { from: "", to: "" };
    } else if (mode === FILTER_MODES.multiSelect || mode === FILTER_MODES.fedexStatus) {
      filters[header.key] = [];
    } else {
      filters[header.key] = "";
    }
  });
  return filters;
};

const toggleSortState = (prev, key) => {
  if (prev.key !== key) return { key, direction: 1 };
  if (prev.direction === 1) return { key, direction: -1 };
  if (prev.direction === -1) return { key: null, direction: 0 };
  return { key, direction: 1 };
};

const applyFilters = (rows, headers, filters) =>
  rows.filter((row) =>
    headers.every((header) => {
      const filterValue = filters[header.key];
      const cell = row[header.key];
      const mode = getFilterModeForHeader(header);
      if (mode === FILTER_MODES.date) {
        const fromTs = parseShortDate(filterValue?.from);
        const toTs = parseShortDate(filterValue?.to);
        const cellTs = parseShortDate(cell);
        if (fromTs && (!cellTs || cellTs < fromTs)) return false;
        if (toTs && (!cellTs || cellTs > toTs)) return false;
        return true;
      }
      if (mode === FILTER_MODES.range) {
        const fromRaw = filterValue?.from ?? "";
        const toRaw = filterValue?.to ?? "";
        const hasFrom = String(fromRaw).trim() !== "";
        const hasTo = String(toRaw).trim() !== "";
        if (!hasFrom && !hasTo) return true;
        const numericCell = Number(String(cell ?? "").replace(/,/g, ""));
        if (Number.isNaN(numericCell)) return false;
        if (hasFrom) {
          const fromVal = Number(fromRaw);
          if (!Number.isNaN(fromVal) && numericCell < fromVal) return false;
        }
        if (hasTo) {
          const toVal = Number(toRaw);
          if (!Number.isNaN(toVal) && numericCell > toVal) return false;
        }
        return true;
      }
      if (mode === FILTER_MODES.multiSelect) {
        const selected = Array.isArray(filterValue) ? filterValue : [];
        if (!selected.length) return true;
        if (header.key === ON_HOLD_KEY) {
          const cellValue = cell ? "yes" : "no";
          return selected.includes(cellValue);
        }
        const normalizedCell = normaliseTextValue(cell);
        return selected.some((value) => value === normalizedCell);
      }
      if (mode === FILTER_MODES.fedexStatus) {
        const selectedValues = Array.isArray(filterValue)
          ? filterValue.map((value) => String(value || "").trim().toUpperCase()).filter(Boolean)
          : [];
        if (!selectedValues.length) return true;
        const summary = row?.[FEDEX_SUMMARY_KEY] || {};
        const statusValue = String(summary?.labelStatus || "").trim().toUpperCase();
        return selectedValues.some((value) => value === statusValue);
      }
      if (mode === FILTER_MODES.fedexTracking) {
        const tokens = parseMultiValueQuery(filterValue);
        if (!tokens.length) return true;
        const trackingNumber = normaliseTextValue(row?.[FEDEX_SUMMARY_KEY]?.trackingNumber);
        if (!trackingNumber) return false;
        return tokens.some((token) => token === trackingNumber);
      }
      if (mode === FILTER_MODES.multiText) {
        const tokens = parseMultiValueQuery(filterValue);
        if (!tokens.length) return true;
        const normalizedCell = normaliseTextValue(cell);
        return tokens.some((token) => token === normalizedCell);
      }
      const term = String(filterValue || "").trim().toLowerCase();
      if (!term) return true;
      return String(cell ?? "").toLowerCase().includes(term);
    })
  );

const applySorting = (rows, headers, sortState) => {
  if (!sortState.key || sortState.direction === 0) return rows;
  const header = headers.find((h) => h.key === sortState.key);
  if (!header) return rows;
  const direction = sortState.direction;
  const type = header.type;
  return [...rows].sort((a, b) => {
    const av = a[header.key];
    const bv = b[header.key];
    if (type === "date") {
      const aTs = parseShortDate(av) ?? 0;
      const bTs = parseShortDate(bv) ?? 0;
      return direction * (aTs - bTs);
    }
    if (type === "number") {
      const aNum = typeof av === "number" ? av : Number(String(av).replace(/,/g, ""));
      const bNum = typeof bv === "number" ? bv : Number(String(bv).replace(/,/g, ""));
      return direction * ((aNum || 0) - (bNum || 0));
    }
    return direction * String(av ?? "").localeCompare(String(bv ?? ""), undefined, { sensitivity: "base" });
  });
};

const paginate = (rows, page, pageSize) => {
  if (pageSize === "All") return rows;
  const start = (page - 1) * pageSize;
  return rows.slice(start, start + pageSize);
};

export default function RoutingStatus({ sandbox = false } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const storageSuffix = sandbox ? "_sandbox" : "";
  const dataStorageKey = `${STORAGE_DATA_KEY}${storageSuffix}`;
  const metaStorageKey = `${STORAGE_META_KEY}${storageSuffix}`;
  const widthsStorageKey = `${STORAGE_WIDTHS_KEY}${storageSuffix}`;
  const visibleColumnsStorageKey = `${STORAGE_VISIBLE_COLUMNS_KEY}_${VISIBLE_COLUMNS_VERSION}${storageSuffix}`;
  const pageLabel = sandbox ? `${ROUTING_STATUS_LABEL}<Sandbox mode>` : ROUTING_STATUS_LABEL;
  const sandboxBanner = sandbox ? "Sandbox mode — changes stay in this browser only." : "";
  const exportSheetName = sandbox ? `${EXPORT_SHEET_NAME} (Sandbox)` : EXPORT_SHEET_NAME;
  const exportFilePrefix = sandbox ? "routing-status-sandbox" : "routing-status";

  const accessVersion = useAccessOverrideVersion();
  const [dataset, setDataset] = useState(() => loadStoredDataset(dataStorageKey));
  const tableHeaders = useMemo(() => withEdiStatusColumn(dataset?.headers || []), [dataset?.headers]);
  const [fileMeta, setFileMeta] = useState(() => loadStoredMeta(metaStorageKey));
  const [versionWarning, setVersionWarning] = useState(null);
  const [error, setError] = useState("");
  const [importCorrectionConflict, setImportCorrectionConflict] = useState(null);
  const [importCorrectionSummary, setImportCorrectionSummary] = useState(null);
  const [workbookImportSummary, setWorkbookImportSummary] = useState(null);
  const [importPhase, setImportPhase] = useState("");
  const [sortState, setSortState] = useState({ key: null, direction: 0 });
  const [filters, setFilters] = useState(() => defaultFiltersFor(dataset?.headers || []));
  const [includeSuperseded, setIncludeSuperseded] = useState(false);
  const [loadHistoryRow, setLoadHistoryRow] = useState(null);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [widths, setWidths] = useState(() => loadStoredWidths(widthsStorageKey));
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(() =>
    loadStoredVisibleColumns(visibleColumnsStorageKey)
  );
  const [loadingRemote, setLoadingRemote] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const accessInfo = getUserAccessInfo();
  const { activeCustTokens, restrictCust } = accessInfo;
  const [customerOptions, setCustomerOptions] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedCustCode, setSelectedCustCode] = useState("");
  const pendingCustCodeRef = useRef("");
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const selectionModeRef = useRef("NORMAL");
  const tableContainerRef = useRef(null);
  const [missingPoBannerDismissed, setMissingPoBannerDismissed] = useState(false);
  const [bolDraft, setBolDraft] = useState(null);
  const [bolLoading, setBolLoading] = useState(false);
  const [bolModalOpen, setBolModalOpen] = useState(false);
  const [bolMissingNodes, setBolMissingNodes] = useState([]);
  const [bolForceDraftView, setBolForceDraftView] = useState(false);
  const [bolManualShipToOverrides, setBolManualShipToOverrides] = useState({});
  const [bolSelectedIndex, setBolSelectedIndex] = useState(0);
  const undoDatasetRef = useRef(null);
  const undoMetaRef = useRef(null);
  const undoWorkbookSourceRef = useRef(null);
  const undoWorkbookTokenRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [holdRules, setHoldRules] = useState([]);
  const [holdFields, setHoldFields] = useState([]);
  const [holdModalOpen, setHoldModalOpen] = useState(false);
  const [holdRulesError, setHoldRulesError] = useState("");
  const [holdForm, setHoldForm] = useState({ column: "", operator: "=", value: "" });
  const [holdFormError, setHoldFormError] = useState("");
  const [savingHoldRule, setSavingHoldRule] = useState(false);
  const [deletingHoldRuleId, setDeletingHoldRuleId] = useState(null);
  const [loadingHoldRules, setLoadingHoldRules] = useState(false);
  const [updateIlsConfirmOpen, setUpdateIlsConfirmOpen] = useState(false);
  const [updateIlsSummary, setUpdateIlsSummary] = useState(null);
  const [updatingIls, setUpdatingIls] = useState(false);
  const [legacyIlsPreview, setLegacyIlsPreview] = useState(null);
  const [legacyIlsPendingAttempt, setLegacyIlsPendingAttempt] = useState(null);
  const [legacyIlsLoading, setLegacyIlsLoading] = useState(false);
  const [creatingFedexLabels, setCreatingFedexLabels] = useState(false);
  const [voidingFedexLabelIds, setVoidingFedexLabelIds] = useState(() => new Set());
  const [fedexSummary, setFedexSummary] = useState(null);
  const [fedexChooser, setFedexChooser] = useState(null);
  const [fedexDetails, setFedexDetails] = useState(null);
  const [fedexDetailsLoading, setFedexDetailsLoading] = useState(false);
  const [fedexDetailsPayloadTab, setFedexDetailsPayloadTab] = useState("create");
  const [wmsSummary, setWmsSummary] = useState(null);
  const [wmsUpdating, setWmsUpdating] = useState(false);
  const [drilldownScopedRowIds, setDrilldownScopedRowIds] = useState(null);
  const [drilldownXPosPayload, setDrilldownXPosPayload] = useState(null);
  const [drilldownXPosLoading, setDrilldownXPosLoading] = useState(false);
  const [drilldownXPosError, setDrilldownXPosError] = useState("");
  const [breakdownView, setBreakdownView] = useState("sku");
  const [breakdownSearch, setBreakdownSearch] = useState("");
  const currentVersionDate = useMemo(
    () => parseRoutingFilenameVersionDate(fileMeta?.name || ""),
    [fileMeta?.name]
  );
  const [allocationWarning, setAllocationWarning] = useState(null);
  const pendingAllocationPairsRef = useRef(null);
  const checkAllocationVersionMismatch = useCallback(() => {
    const poMeta = loadStoredPoMeta();
    if (!poMeta?.name) return null;
    if (!currentVersionDate) return null;
    const poVersionDate = parsePoFilenameVersionDate(poMeta.name);
    if (!poVersionDate) return null;
    const diff = diffVersionDates(currentVersionDate, poVersionDate);
    if (!diff) return null;
    return {
      diff,
      routingDate: currentVersionDate,
      poDate: poVersionDate,
      routingName: fileMeta?.name || "",
      poName: poMeta.name,
    };
  }, [currentVersionDate, fileMeta?.name]);
  const multiSelectOptions = useMemo(() => {
  if (!dataset?.headers || !dataset.data) return {};
  const optionsByKey = {};
  dataset.headers.forEach((header) => {
    const canonical = canonicalizeHeaderLabel(header.label);
    const isOnHold = header.key === ON_HOLD_KEY;
    if (!isOnHold && !MULTI_SELECT_LABELS.has(canonical)) return;
    const allowBlank = canonical === OUTBOUND_CODE_CANONICAL || canonical === WMS_CANONICAL;
    const seen = new Set();
    const options = [];
    let hasBlank = false;
    if (isOnHold) {
      optionsByKey[header.key] = [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ];
      return;
    }
    dataset.data.forEach((record) => {
      const raw = record?.[header.key];
      const label = String(raw ?? "").trim();
      if (!label) {
        if (allowBlank) hasBlank = true;
        return;
      }
      const normalized = label.toLowerCase();
      if (seen.has(normalized)) return;
      seen.add(normalized);
      options.push({ value: normalized, label });
    });
    if (allowBlank && hasBlank) {
      const blankLabel = canonical === WMS_CANONICAL ? "Blank" : "(Blank)";
      options.unshift({ value: "", label: blankLabel });
    }
    options.sort((a, b) => a.label.localeCompare(b.label));
    optionsByKey[header.key] = options;
  });
  return optionsByKey;
}, [dataset]);
  const holdFieldValueOptions = useMemo(() => {
    if (!holdFields.length || !dataset?.headers?.length || !dataset?.data?.length) return {};
    const options = {};
    holdFields.forEach((field) => {
      if (!field?.label || field.type !== "text") return;
      const headerKey = findHeaderKeyByCanonicalLabel(dataset.headers, field.label);
      if (!headerKey) return;
      const seen = new Set();
      const values = [];
      dataset.data.forEach((row) => {
        const raw = row?.[headerKey];
        const label = String(raw ?? "").trim();
        if (!label) return;
        const normalized = label.toLowerCase();
        if (seen.has(normalized)) return;
        seen.add(normalized);
        values.push(label);
      });
      values.sort((a, b) => a.localeCompare(b));
      if (values.length) {
        options[field.column] = values;
      }
    });
    return options;
  }, [dataset?.data, dataset?.headers, holdFields]);
  const currentHoldField = useMemo(
    () => holdFields.find((field) => field.column === holdForm.column),
    [holdFields, holdForm.column],
  );

  useEffect(() => {
    if (!holdFields.length) return;
    setHoldForm((prev) => {
      if (prev.column && holdFields.some((field) => field.column === prev.column)) {
        return prev;
      }
      return { column: holdFields[0].column, operator: "=", value: "" };
    });
  }, [holdFields]);

  useEffect(() => {
    if (currentHoldField?.type === "text" && holdForm.operator !== "=") {
      setHoldForm((prev) => ({ ...prev, operator: "=" }));
    }
  }, [currentHoldField?.type, holdForm.operator]);
  useEffect(() => {
    const handleCopy = (event) => {
      if (selectionModeRef.current !== "PO_ONLY") return;
      const container = tableContainerRef.current;
      if (!container) return;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      const anchorNode = selection.anchorNode;
      if (anchorNode && !container.contains(anchorNode)) return;
      const poNodes = Array.from(container.querySelectorAll(".po-copy-source"));
      if (!poNodes.length) return;
      const values = [];
      const seen = new Set();
      poNodes.forEach((node) => {
        let intersects = false;
        try {
          intersects = range.intersectsNode(node);
        } catch (err) {
          intersects = false;
        }
        if (!intersects) return;
        const value = (node.getAttribute("data-po") || node.textContent || "").trim();
        if (!value || seen.has(value)) return;
        seen.add(value);
        values.push(value);
      });
      if (!values.length) return;
      event.preventDefault();
      event.clipboardData?.setData("text/plain", values.join("\n"));
    };
    document.addEventListener("copy", handleCopy);
    return () => {
      document.removeEventListener("copy", handleCopy);
    };
  }, []);
  const poHeaderKey = useMemo(
    () => findHeaderKeyByCanonicalLabel(dataset?.headers || [], PO_NUMBER_LABEL),
    [dataset?.headers],
  );
  const shippedDateHeaderKey = useMemo(
    () => findHeaderKeyByCanonicalLabel(dataset?.headers || [], SHIPPED_DATE_LABEL, SHIPPED_DATE_KEY),
    [dataset?.headers],
  );
  const shipPointHeaderKey = useMemo(
    () => findHeaderKeyByCanonicalLabel(dataset?.headers || [], SHIP_POINT_LABEL),
    [dataset?.headers],
  );
  const industryScacHeaderKey = useMemo(
    () => findHeaderKeyByCanonicalLabel(dataset?.headers || [], "Industry SCAC"),
    [dataset?.headers],
  );
  const loadHeaderKey = useMemo(
    () => findHeaderKeyByCanonicalLabel(dataset?.headers || [], LOAD_ID_LABEL),
    [dataset?.headers],
  );
  const custHeaderKey = useMemo(
    () => findHeaderKeyByCanonicalLabel(dataset?.headers || [], CUST_CODE_LABEL, CUST_CODE_KEY),
    [dataset?.headers],
  );
  const outboundHeaderKey = useMemo(
    () => findHeaderKeyByCanonicalLabel(dataset?.headers || [], OUTBOUND_CODE_LABEL),
    [dataset?.headers],
  );
  const drilldownParams = useMemo(
    () => parseRoutingDrilldownParams(location.search || ""),
    [location.search]
  );
  const isMrpShippedDrilldown = drilldownParams?.drill === "mrp_shipped";
  const accessibleRows = useMemo(() => {
    void accessVersion;
    if (!dataset?.data) return [];
    const columns = (dataset.headers || []).map((header) => header.key);
    return filterRowsByAccess(dataset.data, {
      columns,
      custKeys: [CUST_CODE_KEY, "cust_code", "customer", "customer_code"],
      allowBlank: true,
      disableWhs: true,
    });
  }, [dataset, accessVersion]);

  useEffect(() => {
    if (!dataset?.headers?.length) {
      setVisibleColumnKeys(null);
      clearStoredVisibleColumns(visibleColumnsStorageKey);
      return;
    }
    const headerKeys = tableHeaders.map((header) => header.key);
    const defaultVisibleRaw = computeDefaultVisibleKeys(tableHeaders);
    if (!defaultVisibleRaw.includes(EDI_STATUS_KEY)) defaultVisibleRaw.splice(defaultVisibleRaw.indexOf(WMS_KEY), 0, EDI_STATUS_KEY);
    const baseDefault = defaultVisibleRaw.length ? defaultVisibleRaw : headerKeys;
    const fallback = ensureRequiredColumnsInList(baseDefault, headerKeys);
    setVisibleColumnKeys((prev) => {
      if (Array.isArray(prev)) {
        if (!prev.length) return prev;
        const filtered = prev.filter((key) => headerKeys.includes(key));
        const nextBase = filtered.length ? filtered : fallback;
        const normalized = ensureRequiredColumnsInList(nextBase, headerKeys);
        if (arraysEqual(prev, normalized)) {
          return prev;
        }
        return normalized;
      }
      return fallback;
    });
  }, [dataset?.headers, tableHeaders, visibleColumnsStorageKey]);

  useEffect(() => {
    if (!Array.isArray(visibleColumnKeys) || !visibleColumnKeys.length) {
      clearStoredVisibleColumns(visibleColumnsStorageKey);
      return;
    }
    persistVisibleColumns(visibleColumnsStorageKey, visibleColumnKeys);
  }, [visibleColumnKeys, visibleColumnsStorageKey]);

  const columnOptions = useMemo(() => {
    if (!dataset?.headers) return [];
    return tableHeaders.map((header) => ({
      value: header.key,
      label: header.label || header.key,
    }));
  }, [dataset?.headers, tableHeaders]);

  const visibleColumnSet = useMemo(() => {
    if (!Array.isArray(visibleColumnKeys) || !visibleColumnKeys.length) return null;
    return new Set(visibleColumnKeys);
  }, [visibleColumnKeys]);

  const visibleHeaders = useMemo(() => {
    if (!dataset?.headers) return [];
    if (!visibleColumnSet) return tableHeaders;
    const filtered = tableHeaders.filter((header) => visibleColumnSet.has(header.key));
    return filtered.length ? filtered : tableHeaders;
  }, [dataset?.headers, tableHeaders, visibleColumnSet]);

  const columnDropdownValues = useMemo(() => {
    if (!dataset?.headers?.length) return [];
    if (Array.isArray(visibleColumnKeys) && visibleColumnKeys.length) return visibleColumnKeys;
    return computeDefaultVisibleKeys(dataset.headers);
  }, [dataset?.headers, visibleColumnKeys]);

  const handleVisibleColumnsChange = useCallback(
    (nextValues = []) => {
      if (!dataset?.headers?.length) return;
      const headerKeys = tableHeaders.map((header) => header.key);
      const filtered = (nextValues || []).filter((key) => headerKeys.includes(key));
      const base = filtered.length ? filtered : headerKeys;
      const normalized = ensureRequiredColumnsInList(base, headerKeys);
      setVisibleColumnKeys(normalized);
    },
    [dataset?.headers, tableHeaders],
  );

  useEffect(() => {
    if (!sortState.key) return;
    if (
      Array.isArray(visibleColumnKeys) &&
      visibleColumnKeys.length &&
      !visibleColumnKeys.includes(sortState.key)
    ) {
      setSortState({ key: null, direction: 0 });
    }
  }, [visibleColumnKeys, sortState.key]);

  const accessibleCustOptions = useMemo(() => {
    if (!customerOptions.length) return [];
    if (!restrictCust || !activeCustTokens.length || activeCustTokens.includes("ALL")) {
      return customerOptions;
    }
    const allowed = new Set(activeCustTokens.map((token) => token.toUpperCase()));
    return customerOptions.filter((option) => allowed.has(option.code.toUpperCase()));
  }, [customerOptions, activeCustTokens, restrictCust]);

  const inputRef = useRef(null);
  const dragRef = useRef({ col: "", startX: 0, startW: 0 });
  const headerCheckboxRef = useRef(null);
const hasOutboundSelected = useMemo(() => {
  if (!outboundHeaderKey || !dataset?.data || !selectedRows.size) return false;
  return dataset.data.some((row) => {
    if (!selectedRows.has(row._id)) return false;
    const rawValue = row?.[outboundHeaderKey];
    return rawValue !== undefined && rawValue !== null && String(rawValue).trim() !== "";
  });
}, [dataset?.data, outboundHeaderKey, selectedRows]);
const selectedRoutingRow = useMemo(() => {
  if (!dataset?.data || selectedRows.size !== 1) return null;
  const selectedId = Array.from(selectedRows)[0];
  return dataset.data.find((row) => row._id === selectedId) || null;
}, [dataset?.data, selectedRows]);
const selectedRoutingStatusId = useMemo(() => {
  if (!selectedRoutingRow) return null;
  const rawId = selectedRoutingRow[ROUTING_ROW_ID_KEY] || selectedRoutingRow._id;
  const parsed = Number(rawId);
  return Number.isFinite(parsed) ? parsed : null;
}, [selectedRoutingRow]);
const ilsEligibleRows = useMemo(() => {
  if (!dataset?.data || !selectedRows.size) return [];
  return dataset.data.filter((row) => {
    if (!selectedRows.has(row._id)) return false;
    return String(row?.[STATUS_KEY] || "").trim().toLowerCase() === "update";
  });
}, [dataset?.data, selectedRows]);
const canUpdateIls = ilsEligibleRows.length > 0;
const canCreateBol = selectedRows.size === 1 && Boolean(selectedRoutingStatusId) && !bolLoading;
const fedexEligibleRows = useMemo(() => {
  if (!dataset?.data || !selectedRows.size || !industryScacHeaderKey) return [];
  return dataset.data.filter((row) => {
    if (!selectedRows.has(row._id)) return false;
    return String(row?.[industryScacHeaderKey] || "").trim().toUpperCase() === "SSPI";
  });
}, [dataset?.data, industryScacHeaderKey, selectedRows]);
const canCreateFedexLabels =
  Boolean(dataset?.data) &&
  selectedRows.size > 0 &&
  fedexEligibleRows.length === selectedRows.size &&
  !creatingFedexLabels;

  useEffect(() => {
    persistWidths(widthsStorageKey, widths);
  }, [widths, widthsStorageKey]);
  const me = getCurrentUser();
  const { getLevel } = usePermissions();
  const permissionLevel = getLevel(me?.role, "routing");
  const importDisabled = permissionLevel !== "Full";
  const canManageFedexLabels = permissionLevel === "Full";

  const fetchRoutingStatusPayload = useCallback(async () => {
    const headers = me?.email ? { "X-User-Email": me.email, "X-User-Role": me.role || "" } : {};
    const url = new URL(`${API_BASE}/routing-status`);
    if (includeSuperseded) {
      url.searchParams.set("include_superseded", "1");
    }
    const resp = await fetch(url.toString(), { headers });
    const payload = await resp.json().catch(() => ({}));
    if (!resp.ok || payload?.ok !== true) {
      throw new Error(payload?.error || "Unable to load Routing Status dataset.");
    }
    return payload;
  }, [includeSuperseded, me?.email, me?.role]);

  const applyRoutingStatusPayload = useCallback((payload, options = {}) => {
    if (!payload) return;
    const { preserveUndo = false } = options;
    if (payload.dataset) {
      setDataset(ensureAuditColumnsForDisplay(payload.dataset));
    }
    if (payload.meta) {
      setFileMeta((prev) => toMetaState(payload.meta, prev));
    }
    if (Array.isArray(payload.holdRules)) {
      setHoldRules(payload.holdRules);
    }
    if (Array.isArray(payload.holdFields)) {
      setHoldFields(payload.holdFields);
    }
    if (!preserveUndo) {
      undoDatasetRef.current = null;
      undoMetaRef.current = null;
      undoWorkbookSourceRef.current = null;
      undoWorkbookTokenRef.current = null;
      setCanUndo(false);
    }
  }, []);

  const refreshRoutingDataset = useCallback(
    async (options = {}) => {
      const payload = await fetchRoutingStatusPayload();
      applyRoutingStatusPayload(payload, options);
      setError("");
      return payload;
    },
    [fetchRoutingStatusPayload, applyRoutingStatusPayload]
  );

  useEffect(() => {
    let cancelled = false;
    const loadRemote = async () => {
      setLoadingRemote(true);
      try {
        const payload = await fetchRoutingStatusPayload();
        if (cancelled) return;
        applyRoutingStatusPayload(payload);
        setError("");
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Unable to load Routing Status dataset.");
        }
      } finally {
        if (!cancelled) setLoadingRemote(false);
      }
    };
    loadRemote();
    return () => {
      cancelled = true;
    };
  }, [fetchRoutingStatusPayload, applyRoutingStatusPayload]);

  useEffect(() => {
    setSelectedRows(new Set());
  }, [includeSuperseded]);

  useEffect(() => {
    if (!dataset?.headers?.length || !drilldownParams) return;
    const nextFilters = defaultFiltersFor(dataset.headers);
    let hasScopedFilter = false;

    if (drilldownParams.dateField === "shipped_date" && shippedDateHeaderKey) {
      nextFilters[shippedDateHeaderKey] = {
        from: drilldownParams.dateFrom || "",
        to: drilldownParams.dateTo || "",
      };
      hasScopedFilter = true;
    }
    if (drilldownParams.shipPoint && shipPointHeaderKey) {
      nextFilters[shipPointHeaderKey] = [normaliseTextValue(drilldownParams.shipPoint)];
      hasScopedFilter = true;
    }

    if (!hasScopedFilter) return;
    setFilters(nextFilters);
    setPage(1);
  }, [dataset?.headers, drilldownParams, shippedDateHeaderKey, shipPointHeaderKey]);

  useEffect(() => {
    const skuText = String(drilldownParams?.walmartItemNo || "").trim();
    const needsXPosPayload = Boolean(skuText) || isMrpShippedDrilldown;
    if (!needsXPosPayload) {
      setDrilldownScopedRowIds(null);
      setDrilldownXPosPayload(null);
      setDrilldownXPosLoading(false);
      setDrilldownXPosError("");
      return;
    }
    if (!dataset?.data?.length) {
      setDrilldownScopedRowIds(skuText ? new Set() : null);
      setDrilldownXPosPayload(null);
      setDrilldownXPosLoading(false);
      setDrilldownXPosError("");
      return;
    }

    let cancelled = false;
    const skuUpper = skuText.toUpperCase();
    const headers = me?.email ? { "X-User-Email": me.email, "X-User-Role": me.role || "" } : {};

    const loadXPosPayload = async () => {
      setDrilldownXPosLoading(true);
      setDrilldownXPosError("");
      try {
        const resp = await fetch(`${API_BASE}/routing/x-pos`, { headers });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(payload?.error || "Unable to load routing allocation items.");
        }
        if (cancelled) return;
        setDrilldownXPosPayload(payload);

        if (!skuUpper) {
          setDrilldownScopedRowIds(null);
          return;
        }
        const { matchedRowRefs, matchedRoutingUids } = buildSkuScopedRoutingSets(payload, skuUpper);
        const scopedIds = new Set();
        (dataset.data || []).forEach((row) => {
          if (!row || typeof row !== "object") return;
          const localRowId = String(row._id || "").trim();
          if (!localRowId) return;
          const rowRef = String(row?.[ROUTING_ROW_ID_KEY] ?? row?.routing_row_id ?? row?.id ?? "").trim();
          const routingUid = String(row?.[ROUTING_UID_KEY] ?? row?.routing_uid ?? "").trim();
          if ((routingUid && matchedRoutingUids.has(routingUid)) || (rowRef && matchedRowRefs.has(rowRef))) {
            scopedIds.add(localRowId);
          }
        });
        setDrilldownScopedRowIds(scopedIds);
      } catch (err) {
        if (!cancelled) {
          console.warn("Unable to apply Item No drill-down scope on Routing Status", err);
          setDrilldownScopedRowIds(skuUpper ? new Set() : null);
          setDrilldownXPosPayload(null);
          setDrilldownXPosError(
            isMrpShippedDrilldown ? err?.message || "Unable to load allocation breakdown." : ""
          );
        }
      } finally {
        if (!cancelled) setDrilldownXPosLoading(false);
      }
    };

    loadXPosPayload();
    return () => {
      cancelled = true;
    };
  }, [
    drilldownParams?.walmartItemNo,
    isMrpShippedDrilldown,
    dataset?.data,
    me?.email,
    me?.role,
  ]);

  useEffect(() => {
    if (!isMrpShippedDrilldown) return;
    setBreakdownView("sku");
    setBreakdownSearch("");
  }, [
    isMrpShippedDrilldown,
    drilldownParams?.dateFrom,
    drilldownParams?.dateTo,
    drilldownParams?.walmartItemNo,
  ]);

  useEffect(() => {
    let cancelled = false;
    const loadCustomers = async () => {
      try {
        const headers = me?.email ? { "X-User-Email": me.email, "X-User-Role": me.role || "" } : {};
        const resp = await fetch(`${API_BASE}/customers`, { headers });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(payload?.error || "Unable to load customers dataset.");
        }
        const rows = Array.isArray(payload.rows) ? payload.rows : [];
        if (!cancelled) {
          const options = rows
            .map((row) => {
              const code = String(row?.cust_code || "").trim().toUpperCase();
              if (!code) return null;
              return {
                code,
                label: code,
              };
            })
            .filter(Boolean)
            .sort((a, b) => a.code.localeCompare(b.code));
          setCustomerOptions(options);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Unable to load customers list", err);
        }
      }
    };
    loadCustomers();
    return () => {
      cancelled = true;
    };
  }, [me?.email, me?.role]);

  const persistRemote = useCallback(
    async (nextDataset, nextMeta, options = {}) => {
      if (sandbox) return { ok: true };
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const payloadMeta = { ...(nextMeta || {}) };
      const updatedAt = payloadMeta.updatedAt instanceof Date
        ? payloadMeta.updatedAt.toISOString()
        : typeof payloadMeta.updatedAt === "string"
          ? payloadMeta.updatedAt
          : new Date().toISOString();
      payloadMeta.updatedAt = updatedAt;
      const requestBody = { dataset: nextDataset, meta: payloadMeta };
      if (options.correctionConflictMode) {
        requestBody.correctionConflictMode = options.correctionConflictMode;
      }
      if (options.workbookImport) {
        requestBody.workbookImport = true;
        if (nextDataset.loadObservation) requestBody.loadObservation = nextDataset.loadObservation;
        requestBody.statusRefreshStartedAt = options.statusRefreshStartedAt || null;
      }
      if (options.workbookUndo) {
        requestBody.workbookUndo = true;
        requestBody.workbookUndoToken = options.workbookUndoToken;
      }
      if (options.workbookImport || options.workbookUndo) {
        requestBody.workbookSource = options.workbookSource;
      }
      const resp = await sendRoutingSave(`${API_BASE}/routing-status`, {
        method: "PUT",
        headers,
        body: JSON.stringify(requestBody),
      }, Boolean(options.workbookImport && nextDataset.loadObservation));
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) {
        const message = isRoutingCorrectionCasesConflict(payload)
          ? "Cases discrepancy detected for correction-protected rows."
          : payload?.message || payload?.error || "Unable to save Routing Status dataset.";
        const err = new Error(message);
        err.payload = payload;
        err.status = resp.status;
        throw err;
      }
      return payload;
    },
    [sandbox, me?.email, me?.role]
  );

  const headerSignature = useMemo(
    () => (dataset?.headers ? dataset.headers.map((header) => header.key).join("|") : ""),
    [dataset?.headers]
  );
  const filterHeaderRef = useRef("");

  useEffect(() => {
    if (!dataset?.headers) return;
    setWidths((prev) => {
      let changed = false;
      const next = { ...prev };
      tableHeaders.forEach((header) => {
        if (!next[header.key]) {
          const approx = Math.min(
            MAX_COL_WIDTH,
            Math.max(MIN_COL_WIDTH, ((header.label?.length || 10) + 6) * 8)
          );
          next[header.key] = approx;
          changed = true;
        }
      });
      if (!next[SELECT_COLUMN_KEY]) {
        next[SELECT_COLUMN_KEY] = SELECT_COLUMN_DEFAULT_WIDTH;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [headerSignature, dataset?.headers, tableHeaders]);

  useEffect(() => {
    if (!dataset?.headers) return;
    if (filterHeaderRef.current === headerSignature) return;
    filterHeaderRef.current = headerSignature;
    setFilters(defaultFiltersFor(dataset.headers));
  }, [headerSignature, dataset?.headers]);

  useEffect(() => {
    if (!dataset) return;
    persistDataset(dataStorageKey, dataset);
  }, [dataset, dataStorageKey]);

  useEffect(() => {
    if (!importDialogOpen) return;
    if (selectedCustCode && accessibleCustOptions.some((option) => option.code === selectedCustCode)) return;
    if (accessibleCustOptions.length) {
      setSelectedCustCode(accessibleCustOptions[0].code);
    }
  }, [importDialogOpen, accessibleCustOptions, selectedCustCode]);

  useEffect(() => {
    setSelectedRows(new Set());
  }, [dataset]);

  useEffect(() => {
    if (!fileMeta) return;
    persistMeta(metaStorageKey, fileMeta);
  }, [fileMeta, metaStorageKey]);

  const importFileFromUpload = async (file, importCustCode) => {
    if (!file) return;
    const normalizedCustCode = (importCustCode || "").trim().toUpperCase();
    if (!normalizedCustCode) {
      setError("Please choose a Cust Code before importing.");
      return;
    }
    const name = file.name || "Routing Status.xlsx";
    setSaving(true);
    setImportCorrectionSummary(null);
    setWorkbookImportSummary(null);
    let pendingImportDataset = null;
    let pendingImportMeta = null;
    let pendingStatusRefreshStartedAt = null;
    let pendingWorkbookSource = null;
    try {
      undoDatasetRef.current = datasetForCustomerScope(dataset, normalizedCustCode);
      undoMetaRef.current = cloneMeta(fileMeta);
      undoWorkbookSourceRef.current = null;
      undoWorkbookTokenRef.current = null;
      setCanUndo(false);
      setImportPhase("Reading workbook...");
      const { rows, sheetName, buffer } = await loadWorkbookFromFile(file);
      const workbookSha = await workbookBytesSha256(buffer);
      const workbookSource = {
        custCode: normalizedCustCode,
        name,
        sha256: workbookSha,
        sheet: sheetName,
        contentsBase64: workbookBytesBase64(buffer),
      };
      pendingWorkbookSource = workbookSource;
      undoWorkbookSourceRef.current = workbookSource;
      const observations = strideObservations(rows);
      const preparedRows = prepareRowsForImport(adaptRoutingStatusRows(rows));
      const builtDataset = buildDataset(preparedRows);
      const datasetWithCust = {
        ...builtDataset,
        data: builtDataset.data.map((record) => ({ ...record, [CUST_CODE_KEY]: normalizedCustCode })),
      };
      const incomingPoKey = findHeaderKeyByCanonicalLabel(datasetWithCust.headers || [], PO_NUMBER_LABEL);
      const incomingPos = new Set(datasetWithCust.data.map((row) => String(row?.[incomingPoKey] || "").trim()).filter(Boolean));
      const relevantRowIds = (dataset?.data || [])
        .filter((row) => String(row?.[custHeaderKey] || row?.[CUST_CODE_KEY] || "").trim().toUpperCase() === normalizedCustCode)
        .filter((row) => incomingPos.has(String(row?.[poHeaderKey] || "").trim()))
        .map((row) => Number(row?.[ROUTING_ROW_ID_KEY] || row?._id || 0))
        .filter((value) => Number.isInteger(value) && value > 0);
      const statusRefreshStartedAt = new Date().toISOString();
      pendingStatusRefreshStartedAt = statusRefreshStartedAt;
      setImportPhase("Updating iLS Status...");
      const wmsResult = sandbox
        ? { ok: true, dataset }
        : await performWmsStatusUpdate({ preserveUndo: true, routingRowIds: relevantRowIds });
      const refreshedDataset = wmsResult?.dataset || dataset;
      const operationalDataset = observations ? preserveObservedOperationalRows(datasetWithCust, refreshedDataset, observations) : datasetWithCust;
      const mergedDataset = mergeDatasetWithHistory(operationalDataset, refreshedDataset, {
        custCode: normalizedCustCode,
        carryForwardUnmatchedHistory: false,
      });
      const nextDataset = ensureAuditColumnsForDisplay(mergedDataset);
      if (observations) {
        const time = name.match(/(\d{2})-(\d{2})-(\d{4})_(\d{2})-(\d{2})-(\d{2})-\d+_(AM|PM)/i);
        const hour = time ? (Number(time[4]) % 12) + (time[7].toUpperCase() === 'PM' ? 12 : 0) : null;
        nextDataset.loadObservation = {
          custCode: normalizedCustCode, importRunId: window.crypto.randomUUID(),
          workbookSha256: workbookSha, sheet: sheetName, observations,
          workbookExportLocal: time ? `${time[3]}-${time[1]}-${time[2]}T${String(hour).padStart(2,'0')}:${time[5]}:${time[6]}` : null,
        };
      }

      const meta = toMetaState({ name, sheetName, updatedAt: new Date() });
      pendingImportDataset = nextDataset;
      pendingImportMeta = meta;
      setDataset(nextDataset);
      setFileMeta(meta);
      persistDataset(dataStorageKey, nextDataset);
      persistMeta(metaStorageKey, meta);
      setImportPhase("Importing workbook...");
      const saveResult = await persistRemote(nextDataset, meta, {
        workbookImport: true,
        workbookSource,
        statusRefreshStartedAt,
      });
      setImportCorrectionConflict(null);
      setImportCorrectionSummary(saveResult?.correctionImport || null);
      setWorkbookImportSummary(saveResult?.workbookImport || null);
      undoWorkbookTokenRef.current = sandbox ? "sandbox" : saveResult?.workbookUndoToken || null;
      if (!sandbox) await refreshRoutingDataset({ preserveUndo: true });
      setCanUndo(Boolean(undoDatasetRef.current && undoWorkbookTokenRef.current));
      setError("");
    } catch (err) {
      console.error(err);
      if (isRoutingCorrectionCasesConflict(err?.payload)) {
        setImportCorrectionConflict({
          payload: err.payload,
          dataset: cloneDataset(pendingImportDataset),
          meta: cloneMeta(pendingImportMeta),
          previousDisplayDataset: cloneDataset(dataset),
          previousDataset: cloneDataset(undoDatasetRef.current),
          previousMeta: cloneMeta(undoMetaRef.current),
          workbookSource: pendingWorkbookSource,
          statusRefreshStartedAt: pendingStatusRefreshStartedAt,
        });
        setError("");
        return;
      }
      setError(err.message || "Unable to import workbook.");
    } finally {
      pendingCustCodeRef.current = "";
      setSaving(false);
      setImportPhase("");
    }
  };

  const getVersionWarningForFile = useCallback(
    (file, importCustCode) => {
      if (!file || !currentVersionDate) return null;
      const updateDate = parseRoutingFilenameVersionDate(file.name || "");
      if (!updateDate) return null;
      const diff = diffVersionDates(updateDate, currentVersionDate);
      if (diff === null) return null;
      if (diff < 0) {
        return { type: "older", file, importCustCode, currentDate: currentVersionDate, updateDate };
      }
      if (diff > 1) {
        return { type: "skip", file, importCustCode, currentDate: currentVersionDate, updateDate };
      }
      return null;
    },
    [currentVersionDate]
  );

  const handleCancelVersionWarning = () => {
    setVersionWarning(null);
    pendingCustCodeRef.current = "";
  };

  const handleConfirmVersionWarning = () => {
    if (!versionWarning?.file || !versionWarning?.importCustCode) {
      handleCancelVersionWarning();
      return;
    }
    const { file, importCustCode } = versionWarning;
    setVersionWarning(null);
    void importFileFromUpload(file, importCustCode);
  };

  const handleCancelAllocationWarning = () => {
    setAllocationWarning(null);
    pendingAllocationPairsRef.current = null;
  };

  const handleConfirmAllocationWarning = () => {
    const pairs = pendingAllocationPairsRef.current;
    setAllocationWarning(null);
    pendingAllocationPairsRef.current = null;
    if (!pairs || !pairs.length) return;
    void performAllocation(pairs);
  };

  const handleCancelCorrectionImportConflict = () => {
    const previousDataset = cloneDataset(importCorrectionConflict?.previousDisplayDataset);
    const previousMeta = cloneMeta(importCorrectionConflict?.previousMeta);
    if (previousDataset) {
      setDataset(previousDataset);
      persistDataset(dataStorageKey, previousDataset);
    }
    if (previousMeta) {
      setFileMeta(previousMeta);
      persistMeta(metaStorageKey, previousMeta);
    }
    setImportCorrectionConflict(null);
    setError("Routing Status import was not saved because cases on protected rows require a decision.");
  };

  const continueCorrectionImportWithMode = async (mode) => {
    if (!importCorrectionConflict?.dataset) return;
    setSaving(true);
    try {
      const saveResult = await persistRemote(importCorrectionConflict.dataset, importCorrectionConflict.meta, {
        correctionConflictMode: mode,
        workbookImport: true,
        workbookSource: importCorrectionConflict.workbookSource,
        statusRefreshStartedAt: importCorrectionConflict.statusRefreshStartedAt,
      });
      setImportCorrectionConflict(null);
      setImportCorrectionSummary(saveResult?.correctionImport || null);
      setWorkbookImportSummary(saveResult?.workbookImport || null);
      undoWorkbookTokenRef.current = sandbox ? "sandbox" : saveResult?.workbookUndoToken || null;
      if (!sandbox) await refreshRoutingDataset({ preserveUndo: true });
      setCanUndo(Boolean(undoDatasetRef.current && undoWorkbookTokenRef.current));
      setError("");
    } catch (err) {
      console.error(err);
      if (isRoutingCorrectionCasesConflict(err?.payload)) {
        setImportCorrectionConflict((prev) => ({ ...(prev || {}), payload: err.payload }));
        setError("");
      } else {
        setError(err?.message || "Unable to continue Routing Status import.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTriggerUpload = () => {
    if (importDisabled) {
      setError("Import is disabled for your role.");
      return;
    }
    if (saving) return;
    if (!accessibleCustOptions.length) {
      setError("No accessible Cust Code is available for import.");
      return;
    }
    setError("");
    setSelectedCustCode((prev) => prev || accessibleCustOptions[0]?.code || "");
    setImportDialogOpen(true);
  };

  const handleCancelImportDialog = () => {
    setImportDialogOpen(false);
    pendingCustCodeRef.current = "";
  };

  const handleConfirmImportDialog = () => {
    const code = (selectedCustCode || "").trim().toUpperCase();
    if (!code) {
      setError("Please select a Cust Code before importing.");
      return;
    }
    pendingCustCodeRef.current = code;
    setImportDialogOpen(false);
    setTimeout(() => inputRef.current?.click(), 0);
  };

  const handleFileChange = async (event) => {
    if (importDisabled) {
      event.target.value = "";
      setError("Import is disabled for your role.");
      return;
    }
    setError("");
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const importCustCode = (pendingCustCodeRef.current || selectedCustCode || "").trim().toUpperCase();
    if (!importCustCode) {
      setError("Please choose a Cust Code before importing.");
      return;
    }
    const warningState = getVersionWarningForFile(file, importCustCode);
    if (warningState) {
      setVersionWarning(warningState);
      return;
    }
    await importFileFromUpload(file, importCustCode);
  };

  const handleUndoImport = async () => {
    if (!undoDatasetRef.current) return;
    setSaving(true);
    try {
      const restoredDataset = cloneDataset(undoDatasetRef.current);
      const restoredMeta = cloneMeta(undoMetaRef.current);
      const workbookSource = undoWorkbookSourceRef.current;
      const workbookUndoToken = undoWorkbookTokenRef.current;
      if (!restoredDataset || !workbookSource || !workbookUndoToken) throw new Error("Nothing to undo.");
      await persistRemote(restoredDataset, restoredMeta || null, {
        workbookUndo: true,
        workbookSource,
        workbookUndoToken,
      });
      if (!sandbox) await refreshRoutingDataset({ preserveUndo: true });
      undoDatasetRef.current = null;
      undoMetaRef.current = null;
      undoWorkbookSourceRef.current = null;
      undoWorkbookTokenRef.current = null;
      setCanUndo(false);
      setError("");
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to undo import.");
    } finally {
      setSaving(false);
    }
  };

  const fetchHoldRulesList = useCallback(async () => {
    setLoadingHoldRules(true);
    setHoldRulesError("");
    try {
      const headers = {};
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const resp = await fetch(`${API_BASE}/routing-status/hold-rules`, { headers });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) {
        throw new Error(payload?.error || "Unable to load Hold OB Create rules.");
      }
      if (Array.isArray(payload.rules)) {
        setHoldRules(payload.rules);
      }
      if (Array.isArray(payload.fields) && payload.fields.length) {
        setHoldFields(payload.fields);
      }
      setHoldRulesError("");
    } catch (err) {
      console.error(err);
      setHoldRulesError(err?.message || "Unable to load Hold OB Create rules.");
    } finally {
      setLoadingHoldRules(false);
    }
  }, [me?.email, me?.role]);

  const handleOpenHoldModal = useCallback(() => {
    setHoldFormError("");
    setHoldRulesError("");
    setHoldModalOpen(true);
    void fetchHoldRulesList();
  }, [fetchHoldRulesList]);

  const handleCloseHoldModal = useCallback(() => {
    setHoldModalOpen(false);
    setHoldFormError("");
    setHoldRulesError("");
  }, []);

  const handleSaveHoldRule = useCallback(async () => {
    if (!holdForm.column) {
      setHoldFormError("Please choose a field.");
      return;
    }
    const valueText = String(holdForm.value ?? "").trim();
    if (!valueText) {
      setHoldFormError("Please choose a value.");
      return;
    }
    setSavingHoldRule(true);
    setHoldFormError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const resp = await fetch(`${API_BASE}/routing-status/hold-rules`, {
        method: "POST",
        headers,
        body: JSON.stringify({ column: holdForm.column, operator: holdForm.operator || "=", value: valueText }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) {
        throw new Error(payload?.error || "Unable to save Hold OB Create rule.");
      }
      if (Array.isArray(payload.rules)) {
        setHoldRules(payload.rules);
      }
      if (Array.isArray(payload.fields) && payload.fields.length) {
        setHoldFields(payload.fields);
      }
      setHoldForm((prev) => ({ ...prev, value: "" }));
      await refreshRoutingDataset();
    } catch (err) {
      console.error(err);
      setHoldFormError(err?.message || "Unable to save Hold OB Create rule.");
    } finally {
      setSavingHoldRule(false);
    }
  }, [holdForm, me?.email, me?.role, refreshRoutingDataset]);

  const handleDeleteHoldRule = useCallback(
    async (ruleId) => {
      if (!ruleId) return;
      setDeletingHoldRuleId(ruleId);
      setHoldRulesError("");
      try {
        const headers = {};
        if (me?.email) headers["X-User-Email"] = me.email;
        if (me?.role) headers["X-User-Role"] = me.role || "";
        const resp = await fetch(`${API_BASE}/routing-status/hold-rules/${ruleId}`, {
          method: "DELETE",
          headers,
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(payload?.error || "Unable to delete Hold OB Create rule.");
        }
        if (Array.isArray(payload.rules)) {
          setHoldRules(payload.rules);
        }
        await refreshRoutingDataset();
      } catch (err) {
        console.error(err);
        setHoldRulesError(err?.message || "Unable to delete Hold OB Create rule.");
      } finally {
        setDeletingHoldRuleId(null);
      }
    },
    [me?.email, me?.role, refreshRoutingDataset],
  );

  const handleStartUpdateIls = useCallback(() => {
    if (!canUpdateIls || updatingIls) return;
    setUpdateIlsConfirmOpen(true);
  }, [canUpdateIls, updatingIls]);

  const handleCancelUpdateIls = useCallback(() => {
    if (updatingIls) return;
    setUpdateIlsConfirmOpen(false);
  }, [updatingIls]);

  const handleConfirmUpdateIls = useCallback(async () => {
    if (!ilsEligibleRows.length || updatingIls) return;
    const normalize = (value) => (value === undefined || value === null ? "" : String(value).trim());
    const rowsPayload = ilsEligibleRows
      .map((row) => {
        const routingRowId = normalize(row[ROUTING_ROW_ID_KEY] || row._id || "");
        return {
          routingRowId,
          [ROUTING_ROW_ID_KEY]: routingRowId,
          custCode: normalize(custHeaderKey ? row[custHeaderKey] : row[CUST_CODE_KEY]),
          poNumber: normalize(poHeaderKey ? row[poHeaderKey] : row[PO_NUMBER_LABEL]),
          loadId: normalize(loadHeaderKey ? row[loadHeaderKey] : row[LOAD_ID_LABEL]),
          outboundCode: normalize(outboundHeaderKey ? row[outboundHeaderKey] : row[OUTBOUND_CODE_LABEL]),
        };
      })
      .filter((entry) => entry.routingRowId);
    if (!rowsPayload.length) {
      setError("Selected rows are missing routing identifiers.");
      return;
    }
    setUpdatingIls(true);
    setError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const resp = await fetch(`${API_BASE}/routing-status/ils-update`, {
        method: "POST",
        headers,
        body: JSON.stringify({ rows: rowsPayload }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || payload?.ok !== true) {
        const fallback = classifyHistoricalUpdateFallback(payload, rowsPayload.length);
        if (fallback.action === "PREVIEW") {
          setUpdateIlsConfirmOpen(false);
          setLegacyIlsLoading(true);
          try {
            const preview = await previewLegacyIlsUpdate(API_BASE, headers, fallback.routingRowId);
            setLegacyIlsPreview(preview);
          } finally {
            setLegacyIlsLoading(false);
          }
          return;
        }
        if (fallback.action === "ERROR") {
          throw new Error(fallback.message);
        }
        throw new Error(payload?.message || payload?.error || "Unable to update iLS.");
      }
      if (payload.dataset) {
        applyRoutingStatusPayload(payload);
      } else {
        await refreshRoutingDataset();
      }
      setSelectedRows(new Set());
      undoDatasetRef.current = null;
      undoMetaRef.current = null;
      undoWorkbookSourceRef.current = null;
      undoWorkbookTokenRef.current = null;
      setCanUndo(false);
      setUpdateIlsSummary({
        attempted: payload.attempted ?? rowsPayload.length,
        successCount: payload.successCount ?? (payload.succeeded?.length || 0),
        failureCount: payload.failureCount ?? (payload.failed?.length || 0),
        succeeded: Array.isArray(payload.succeeded) ? payload.succeeded : [],
        failed: Array.isArray(payload.failed) ? payload.failed : [],
      });
      setUpdateIlsConfirmOpen(false);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to update iLS.");
    } finally {
      setUpdatingIls(false);
    }
  }, [
    ilsEligibleRows,
    updatingIls,
    custHeaderKey,
    poHeaderKey,
    loadHeaderKey,
    outboundHeaderKey,
    me?.email,
    me?.role,
    applyRoutingStatusPayload,
    refreshRoutingDataset,
  ]);

  const handleOpenBolPreview = useCallback(async () => {
    if (bolLoading) return;
    if (!selectedRoutingStatusId) {
      setError("Selected row is missing routing identifier.");
      return;
    }
    setError("");
    setBolLoading(true);
    setBolModalOpen(true);
    setBolDraft(null);
    setBolSelectedIndex(0);
    setBolMissingNodes([]);
    setBolForceDraftView(false);
    setBolManualShipToOverrides({});
    try {
      const headers = me?.email ? { "X-User-Email": me.email, "X-User-Role": me.role || "" } : {};
      const resp = await fetch(`${API_BASE}/api/admin/bol-draft/${selectedRoutingStatusId}`, { headers });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        if (payload?.error === "MISSING_SHIP_TO_ADDRESS" && Array.isArray(payload?.missing_address_nodes)) {
          setBolMissingNodes(payload.missing_address_nodes.filter((node) => node));
          return;
        }
        if (resp.status === 403) throw new Error("Forbidden");
        if (resp.status === 404) throw new Error("Not found");
        if (resp.status === 400) throw new Error(payload?.error || "Bad request");
        throw new Error(payload?.error || "Unable to load BOL draft.");
      }
      setBolDraft(payload);
    } catch (err) {
      setBolModalOpen(false);
      setBolDraft(null);
      setBolMissingNodes([]);
      setError(err?.message || "Unable to load BOL draft.");
    } finally {
      setBolLoading(false);
    }
  }, [bolLoading, me?.email, me?.role, selectedRoutingStatusId]);

  const handleCloseBolPreview = useCallback(() => {
    setBolModalOpen(false);
    setBolDraft(null);
    setBolSelectedIndex(0);
    setBolMissingNodes([]);
    setBolForceDraftView(false);
    setBolManualShipToOverrides({});
  }, []);

  const handleResolveMissingBolAddresses = useCallback(
    async (overrides) => {
      if (!selectedRoutingStatusId) return;
      setBolLoading(true);
      setError("");
      setBolForceDraftView(true);
      setBolManualShipToOverrides((prev) => ({ ...(prev || {}), ...(overrides || {}) }));
      try {
        const headers = me?.email ? { "X-User-Email": me.email, "X-User-Role": me.role || "" } : {};
        const query = encodeURIComponent(JSON.stringify(overrides || {}));
        if (process.env.NODE_ENV !== "production") {
          console.info("BOL draft override request", { routingStatusId: selectedRoutingStatusId, overrides });
        }
        const resp = await fetch(
          `${API_BASE}/api/admin/bol-draft/${selectedRoutingStatusId}?manual_ship_to_overrides=${query}`,
          { headers }
        );
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          if (payload?.error === "MISSING_SHIP_TO_ADDRESS" && Array.isArray(payload?.missing_address_nodes)) {
            setBolMissingNodes(payload.missing_address_nodes.filter((node) => node));
            return;
          }
          throw new Error(payload?.error || "Unable to load BOL draft.");
        }
        setBolDraft(payload);
        setBolMissingNodes([]);
      } catch (err) {
        setBolDraft(null);
        setBolMissingNodes([]);
        setBolModalOpen(false);
        setBolForceDraftView(false);
        setBolManualShipToOverrides({});
        setError(err?.message || "Unable to load BOL draft.");
      } finally {
        setBolLoading(false);
      }
    },
    [me?.email, me?.role, selectedRoutingStatusId]
  );

  const handleCloseUpdateIlsSummary = useCallback(() => {
    setUpdateIlsSummary(null);
  }, []);

  const handleCloseFedexSummary = useCallback(() => {
    setFedexSummary(null);
  }, []);

  const handleCloseFedexChooser = useCallback(() => {
    setFedexChooser(null);
  }, []);

  const handleCloseFedexDetails = useCallback(() => {
    setFedexDetails(null);
    setFedexDetailsLoading(false);
    setFedexDetailsPayloadTab("create");
  }, []);

  const copyFedexText = useCallback(async (value, successMessage) => {
    const text = String(value || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      setError(successMessage || "Unable to copy.");
    }
  }, []);

  const openFedexDetails = useCallback(
    async (target = {}) => {
      const query = new URLSearchParams();
      const labelId = String(target?.labelId || target?.label_id || "").trim();
      const trackingNumber = String(target?.trackingNumber || target?.tracking_number || "").trim();
      const routingRowId = String(target?.routingRowId || target?.routing_row_id || "").trim();
      const routingUid = String(target?.routingUid || target?.routing_uid || "").trim();
      if (labelId) query.set("label_id", labelId);
      if (trackingNumber) query.set("tracking_number", trackingNumber);
      if (routingRowId) query.set("routing_row_id", routingRowId);
      if (routingUid) query.set("routing_uid", routingUid);
      if (![...query.keys()].length) {
        setError("FedEx label details target is missing.");
        return;
      }
      setError("");
      setFedexDetails(null);
      setFedexDetailsLoading(true);
      setFedexDetailsPayloadTab("create");
      try {
        const headers = buildAccessHeaders(me, accessInfo);
        const resp = await fetch(`${API_BASE}/api/routing-status/fedex-labels/details?${query.toString()}`, {
          method: "GET",
          headers,
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(payload?.error || "Unable to load FedEx label details.");
        }
        const defaultTab = payload?.payloads?.create?.available
          ? "create"
          : payload?.payloads?.void?.available
            ? "void"
            : payload?.payloads?.voidFailureDiagnostic?.available
              ? "voidFailureDiagnostic"
              : "create";
        setFedexDetails(payload);
        setFedexDetailsPayloadTab(defaultTab);
      } catch (err) {
        console.error(err);
        setFedexDetails(null);
        setError(err?.message || "Unable to load FedEx label details.");
      } finally {
        setFedexDetailsLoading(false);
      }
    },
    [accessInfo, me]
  );

  const buildFedexRowsPayloadFromTarget = useCallback((target = {}) => {
    const routingRowId = String(target?.routingRowId || target?.routing_row_id || "").trim();
    if (!routingRowId) return [];
    const custCode = String(target?.custCode || target?.cust_code || "").trim();
    const poNumber = String(target?.poNumber || target?.po_number || "").trim();
    const loadId = String(target?.loadId || target?.load_id || "").trim();
    return [
      {
        routingRowId,
        [ROUTING_ROW_ID_KEY]: routingRowId,
        custCode,
        poNumber,
        loadId,
      },
    ];
  }, []);

  const buildCurrentFedexDetailsTarget = useCallback(() => {
    if (!fedexDetails) return null;
    return {
      labelId: fedexDetails?.lifecycle?.labelId,
      trackingNumber: fedexDetails?.lifecycle?.trackingNumber,
      routingRowId: fedexDetails?.routingSummary?.routingRowId,
      routingUid: fedexDetails?.routingSummary?.routingUid,
      custCode: fedexDetails?.routingSummary?.custCode,
      poNumber: fedexDetails?.routingSummary?.poNumber,
      loadId: fedexDetails?.routingSummary?.loadId,
      bolNumber: fedexDetails?.routingSummary?.bolNumber,
    };
  }, [fedexDetails]);

  const handleDismissWmsSummary = useCallback(() => {
    setWmsSummary(null);
  }, []);

  const performWmsStatusUpdate = useCallback(
    async (options = {}) => {
      const { preserveUndo = false, routingRowIds = [] } = options;
      if (wmsUpdating) {
        return { ok: false, reason: "in_progress" };
      }
      setWmsSummary(null);
      setError("");
      setWmsUpdating(true);
      try {
        const headers = { "Content-Type": "application/json" };
        if (me?.email) headers["X-User-Email"] = me.email;
        if (me?.role) headers["X-User-Role"] = me.role || "";
        const resp = await fetch(`${API_BASE}/routing-status/wms-status`, {
          method: "POST",
          headers,
          body: JSON.stringify(routingRowIds.length ? { routingRowIds } : {}),
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(payload?.error || "Unable to refresh WMS status.");
        }
        if (payload.dataset || payload.meta || payload.holdRules) {
          applyRoutingStatusPayload(payload, { preserveUndo });
        } else {
          await refreshRoutingDataset({ preserveUndo });
        }
        setWmsSummary({
          successCount: payload?.successCount ?? 0,
          failureCount: payload?.failureCount ?? 0,
          skipped: payload?.skipped ?? 0,
          attempted: payload?.attempted ?? 0,
          failed: Array.isArray(payload?.failed) ? payload.failed : [],
        });
        setError("");
        return { ...payload, ok: true };
      } catch (err) {
        console.error(err);
        setError(err?.message || "Unable to refresh WMS status.");
        return { ok: false, error: err };
      } finally {
        setWmsUpdating(false);
      }
    },
    [wmsUpdating, me?.email, me?.role, applyRoutingStatusPayload, refreshRoutingDataset]
  );

  const handleWmsStatusUpdate = useCallback(() => {
    void performWmsStatusUpdate();
  }, [performWmsStatusUpdate]);

  const buildFedexRowsPayload = useCallback(() => {
    if (!dataset?.data || !selectedRows.size) return [];
    const normalize = (value) => (value === undefined || value === null ? "" : String(value).trim());
    return dataset.data
      .filter((row) => selectedRows.has(row._id))
      .map((row) => {
        const routingRowId = normalize(row[ROUTING_ROW_ID_KEY] || row._id || "");
        return {
          routingRowId,
          [ROUTING_ROW_ID_KEY]: routingRowId,
          custCode: normalize(custHeaderKey ? row[custHeaderKey] : row[CUST_CODE_KEY]),
          poNumber: normalize(poHeaderKey ? row[poHeaderKey] : row[PO_NUMBER_LABEL]),
          loadId: normalize(loadHeaderKey ? row[loadHeaderKey] : row[LOAD_ID_LABEL]),
        };
      })
      .filter((entry) => entry.routingRowId);
  }, [custHeaderKey, dataset?.data, loadHeaderKey, poHeaderKey, selectedRows]);

  const executeFedexLabelAction = useCallback(
    async (action, rowsPayload, options = {}) => {
      if (!rowsPayload?.length) {
        setError("Selected rows are missing routing identifiers.");
        return;
      }
      setCreatingFedexLabels(true);
      setFedexSummary(null);
      setFedexChooser(null);
      setError("");
      try {
        const headers = {
          "Content-Type": "application/json",
          ...buildAccessHeaders(me, accessInfo),
        };
        const resp = await fetch(`${API_BASE}/routing-status/fedex-labels`, {
          method: "POST",
          headers,
          body: JSON.stringify({ rows: rowsPayload, action }),
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          if (resp.status === 409 && payload?.error === "existing_labels_found") {
            setFedexChooser({
              rowsPayload,
              existingCount: payload?.existingCount ?? 0,
              rows: Array.isArray(payload?.rows) ? payload.rows : [],
              originDetailsTarget: options?.originDetailsTarget || null,
            });
            return;
          }
          throw new Error(getFedexLabelErrorMessage(payload?.error, payload?.message));
        }
        setSelectedRows(new Set());
        await refreshRoutingDataset({ preserveUndo: true });
        if (!options?.suppressSummary) {
          setFedexSummary({
            action: payload.action || action,
            attempted: payload.attempted ?? rowsPayload.length,
            successCount: payload.successCount ?? (payload.succeeded?.length || 0),
            failureCount: payload.failureCount ?? (payload.failed?.length || 0),
            succeeded: Array.isArray(payload.succeeded) ? payload.succeeded : [],
            failed: Array.isArray(payload.failed) ? payload.failed : [],
          });
        }
        if (options?.reopenDetailsTarget) {
          await openFedexDetails(options.reopenDetailsTarget);
        }
      } catch (err) {
        console.error(err);
        setError(err?.message || "Unable to process FedEx labels.");
      } finally {
        setCreatingFedexLabels(false);
      }
    },
    [accessInfo, me, openFedexDetails, refreshRoutingDataset]
  );

  const startFedexCreateFlow = useCallback(
    async (rowsPayload, options = {}) => {
      if (!rowsPayload?.length) {
        setError("Selected rows are missing routing identifiers.");
        return;
      }
      setCreatingFedexLabels(true);
      setFedexSummary(null);
      setFedexChooser(null);
      setError("");
      try {
        const headers = {
          "Content-Type": "application/json",
          ...buildAccessHeaders(me, accessInfo),
        };
        const resp = await fetch(`${API_BASE}/routing-status/fedex-labels/precheck`, {
          method: "POST",
          headers,
          body: JSON.stringify({ rows: rowsPayload }),
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(getFedexLabelErrorMessage(payload?.error, payload?.message) || "Unable to pre-check FedEx labels.");
        }
        const existingCount = payload?.existingCount ?? 0;
        if (existingCount > 0) {
          setFedexChooser({
            rowsPayload,
            existingCount,
            noExistingCount: payload?.noExistingCount ?? 0,
            rows: Array.isArray(payload?.rows) ? payload.rows : [],
            originDetailsTarget: options?.originDetailsTarget || null,
          });
          return;
        }
        await executeFedexLabelAction("create_new", rowsPayload, options);
      } catch (err) {
        console.error(err);
        setError(err?.message || "Unable to pre-check FedEx labels.");
      } finally {
        setCreatingFedexLabels(false);
      }
    },
    [accessInfo, executeFedexLabelAction, me]
  );

  const handleCreateFedexLabels = useCallback(async () => {
    if (!dataset?.data || !selectedRows.size) return;
    if (!industryScacHeaderKey) {
      setError("Routing dataset is missing Industry SCAC.");
      return;
    }
    const rowsPayload = buildFedexRowsPayload();
    if (!rowsPayload.length) {
      setError("Selected rows are missing routing identifiers.");
      return;
    }
    await startFedexCreateFlow(rowsPayload);
  }, [
    buildFedexRowsPayload,
    dataset?.data,
    industryScacHeaderKey,
    selectedRows,
    startFedexCreateFlow,
  ]);

  const legacyHeaders = useMemo(() => {
    const headers = {};
    if (me?.email) headers["X-User-Email"] = me.email;
    if (me?.role) headers["X-User-Role"] = me.role || "";
    return headers;
  }, [me?.email, me?.role]);

  const refreshLegacyPending = useCallback(async () => {
    try {
      const payload = await loadPendingLegacyIlsUpdates(API_BASE, legacyHeaders);
      setLegacyIlsPendingAttempt(Array.isArray(payload?.pending) ? payload.pending[0] || null : null);
    } catch (err) {
      console.warn("Unable to load pending Legacy iLS verification", err);
    }
  }, [legacyHeaders]);

  useEffect(() => {
    refreshLegacyPending();
  }, [refreshLegacyPending]);

  const handleExecuteLegacyIlsUpdate = useCallback(
    async ({ riskAccepted, confirmation }) => {
      if (!legacyIlsPreview || legacyIlsLoading) return;
      setLegacyIlsLoading(true);
      setError("");
      try {
        const result = await executeLegacyIlsUpdate(
          API_BASE,
          legacyHeaders,
          legacyIlsPreview,
          riskAccepted,
          confirmation,
        );
        setLegacyIlsPreview(null);
        setLegacyIlsPendingAttempt({
          attemptId: result.attemptId,
          po: result.po,
          outboundCode: result.outboundCode,
          expectedLines: result.expectedLines,
          expectedTotal: result.expectedTotal,
        });
      } catch (err) {
        setError(err?.message || "Unable to execute Legacy iLS Update.");
      } finally {
        setLegacyIlsLoading(false);
      }
    },
    [legacyHeaders, legacyIlsLoading, legacyIlsPreview],
  );

  const handleLegacyVerification = useCallback(
    async (result, verificationNote = "") => {
      if (!legacyIlsPendingAttempt || legacyIlsLoading) return;
      setLegacyIlsLoading(true);
      setError("");
      try {
        await verifyLegacyIlsUpdate(
          API_BASE,
          legacyHeaders,
          legacyIlsPendingAttempt.attemptId,
          result,
          verificationNote,
        );
        setLegacyIlsPendingAttempt(null);
        await refreshLegacyPending();
      } catch (err) {
        setError(err?.message || "Unable to record manual Products verification.");
      } finally {
        setLegacyIlsLoading(false);
      }
    },
    [legacyHeaders, legacyIlsLoading, legacyIlsPendingAttempt, refreshLegacyPending],
  );

  const saveFedexBlobAsFile = useCallback((blob, fileName) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  }, []);

  const fetchFedexArtifactBlob = useCallback(
    async (relativeUrl, fallbackMessage) => {
      const url = typeof relativeUrl === "string" ? relativeUrl.trim() : "";
      if (!url) {
        throw new Error(fallbackMessage || "FedEx label artifact is missing.");
      }
      const headers = {
        ...buildAccessHeaders(me, accessInfo),
      };
      const resp = await fetch(`${API_BASE}${url}`, {
        method: "GET",
        headers,
      });
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(getFedexLabelErrorMessage(payload?.error, payload?.message) || fallbackMessage || "Unable to fetch FedEx label.");
      }
      return resp.blob();
    },
    [accessInfo, me]
  );

  const handleDownloadFedexArtifactUrl = useCallback(
    async (relativeUrl, fallbackName) => {
      try {
        const blob = await fetchFedexArtifactBlob(relativeUrl, "Unable to download FedEx label.");
        saveFedexBlobAsFile(blob, fallbackName || "fedex-label");
      } catch (err) {
        console.error(err);
        setError(err?.message || "Unable to download FedEx label.");
      }
    },
    [fetchFedexArtifactBlob, saveFedexBlobAsFile]
  );

  const base64ToUint8Array = useCallback((value) => {
    const normalized = typeof value === "string" ? value.trim() : "";
    const binary = window.atob(normalized);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  }, []);

  const renderFedexZplToPdfBlob = useCallback(
    async (zplText) => {
      const [{ ready }, { PDFDocument }] = await Promise.all([
        import("zpl-renderer-js"),
        import("pdf-lib"),
      ]);
      const { api } = await ready;
      const labels = await api.zplToBase64MultipleAsync(
        zplText,
        FEDEX_ZPL_WIDTH_MM,
        FEDEX_ZPL_HEIGHT_MM,
        8
      );
      const pdfDoc = await PDFDocument.create();
      for (const encodedPng of labels) {
        const pngImage = await pdfDoc.embedPng(base64ToUint8Array(encodedPng));
        const page = pdfDoc.addPage([FEDEX_PDF_PAGE_WIDTH_PT, FEDEX_PDF_PAGE_HEIGHT_PT]);
        page.drawImage(pngImage, {
          x: 0,
          y: 0,
          width: FEDEX_PDF_PAGE_WIDTH_PT,
          height: FEDEX_PDF_PAGE_HEIGHT_PT,
        });
      }
      const bytes = await pdfDoc.save();
      return new Blob([bytes], { type: "application/pdf" });
    },
    [base64ToUint8Array]
  );

  const buildFedexPdfFallbackBlob = useCallback(
    async (rows) => {
      const normalizedRows = Array.isArray(rows) ? rows.filter(Boolean) : [];
      if (!normalizedRows.length) {
        throw new Error("No printable FedEx labels are available.");
      }
      const { PDFDocument } = await import("pdf-lib");
      const outputPdf = await PDFDocument.create();
      for (const row of normalizedRows) {
        if (row?.downloadUrl) {
          const pdfBlob = await fetchFedexArtifactBlob(row.downloadUrl, "Unable to fetch FedEx PDF.");
          const sourcePdf = await PDFDocument.load(await pdfBlob.arrayBuffer());
          const copiedPages = await outputPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
          copiedPages.forEach((page) => outputPdf.addPage(page));
          continue;
        }
        if (row?.directPrintUrl) {
          const zplBlob = await fetchFedexArtifactBlob(row.directPrintUrl, "Unable to fetch FedEx ZPL.");
          const zplPdfBlob = await renderFedexZplToPdfBlob(await zplBlob.text());
          const sourcePdf = await PDFDocument.load(await zplPdfBlob.arrayBuffer());
          const copiedPages = await outputPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
          copiedPages.forEach((page) => outputPdf.addPage(page));
          continue;
        }
        throw new Error("No printable FedEx label artifact is available.");
      }
      const bytes = await outputPdf.save();
      return new Blob([bytes], { type: "application/pdf" });
    },
    [fetchFedexArtifactBlob, renderFedexZplToPdfBlob]
  );

  const buildFedexPdfBlobForRow = useCallback(
    async (row) => {
      if (!row) {
        throw new Error("No FedEx label is available.");
      }
      if (row?.downloadUrl) {
        return fetchFedexArtifactBlob(row.downloadUrl, "Unable to download FedEx PDF.");
      }
      if (row?.directPrintUrl) {
        const zplBlob = await fetchFedexArtifactBlob(row.directPrintUrl, "Unable to fetch FedEx ZPL.");
        return renderFedexZplToPdfBlob(await zplBlob.text());
      }
      throw new Error("No PDF fallback is available for this label.");
    },
    [fetchFedexArtifactBlob, renderFedexZplToPdfBlob]
  );

  const handleDownloadFedexPdf = useCallback(
    async (row, fileName) => {
      if (!row) return;
      setError("");
      try {
        const pdfBlob = await buildFedexPdfBlobForRow(row);
        const requestedName = typeof fileName === "string" && fileName.trim()
          ? fileName.trim()
          : row?.trackingNumber
            ? `fedex-label-${row.trackingNumber}.pdf`
            : "fedex-label.pdf";
        saveFedexBlobAsFile(pdfBlob, requestedName);
      } catch (err) {
        console.error(err);
        setError(err?.message || "Unable to download FedEx PDF.");
      }
    },
    [buildFedexPdfBlobForRow, saveFedexBlobAsFile]
  );

  const openFedexPdfBlobPreview = useCallback((pdfBlob, options = {}) => {
    const loadingTitle = typeof options.loadingTitle === "string" && options.loadingTitle.trim()
      ? options.loadingTitle.trim()
      : "Loading FedEx PDF Preview...";
    const previewWindow = options.previewWindow || window.open("", "_blank");
    if (!previewWindow) {
      throw new Error("Allow pop-ups for PortPilot to open the PDF preview.");
    }
    previewWindow.document.title = loadingTitle;
    previewWindow.document.body.innerHTML =
      "<p style=\"font-family: Arial, sans-serif; padding: 24px;\">Loading FedEx PDF preview...</p>";
    const previewUrl = URL.createObjectURL(pdfBlob);
    previewWindow.location.replace(previewUrl);
    window.setTimeout(() => {
      URL.revokeObjectURL(previewUrl);
    }, 60_000);
  }, []);

  const handleOpenFedexPdfPreview = useCallback(
    async (row) => {
      if (!row) return;
      setError("");
      const previewWindow = window.open("", "_blank");
      if (!previewWindow) {
        setError("Allow pop-ups for PortPilot to open the PDF preview.");
        return;
      }
      previewWindow.document.title = "Loading FedEx PDF Preview...";
      previewWindow.document.body.innerHTML =
        "<p style=\"font-family: Arial, sans-serif; padding: 24px;\">Loading FedEx PDF preview...</p>";
      try {
        const pdfBlob = await buildFedexPdfBlobForRow(row);
        openFedexPdfBlobPreview(pdfBlob, { previewWindow });
      } catch (err) {
        console.error(err);
        previewWindow.close();
        setError(err?.message || "Unable to open the FedEx PDF preview.");
      }
    },
    [buildFedexPdfBlobForRow, openFedexPdfBlobPreview]
  );

  const handleDirectPrintFedexLabel = useCallback(
    async (row) => {
      await handleOpenFedexPdfPreview(row);
    },
    [handleOpenFedexPdfPreview]
  );

  const handlePrintAllFedexLabels = useCallback(async () => {
    const succeeded = Array.isArray(fedexSummary?.succeeded)
      ? fedexSummary.succeeded.filter((row) => hasFedexPdfFallback(row))
      : [];
    if (!succeeded.length) return;
    setError("");
    const previewWindow = window.open("", "_blank");
    if (!previewWindow) {
      setError("Allow pop-ups for PortPilot to open the PDF preview.");
      return;
    }
    previewWindow.document.title = "Loading FedEx Print Preview...";
    previewWindow.document.body.innerHTML =
      "<p style=\"font-family: Arial, sans-serif; padding: 24px;\">Loading FedEx PDF preview...</p>";
    try {
      const pdfBlob = await buildFedexPdfFallbackBlob(succeeded);
      openFedexPdfBlobPreview(pdfBlob, { loadingTitle: "Loading FedEx Print Preview...", previewWindow });
    } catch (err) {
      console.error(err);
      previewWindow.close();
      setError(err?.message || "Unable to open the FedEx print preview.");
    }
  }, [buildFedexPdfFallbackBlob, fedexSummary?.succeeded, openFedexPdfBlobPreview]);

  const handleDownloadAllFedexLabels = useCallback(async () => {
    const succeeded = Array.isArray(fedexSummary?.succeeded)
      ? fedexSummary.succeeded.filter((row) => hasFedexPdfFallback(row))
      : [];
    if (!succeeded.length) return;
    setError("");
    try {
      const pdfBlob = await buildFedexPdfFallbackBlob(succeeded);
      const fileName = succeeded.length > 1 ? "fedex-labels-merged.pdf" : "fedex-label.pdf";
      saveFedexBlobAsFile(pdfBlob, fileName);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to download all FedEx PDFs.");
    }
  }, [buildFedexPdfFallbackBlob, fedexSummary?.succeeded, saveFedexBlobAsFile]);

  const handleVoidFedexLabels = useCallback(
    async (labelIds, options = {}) => {
      const normalizedIds = Array.isArray(labelIds)
        ? labelIds.map((value) => String(value || "").trim()).filter(Boolean)
        : [];
      if (!normalizedIds.length) return;
      const confirmationMessage =
        typeof options?.confirmationMessage === "string" && options.confirmationMessage.trim()
          ? options.confirmationMessage
          : `Void ${normalizedIds.length} FedEx ${normalizedIds.length === 1 ? "label" : "labels"}? This cannot be undone.`;
      const confirmed = window.confirm(confirmationMessage);
      if (!confirmed) return;

      setError("");
      if (options?.closeChooser) {
        setFedexChooser(null);
      }
      setVoidingFedexLabelIds(new Set(normalizedIds));
      try {
        const headers = {
          "Content-Type": "application/json",
          ...buildAccessHeaders(me, accessInfo),
        };
        const resp = await fetch(`${API_BASE}/routing-status/fedex-labels/void`, {
          method: "POST",
          headers,
          body: JSON.stringify({ labelIds: normalizedIds }),
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(getFedexLabelErrorMessage(payload?.error, payload?.message) || "Unable to void FedEx labels.");
        }

        if (options?.replaceSummary) {
          setFedexChooser(null);
          setFedexSummary({
            action: payload.action || "void_label",
            attempted: payload.attempted ?? normalizedIds.length,
            successCount: payload.successCount ?? (payload.succeeded?.length || 0),
            failureCount: payload.failureCount ?? (payload.failed?.length || 0),
            succeeded: Array.isArray(payload.succeeded) ? payload.succeeded : [],
            failed: Array.isArray(payload.failed) ? payload.failed : [],
          });
        } else if (!options?.suppressSummary) {
          const successMap = new Map(
            (Array.isArray(payload.succeeded) ? payload.succeeded : []).map((row) => [String(row?.labelId || ""), row])
          );
          setFedexSummary((prev) => {
            if (!prev) return prev;
            const prevSucceeded = Array.isArray(prev.succeeded) ? prev.succeeded : [];
            const mergedSucceeded = prevSucceeded.map((row) => {
              const nextRow = successMap.get(String(row?.labelId || ""));
              return nextRow ? { ...row, ...nextRow } : row;
            });
            const prevFailed = Array.isArray(prev.failed) ? prev.failed : [];
            const nextFailed = Array.isArray(payload.failed) ? payload.failed : [];
            return {
              ...prev,
              action: "void_label",
              successCount: mergedSucceeded.length,
              failureCount: prevFailed.length + nextFailed.length,
              succeeded: mergedSucceeded,
              failed: [...prevFailed, ...nextFailed],
            };
          });
        }

        const failed = Array.isArray(payload.failed) ? payload.failed : [];
        const failedMessage = failed.length
          ? (
              failed
                .map((row) => formatFedexFailureDetail(row, me?.role?.toLowerCase?.() === "admin"))
                .filter(Boolean)
                .join(" | ") || "Some labels could not be voided."
            )
          : "";
        if (failed.length) {
          setError(failedMessage);
        }
        await refreshRoutingDataset({ preserveUndo: true });
        if (options?.reopenDetailsTarget) {
          await openFedexDetails(options.reopenDetailsTarget);
          if (failedMessage) {
            setError(failedMessage);
          }
        }
      } catch (err) {
        console.error(err);
        setError(err?.message || "Unable to void FedEx labels.");
      } finally {
        setVoidingFedexLabelIds(new Set());
      }
    },
    [accessInfo, me, openFedexDetails, refreshRoutingDataset]
  );

  const handleCreateFedexLabelFromDetails = useCallback(async () => {
    const target = buildCurrentFedexDetailsTarget();
    const rowsPayload = buildFedexRowsPayloadFromTarget(target || {});
    if (!target || !rowsPayload.length) {
      setError("Routing context is missing for this FedEx label.");
      return;
    }
    setFedexDetails(null);
    setFedexDetailsLoading(false);
    await startFedexCreateFlow(rowsPayload, {
      suppressSummary: true,
      reopenDetailsTarget: target,
      originDetailsTarget: target,
    });
  }, [buildCurrentFedexDetailsTarget, buildFedexRowsPayloadFromTarget, startFedexCreateFlow]);

  const handleVoidFedexLabelFromDetails = useCallback(async () => {
    const target = buildCurrentFedexDetailsTarget();
    const labelId = String(target?.labelId || "").trim();
    const trackingNumber = String(target?.trackingNumber || "").trim() || "-";
    if (!target || !labelId) {
      setError("FedEx label details are missing the active label identifier.");
      return;
    }
    await handleVoidFedexLabels([labelId], {
      suppressSummary: true,
      reopenDetailsTarget: target,
      confirmationMessage: `Are you sure you want to void this FedEx label?\nTracking #: ${trackingNumber}\nThis action cannot be undone.`,
    });
  }, [buildCurrentFedexDetailsTarget, handleVoidFedexLabels]);

  const handleDeleteSelected = async () => {
    if (!dataset || selectedRows.size === 0) return;
    const count = selectedRows.size;
    const confirmed = window.confirm(
      `Are you sure you want to delete ${count} selected ${count === 1 ? "row" : "rows"}?`
    );
    if (!confirmed) return;

    if (sandbox) {
      setSaving(true);
      try {
        const remaining = dataset.data.filter((row) => !selectedRows.has(row._id));
        const nextDataset = { ...dataset, data: remaining };
        setDataset(nextDataset);
        setSelectedRows(new Set());
        persistDataset(dataStorageKey, nextDataset);
        if (fileMeta) persistMeta(metaStorageKey, fileMeta);
        await persistRemote(nextDataset, fileMeta);
        setError("");
      } catch (err) {
        console.error(err);
        setError(err.message || "Unable to delete selected rows.");
      } finally {
        setSaving(false);
      }
      return;
    }

    const normalize = (value) => (value === undefined || value === null ? "" : String(value).trim());
    const rowsPayload = dataset.data
      .filter((row) => selectedRows.has(row._id))
      .map((row) => ({
        custCode: normalize(row[custHeaderKey]),
        poNumber: normalize(row[poHeaderKey]),
        loadId: normalize(row[loadHeaderKey]),
      }))
      .filter((entry) => entry.custCode && entry.poNumber);

    if (!rowsPayload.length) {
      setError("Selected rows must include both Cust Code and PO Number.");
      return;
    }

    setSaving(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const resp = await fetch(`${API_BASE}/routing-status/delete`, {
        method: "POST",
        headers,
        body: JSON.stringify({ rows: rowsPayload }),
      });
      const payload = await resp.json().catch(() => ({}));
      const serverError = typeof payload?.error === "string" ? payload.error : "";
      if (!resp.ok || payload?.ok !== true) {
        let friendly = "";
        if (serverError === "rows_not_found") {
          friendly = "Selected rows were not found. Refresh and try again.";
        } else if (serverError === "rows_required" || serverError === "rows_invalid") {
          friendly = "Select at least one row that includes Cust Code and PO Number.";
        } else if (serverError === "read_only_role") {
          friendly = "Your role cannot delete Routing Status rows.";
        } else if (serverError && serverError.startsWith("cust_code_not_allowed:")) {
          friendly = "You cannot delete rows outside your Cust Code access.";
        } else if (serverError === "delete_failed" || serverError === "meta_save_failed" || serverError === "refresh_failed") {
          friendly = "Unable to delete selected rows right now. Please try again shortly.";
        }
        throw new Error(friendly || serverError || "Unable to delete selected rows.");
      }
      if (payload.dataset || payload.meta) {
        applyRoutingStatusPayload(payload);
      } else {
        await refreshRoutingDataset();
      }
      setSelectedRows(new Set());
      setError("");

    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to delete selected rows.");
    } finally {
      setSaving(false);
    }
  };

  const performAllocation = useCallback(
    async (pairs) => {
      setAllocating(true);
      try {
        const headers = { "Content-Type": "application/json" };
        if (me?.email) headers["X-User-Email"] = me.email;
        if (me?.role) headers["X-User-Role"] = me.role || "";
        const resp = await fetch(`${API_BASE}/routing/x-pos/allocate`, {
          method: "POST",
          headers,
          body: JSON.stringify({ pairs }),
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || payload?.ok !== true) {
          throw new Error(payload?.error || "Unable to allocate selected rows.");
        }
        setSelectedRows(new Set());
        navigate("/routing/routing-x-pos");
      } catch (err) {
        console.error(err);
        setError(err?.message || "Unable to allocate selected rows.");
      } finally {
        setAllocating(false);
      }
    },
    [me?.email, me?.role, navigate]
  );

  const handleAllocateSelected = useCallback(() => {
    if (!dataset || selectedRows.size === 0) return;
    if (!poHeaderKey || !loadHeaderKey || !custHeaderKey) {
      setError("Routing dataset is missing required columns for allocation.");
      return;
    }
    if (hasOutboundSelected) {
      setError("Selected rows already have Outbound Code and cannot be allocated.");
      return;
    }
    const normalize = (value) => (value === undefined || value === null ? "" : String(value).trim());
    const selectedRecords = dataset.data.filter((row) => selectedRows.has(row._id));
    const pairs = selectedRecords
      .map((row) => ({
        custCode: normalize(row[custHeaderKey]),
        poNumber: normalize(row[poHeaderKey]),
        loadId: normalize(row[loadHeaderKey]),
      }))
      .filter((pair) => pair.custCode && pair.poNumber);
    if (!pairs.length) {
      setError("Selected rows must include both Cust Code and PO Number.");
      return;
    }
    const mismatch = checkAllocationVersionMismatch();
    if (mismatch) {
      pendingAllocationPairsRef.current = pairs;
      setAllocationWarning(mismatch);
      return;
    }
    void performAllocation(pairs);
  }, [
    dataset,
    selectedRows,
    poHeaderKey,
    loadHeaderKey,
    custHeaderKey,
    hasOutboundSelected,
    checkAllocationVersionMismatch,
    performAllocation,
  ]);


  const handleDragging = useCallback((event) => {
    const { col, startX, startW } = dragRef.current;
    if (!col) return;
    const delta = event.clientX - startX;
    const nextWidth = Math.min(MAX_COL_WIDTH, Math.max(MIN_COL_WIDTH, startW + delta));
    setWidths((prev) => {
      if (prev[col] === nextWidth) return prev;
      return { ...prev, [col]: nextWidth };
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    dragRef.current = { col: "", startX: 0, startW: 0 };
    window.removeEventListener("mousemove", handleDragging);
    window.removeEventListener("mouseup", handleDragEnd);
  }, [handleDragging]);

  const onDragStart = (event, key) => {
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      col: key,
      startX: event.clientX,
      startW: widths[key] || DEFAULT_COL_WIDTH,
    };
    window.addEventListener("mousemove", handleDragging);
    window.addEventListener("mouseup", handleDragEnd);
  };

  useEffect(() => {
    return () => handleDragEnd();
  }, [handleDragEnd]);

  const filteredRows = useMemo(() => {
    if (!dataset) return [];
    const scopedRows =
      drilldownScopedRowIds instanceof Set
        ? accessibleRows.filter((row) => drilldownScopedRowIds.has(row?._id))
        : accessibleRows;
    return applyFilters(scopedRows, dataset.headers, filters);
  }, [dataset, accessibleRows, filters, drilldownScopedRowIds]);
  const supersededVisibleCount = useMemo(
    () => filteredRows.filter((row) => isSupersededRoutingRow(row)).length,
    [filteredRows]
  );

  const drilldownRowsForBreakdown = useMemo(() => {
    if (!isMrpShippedDrilldown) return [];
    const scopedRows =
      drilldownScopedRowIds instanceof Set
        ? accessibleRows.filter((row) => drilldownScopedRowIds.has(row?._id))
        : accessibleRows;
    if (drilldownParams?.dateField !== "shipped_date" || !shippedDateHeaderKey) {
      return scopedRows;
    }
    const fromTs = parseShortDate(drilldownParams?.dateFrom);
    const toTs = parseShortDate(drilldownParams?.dateTo);
    return scopedRows.filter((row) => {
      const cellTs = parseShortDate(row?.[shippedDateHeaderKey] ?? row?.[SHIPPED_DATE_KEY]);
      if (fromTs && (!cellTs || cellTs < fromTs)) return false;
      if (toTs && (!cellTs || cellTs > toTs)) return false;
      return true;
    });
  }, [
    isMrpShippedDrilldown,
    accessibleRows,
    drilldownScopedRowIds,
    drilldownParams?.dateField,
    drilldownParams?.dateFrom,
    drilldownParams?.dateTo,
    shippedDateHeaderKey,
  ]);

  const shippedBreakdown = useMemo(() => {
    if (!isMrpShippedDrilldown) return null;
    const itemsByRoutingRowId =
      drilldownXPosPayload?.itemsByRoutingRowId && typeof drilldownXPosPayload.itemsByRoutingRowId === "object"
        ? drilldownXPosPayload.itemsByRoutingRowId
        : {};
    const payloadRows = Array.isArray(drilldownXPosPayload?.dataset?.data) ? drilldownXPosPayload.dataset.data : [];
    const rowByRef = new Map();
    const rowByUid = new Map();
    const routingUidByPayloadRowRef = new Map();

    drilldownRowsForBreakdown.forEach((row) => {
      if (!row || typeof row !== "object") return;
      const rowRef = String(row?.[ROUTING_ROW_ID_KEY] ?? row?.routing_row_id ?? row?.id ?? "").trim();
      const routingUid = String(row?.[ROUTING_UID_KEY] ?? row?.routing_uid ?? "").trim();
      if (rowRef && !rowByRef.has(rowRef)) rowByRef.set(rowRef, row);
      if (routingUid && !rowByUid.has(routingUid)) rowByUid.set(routingUid, row);
    });

    payloadRows.forEach((row) => {
      if (!row || typeof row !== "object") return;
      const rowRef = String(row?.[ROUTING_ROW_ID_KEY] ?? row?.routing_row_id ?? row?.id ?? "").trim();
      const routingUid = String(row?.[ROUTING_UID_KEY] ?? row?.routing_uid ?? "").trim();
      if (rowRef && routingUid && !routingUidByPayloadRowRef.has(rowRef)) {
        routingUidByPayloadRowRef.set(rowRef, routingUid);
      }
    });

    const detailRows = [];
    const routingRowIdSet = new Set();
    const poLineSet = new Set();
    const loadIdSet = new Set();
    let incompleteDetailCount = 0;
    const skuFilterUpper = String(drilldownParams?.walmartItemNo || "").trim().toUpperCase();
    drilldownRowsForBreakdown.forEach((row) => {
      const routingLocalId = String(row?._id || "").trim();
      if (routingLocalId) routingRowIdSet.add(routingLocalId);
      const poValue = String(row?.[poHeaderKey] ?? row?.["po-number"] ?? row?.po_number ?? "").trim();
      if (poValue) poLineSet.add(normaliseTextValue(poValue));
      // Count distinct non-blank load ids only.
      const loadValue = String(row?.[loadHeaderKey] ?? row?.["load-id"] ?? row?.load_id ?? "").trim();
      if (loadValue) loadIdSet.add(normaliseTextValue(loadValue));
    });
    Object.entries(itemsByRoutingRowId).forEach(([rowRefRaw, itemsRaw]) => {
      const rowRef = String(rowRefRaw || "").trim();
      if (!rowRef) return;
      let routingRow = rowByRef.get(rowRef) || null;
      if (!routingRow) {
        const linkedUid = routingUidByPayloadRowRef.get(rowRef);
        routingRow = linkedUid ? rowByUid.get(linkedUid) || null : null;
      }
      if (!routingRow) return;

      const items = Array.isArray(itemsRaw) ? itemsRaw : [];
      const outboundCode = String(
        routingRow?.[outboundHeaderKey] ?? routingRow?.[OUTBOUND_CODE_COLUMN.key] ?? routingRow?.outbound_code ?? ""
      ).trim();
      const shippedDateText = String(routingRow?.[shippedDateHeaderKey] ?? routingRow?.[SHIPPED_DATE_KEY] ?? "").trim();
      const routingPoNumber = String(
        routingRow?.[poHeaderKey] ?? routingRow?.["po-number"] ?? routingRow?.po_number ?? ""
      ).trim();

      items.forEach((item, itemIndex) => {
        const rawSku = String(item?.sku ?? item?.walmart_item_no ?? "").trim();
        if (skuFilterUpper && rawSku.toUpperCase() !== skuFilterUpper) return;
        const poNumber = String(item?.poNo ?? item?.po_no ?? item?.poNumber ?? item?.po ?? "").trim() || routingPoNumber;
        const allocText = String(item?.allocQty ?? "").replace(/,/g, "").trim();
        const allocNumeric = allocText === "" ? NaN : Number(allocText);
        const hasInvalidAlloc = allocText === "" || !Number.isFinite(allocNumeric) || allocNumeric < 0;
        if (!rawSku || !poNumber || hasInvalidAlloc) {
          incompleteDetailCount += 1;
        }
        if (!rawSku) return;
        const allocQty = toFiniteNumber(item?.allocQty);
        if (!allocQty) return;
        detailRows.push({
          rowKey: `${rowRef}-${itemIndex}-${rawSku}-${poNumber || "po"}`,
          sku: rawSku,
          poNumber,
          outboundCode,
          shippedDate: shippedDateText,
          allocQty,
          routingRowRef: rowRef,
        });
      });
    });

    const totalAllocQty = detailRows.reduce((sum, row) => sum + toFiniteNumber(row.allocQty), 0);
    const mrpCellValue =
      drilldownParams && typeof drilldownParams.mrpCellValue === "number" && Number.isFinite(drilldownParams.mrpCellValue)
        ? drilldownParams.mrpCellValue
        : null;
    return {
      detailRows,
      totalAllocQty,
      routingRowCount: routingRowIdSet.size,
      itemRowCount: detailRows.length,
      poLineCount: poLineSet.size,
      loadIdCount: loadIdSet.size,
      incompleteDetailCount,
      mrpCellValue,
    };
  }, [
    isMrpShippedDrilldown,
    drilldownXPosPayload,
    drilldownRowsForBreakdown,
    drilldownParams,
    poHeaderKey,
    loadHeaderKey,
    outboundHeaderKey,
    shippedDateHeaderKey,
  ]);

  const breakdownSearchTerm = useMemo(() => normaliseTextValue(breakdownSearch), [breakdownSearch]);
  const breakdownDetailRows = useMemo(() => {
    const rows = Array.isArray(shippedBreakdown?.detailRows) ? shippedBreakdown.detailRows : [];
    if (!breakdownSearchTerm) return rows;
    return rows.filter((row) => {
      const haystack = [
        row?.sku,
        row?.poNumber,
        row?.outboundCode,
        row?.shippedDate,
      ]
        .map((value) => normaliseTextValue(value))
        .join(" ");
      return haystack.includes(breakdownSearchTerm);
    });
  }, [shippedBreakdown?.detailRows, breakdownSearchTerm]);

  const breakdownSkuRows = useMemo(() => {
    const bucket = new Map();
    breakdownDetailRows.forEach((row) => {
      const sku = String(row?.sku || "").trim();
      if (!sku) return;
      bucket.set(sku, (bucket.get(sku) || 0) + toFiniteNumber(row?.allocQty));
    });
    return Array.from(bucket.entries())
      .map(([sku, totalAllocQty]) => ({ sku, totalAllocQty }))
      .sort((a, b) => b.totalAllocQty - a.totalAllocQty || a.sku.localeCompare(b.sku));
  }, [breakdownDetailRows]);

  const breakdownTotalMismatch = useMemo(() => {
    if (!isMrpShippedDrilldown || !shippedBreakdown || shippedBreakdown.mrpCellValue === null) return false;
    if (!drilldownParams?.cellValueProvided) return false;
    return Math.abs(toFiniteNumber(shippedBreakdown.totalAllocQty) - toFiniteNumber(shippedBreakdown.mrpCellValue)) > 0.000001;
  }, [isMrpShippedDrilldown, shippedBreakdown, drilldownParams?.cellValueProvided]);

  const breakdownMissingAllocDetails = useMemo(() => {
    if (!isMrpShippedDrilldown || !shippedBreakdown) return false;
    const routingRows = shippedBreakdown.routingRowCount || 0;
    const itemRows = shippedBreakdown.itemRowCount || 0;
    const allocTotal = toFiniteNumber(shippedBreakdown.totalAllocQty);
    return routingRows > 0 && (itemRows === 0 || allocTotal === 0);
  }, [isMrpShippedDrilldown, shippedBreakdown]);

  const breakdownHasIncompleteDetails = useMemo(() => {
    if (!isMrpShippedDrilldown || !shippedBreakdown) return false;
    return (shippedBreakdown.incompleteDetailCount || 0) > 0;
  }, [isMrpShippedDrilldown, shippedBreakdown]);

  const hasInvalidDrilldownWeekRange = useMemo(() => {
    if (!isMrpShippedDrilldown) return false;
    if (!drilldownParams?.hasDateRangeInputs) return false;
    if (drilldownParams?.hasInvalidDateFormat) return true;
    return !isValidWalmartWeekRange(drilldownParams?.dateFrom, drilldownParams?.dateTo);
  }, [
    isMrpShippedDrilldown,
    drilldownParams?.hasDateRangeInputs,
    drilldownParams?.hasInvalidDateFormat,
    drilldownParams?.dateFrom,
    drilldownParams?.dateTo,
  ]);

  const breakdownWarning = useMemo(() => {
    if (!isMrpShippedDrilldown) return null;
    if (breakdownTotalMismatch) {
      return {
        tone: "amber",
        title: "Drill-down totals do not match",
        body: "MRP cell value and allocation breakdown total are different. Please verify date range semantics and filters.",
      };
    }
    if (breakdownMissingAllocDetails) {
      return {
        tone: "amber",
        title: "Allocation details not found",
        body: "Routing rows exist for this shipped date range, but no allocation items were found. This may indicate missing linkage or incomplete backfill.",
      };
    }
    if (breakdownHasIncompleteDetails) {
      return {
        tone: "amber",
        title: "Some allocation details are incomplete",
        body: "Some rows are missing SKU/PO or have invalid quantities. Totals may be understated.",
      };
    }
    if (hasInvalidDrilldownWeekRange) {
      return {
        tone: "amber",
        title: "Invalid drill-down date range",
        body: "The provided date range is not a valid Walmart week (Saturday-Friday). Please re-open from MRP.",
      };
    }
    return null;
  }, [
    isMrpShippedDrilldown,
    breakdownTotalMismatch,
    breakdownMissingAllocDetails,
    breakdownHasIncompleteDetails,
    hasInvalidDrilldownWeekRange,
  ]);

  const poFilterTokens = useMemo(() => {
    if (!poHeaderKey) return [];
    return parsePoFilterTokens(filters?.[poHeaderKey] || "");
  }, [filters, poHeaderKey]);

  const poFilterTokenKey = useMemo(
    () => poFilterTokens.map((token) => token.normalized).join("|"),
    [poFilterTokens]
  );

  useEffect(() => {
    setMissingPoBannerDismissed(false);
  }, [poFilterTokenKey]);

  const missingPoNumbers = useMemo(() => {
    if (!poHeaderKey || poFilterTokens.length < 2) return [];
    const found = new Set();
    filteredRows.forEach((row) => {
      const value = String(row?.[poHeaderKey] ?? "").trim().toLowerCase();
      if (value) found.add(value);
    });
    return poFilterTokens
      .filter((token) => !found.has(token.normalized))
      .map((token) => token.raw);
  }, [filteredRows, poFilterTokens, poHeaderKey]);

  const showMissingPoBanner =
    poFilterTokens.length >= 2 && missingPoNumbers.length > 0 && !missingPoBannerDismissed;

  const sortedRows = useMemo(() => {
    if (!dataset) return [];
    return applySorting(filteredRows, dataset.headers, sortState);
  }, [dataset, filteredRows, sortState]);

  const handleExport = useCallback(() => {
    const headersForExport =
      ((visibleHeaders.length ? visibleHeaders : dataset?.headers) || []).filter(header => header.key !== EDI_STATUS_KEY);
    if (!headersForExport.length || !sortedRows.length) return;
    const headerLabels = headersForExport.map((header) => header.label);
    const rows = sortedRows.map((record) =>
      headersForExport.map((header) => normaliseValueForExport(record[header.key], header.type))
    );
    const worksheet = XLSX.utils.aoa_to_sheet([headerLabels, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, exportSheetName);
    const timestamp = formatTimestampForFilename(new Date());
    const filename = `${exportFilePrefix}-${timestamp}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }, [dataset?.headers, visibleHeaders, sortedRows, exportSheetName, exportFilePrefix]);

  useEffect(() => {
    setPage(1);
  }, [filters, sortState.key, sortState.direction, dataset]);

  const totalPages = useMemo(() => {
    if (!sortedRows.length) return 1;
    if (pageSize === "All") return 1;
    return Math.max(1, Math.ceil(sortedRows.length / pageSize));
  }, [sortedRows, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagedRows = useMemo(() => paginate(sortedRows, page, pageSize), [sortedRows, page, pageSize]);
  const ediIdentities = pagedRows.map(row => ({id: row[ROUTING_ROW_ID_KEY], identity: {
    cust_code: String(row[custHeaderKey] || '').trim().toUpperCase(),
    po_number: String(row[poHeaderKey] ?? '').trim(), load_id: String(row[loadHeaderKey] ?? '').trim(),
    outbound_code: String(row[outboundHeaderKey] ?? '').trim(),
  }}));
  const ediForRow = useRoutingEdiSummary(ediIdentities, dataset, API_BASE,
    buildAccessHeaders(me, accessInfo, {includeWhs: false}), !sandbox && !loadingRemote);
  const startIdx = pageSize === "All" ? 0 : (page - 1) * pageSize;
  const endIdx = pageSize === "All" ? sortedRows.length : Math.min(sortedRows.length, startIdx + pageSize);

  const selectableVisibleRowIds = useMemo(
    () =>
      filteredRows
        .filter((row) => !row?.[ON_HOLD_KEY] && !isSupersededRoutingRow(row))
        .map((row) => row._id),
    [filteredRows]
  );
  const allVisibleSelected = useMemo(
    () => selectableVisibleRowIds.length > 0 && selectableVisibleRowIds.every((id) => selectedRows.has(id)),
    [selectableVisibleRowIds, selectedRows]
  );
  const someVisibleSelected = useMemo(
    () => selectableVisibleRowIds.some((id) => selectedRows.has(id)),
    [selectableVisibleRowIds, selectedRows]
  );

  useEffect(() => {
    if (!headerCheckboxRef.current) return;
    headerCheckboxRef.current.indeterminate = !allVisibleSelected && someVisibleSelected;
  }, [allVisibleSelected, someVisibleSelected]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const setRangeFilter = (key, range) => {
    setFilters((prev) => ({
      ...prev,
      [key]: {
        from: range.from || "",
        to: range.to || "",
      },
    }));
  };

  const resetColumnWidths = useCallback(() => {
    setWidths(() => {
      if (!dataset?.headers) return {};
      const defaults = {};
      tableHeaders.forEach((header) => {
        defaults[header.key] = Math.min(
          MAX_COL_WIDTH,
          Math.max(MIN_COL_WIDTH, ((header.label?.length || 10) + 6) * 8)
        );
      });
      defaults[SELECT_COLUMN_KEY] = SELECT_COLUMN_DEFAULT_WIDTH;
      return defaults;
    });
    clearStoredWidths(widthsStorageKey);
  }, [dataset?.headers, tableHeaders, widthsStorageKey]);

  const toggleRowSelection = (id) => {
    const targetRow = dataset?.data?.find((row) => row._id === id);
    if (isSupersededRoutingRow(targetRow)) {
      setError("Superseded rows are historical only and cannot be selected for active Routing Status actions.");
      return;
    }
    if (targetRow?.[ON_HOLD_KEY]) {
      setError("Rows On Hold cannot be selected for PO Allocate.");
      return;
    }
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        selectableVisibleRowIds.forEach((id) => next.delete(id));
      } else {
        selectableVisibleRowIds.forEach((id) => {
          const row = dataset?.data?.find((entry) => entry._id === id);
          if (row && !row[ON_HOLD_KEY]) {
            next.add(id);
          }
        });
      }
      return next;
    });
  };

  const handleResetFilters = useCallback(() => {
    if (!dataset?.headers) return;
    setFilters(defaultFiltersFor(dataset.headers));
    setSelectedRows(new Set());
  }, [dataset?.headers]);

  const hasActiveFilters = useMemo(() => {
    if (!dataset?.headers) return false;
    return dataset.headers.some((header) => {
      const mode = getFilterModeForHeader(header);
      const value = filters[header.key];
      if (mode === FILTER_MODES.date) {
        return Boolean(value?.from || value?.to);
      }
      if (mode === FILTER_MODES.multiSelect || mode === FILTER_MODES.fedexStatus) {
        return Array.isArray(value) && value.length > 0;
      }
      return Boolean(String(value || "").trim());
    });
  }, [dataset?.headers, filters]);

  const toggleSort = (key) => {
    setSortState((prev) => toggleSortState(prev, key));
  };

  const selectColumnWidth = widths[SELECT_COLUMN_KEY] || SELECT_COLUMN_DEFAULT_WIDTH;
  const stickyOffsets = useMemo(() => {
    if (!visibleHeaders.length) return {};
    let offset = selectColumnWidth;
    const offsets = {};
    visibleHeaders.forEach((header) => {
      const canonical = canonicalizeHeaderLabel(header.label);
      const width = widths[header.key] || DEFAULT_COL_WIDTH;
      if (STICKY_HEADER_SET.has(canonical)) {
        offsets[header.key] = offset;
      }
      offset += width;
    });
    return offsets;
  }, [visibleHeaders, widths, selectColumnWidth]);
  const warningTitle =
    versionWarning?.type === "skip"
      ? "You are attempting to import a file whose version skips ahead of the current one!"
      : "You are attempting to import an older version than the current file!";
  const warningCurrentLabel = versionWarning ? formatVersionDateLabel(versionWarning.currentDate) : "";
  const warningUpdateLabel = versionWarning ? formatVersionDateLabel(versionWarning.updateDate) : "";
  const allocationWarningTitle = "Routing Status and PO Details versions do not match!";
  const allocationWarningMessage =
    allocationWarning?.diff && allocationWarning.diff > 0
      ? "Routing Status data is newer than PO Details, so PO Allocate may produce inconsistent results."
      : "PO Details data is newer than Routing Status, so PO Allocate may skip the latest PO updates.";
  const allocationRoutingLabel = allocationWarning ? formatVersionDateLabel(allocationWarning.routingDate) : "";
  const allocationPoLabel = allocationWarning ? formatVersionDateLabel(allocationWarning.poDate) : "";
  const currentFedexDetailsTarget = buildCurrentFedexDetailsTarget();
  const currentFedexDetailsRowsPayload = buildFedexRowsPayloadFromTarget(currentFedexDetailsTarget || {});
  const currentFedexDetailsLifecycle = fedexDetails?.lifecycle || {};
  const canVoidFedexLabelFromDetails =
    canManageFedexLabels &&
    String(currentFedexDetailsLifecycle?.labelStatus || "").trim().toUpperCase() === "CREATED" &&
    Boolean(currentFedexDetailsLifecycle?.isActive) &&
    !String(currentFedexDetailsLifecycle?.voidedAt || "").trim() &&
    !String(currentFedexDetailsLifecycle?.supersededAt || "").trim() &&
    Boolean(String(currentFedexDetailsLifecycle?.labelId || "").trim());
  const canCreateFedexLabelFromDetails =
    canManageFedexLabels &&
    currentFedexDetailsRowsPayload.length > 0 &&
    !creatingFedexLabels;

  return (
    <>
      {allocationWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCancelAllocationWarning} />
          <div className="relative z-50 w-full max-w-lg rounded-2xl border-2 border-amber-300 bg-white p-6 shadow-2xl">
            <div className="flex items-center gap-3 rounded-xl bg-amber-50 px-4 py-3">
              <AlertTriangle className="h-6 w-6 flex-shrink-0 text-amber-500" />
              <div>
                <h2 className="text-lg font-semibold text-amber-900">{allocationWarningTitle}</h2>
                <p className="text-sm text-amber-800">{allocationWarningMessage}</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-700">
              Review both dataset versions before continuing with PO Allocate.
            </p>
            <div className="mt-4 space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-slate-800">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-amber-700">Routing Status Version:</span>
                <span>{allocationRoutingLabel || "Unknown"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-amber-700">PO Details Version:</span>
                <span>{allocationPoLabel || "Unknown"}</span>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={handleCancelAllocationWarning}
              >
                Abort
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                onClick={handleConfirmAllocationWarning}
                disabled={allocating}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
      {versionWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCancelVersionWarning} />
          <div className="relative z-50 w-full max-w-lg rounded-2xl border-2 border-amber-300 bg-white p-6 shadow-2xl">
            <div className="flex items-center gap-3 rounded-xl bg-amber-50 px-4 py-3">
              <AlertTriangle className="h-6 w-6 flex-shrink-0 text-amber-500" />
              <div>
                <h2 className="text-lg font-semibold text-amber-900">{warningTitle}</h2>
                <p className="text-sm text-amber-800">This action may overwrite the latest Routing Status dataset.</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-700">Review the version dates below before continuing.</p>
            <div className="mt-4 space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-slate-800">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-amber-700">Current Version:</span>
                <span>{warningCurrentLabel || "Unknown"}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-amber-700">Update Version:</span>
                <span>{warningUpdateLabel || "Unknown"}</span>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                onClick={handleCancelVersionWarning}
              >
                Abort
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                onClick={handleConfirmVersionWarning}
                disabled={saving}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
      {importDialogOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCancelImportDialog} />
          <div className="relative z-50 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-900">Select Customer Code</h2>
            <p className="mt-1 text-sm text-slate-600">Which Customer Code do you want to import?</p>
            <div className="mt-4">
              <label htmlFor="routing-import-cust" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                Cust Code
              </label>
              <select
                id="routing-import-cust"
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={selectedCustCode}
                onChange={(event) => setSelectedCustCode(event.target.value)}
              >
                {accessibleCustOptions.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
                onClick={handleCancelImportDialog}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                onClick={handleConfirmImportDialog}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {importCorrectionConflict && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={saving ? undefined : handleCancelCorrectionImportConflict} />
          <div className="relative z-50 w-full max-w-3xl rounded-2xl border-2 border-amber-300 bg-white p-6 shadow-2xl">
            <div className="flex items-start gap-3 rounded-xl bg-amber-50 px-4 py-3">
              <AlertTriangle className="mt-0.5 h-6 w-6 flex-shrink-0 text-amber-500" />
              <div>
                <h2 className="text-lg font-semibold text-amber-900">Cases Discrepancy Detected</h2>
                <p className="mt-1 text-sm text-amber-800">
                  Some rows have a cases mismatch between the imported Routing Status and the current protected routing state.
                  For correction-protected rows, the current cases must be preserved and cannot be overwritten by import data.
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm font-semibold text-slate-800">Cases on protected rows cannot be changed by import.</p>
            <p className="mt-1 text-sm text-slate-600">
              Mismatched protected rows: {importCorrectionConflict.payload?.mismatchedProtectedRowCount ?? importCorrectionConflict.payload?.casesMismatches?.length ?? 0}
            </p>
            {Array.isArray(importCorrectionConflict.payload?.casesMismatches) &&
              importCorrectionConflict.payload.casesMismatches.length > 0 && (
                <div className="mt-4 max-h-56 overflow-auto rounded border border-amber-200">
                  <table className="min-w-full text-xs">
                    <thead className="bg-amber-50 text-amber-800">
                      <tr>
                        <th className="px-2 py-1 text-left">PO Number</th>
                        <th className="px-2 py-1 text-left">Outbound</th>
                        <th className="px-2 py-1 text-left">Load ID</th>
                        <th className="px-2 py-1 text-right">Current Cases</th>
                        <th className="px-2 py-1 text-right">Imported Cases</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importCorrectionConflict.payload.casesMismatches.slice(0, 8).map((row, index) => (
                        <tr key={`${row.routingUid || row.outboundCode || "row"}-${index}`} className="odd:bg-white even:bg-amber-50/60">
                          <td className="px-2 py-1 text-slate-700">{row.poNumber || "-"}</td>
                          <td className="px-2 py-1 font-semibold text-slate-800">{row.outboundCode || "-"}</td>
                          <td className="px-2 py-1 text-slate-700">{row.loadId || "-"}</td>
                          <td className="px-2 py-1 text-right text-slate-700">{row.currentCases || "-"}</td>
                          <td className="px-2 py-1 text-right text-amber-800">{row.importedCases || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importCorrectionConflict.payload.casesMismatches.length > 8 && (
                    <p className="border-t border-amber-100 px-2 py-1 text-xs text-slate-500">
                      +{importCorrectionConflict.payload.casesMismatches.length - 8} more row(s)
                    </p>
                  )}
                </div>
              )}
            <div className="mt-6 grid gap-3 md:grid-cols-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-4 py-3 text-left text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                onClick={() => continueCorrectionImportWithMode(CORRECTION_IMPORT_MODE_SKIP_MISMATCHED_CASES)}
                disabled={saving}
              >
                Update only rows without a cases mismatch
                <span className="mt-1 block text-xs font-normal text-slate-500">Mismatched protected rows stay unchanged.</span>
              </button>
              <button
                type="button"
                className="rounded-lg bg-blue-600 px-4 py-3 text-left text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                onClick={() => continueCorrectionImportWithMode(CORRECTION_IMPORT_MODE_PRESERVE_CURRENT_CASES)}
                disabled={saving}
              >
                Update all rows, but keep the current cases for mismatched rows
                <span className="mt-1 block text-xs font-normal text-blue-100">
                  Imported stale cases are ignored only on protected mismatched rows.
                </span>
              </button>
            </div>
            <div className="mt-4 flex justify-between gap-3">
              <button
                type="button"
                className="rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                onClick={handleCancelCorrectionImportConflict}
                disabled={saving}
              >
                Abort Import
              </button>
              {saving && <span className="self-center text-sm text-slate-500">Saving selected import strategy...</span>}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={updateIlsConfirmOpen}
        title="Update iLS"
        message={`Are you sure you want to update ${ilsEligibleRows.length} outbound${
          ilsEligibleRows.length === 1 ? "" : "s"
        } to iLS?`}
        confirmText="Confirm"
        cancelText="Cancel"
        loading={updatingIls}
        disableConfirm={!canUpdateIls || updatingIls}
        onConfirm={handleConfirmUpdateIls}
        onCancel={handleCancelUpdateIls}
      />

      <LegacyIlsUpdateDialog
        preview={legacyIlsPreview}
        loading={legacyIlsLoading}
        onCancel={() => {
          if (!legacyIlsLoading) setLegacyIlsPreview(null);
        }}
        onExecute={handleExecuteLegacyIlsUpdate}
      />

      <LegacyIlsPendingVerification
        attempt={legacyIlsPendingAttempt}
        loading={legacyIlsLoading}
        onVerified={() => handleLegacyVerification("VERIFIED")}
        onMismatch={(note) => handleLegacyVerification("MISMATCH_FOUND", note)}
      />

      {updateIlsSummary && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCloseUpdateIlsSummary} />
          <div className="relative z-50 w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">iLS Update Summary</h2>
                <p className="text-sm text-slate-500">
                  Attempted {updateIlsSummary.attempted ?? 0} • Succeeded {updateIlsSummary.successCount ?? 0} • Failed{" "}
                  {updateIlsSummary.failureCount ?? 0}
                </p>
              </div>
              <button
                type="button"
                className="mt-2 rounded border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 md:mt-0"
                onClick={handleCloseUpdateIlsSummary}
              >
                Close
              </button>
            </div>
            <div className="mt-4 grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="text-sm font-semibold text-emerald-700">Succeeded</h3>
                {updateIlsSummary.succeeded?.length ? (
                  <div className="mt-2 max-h-72 overflow-auto rounded border border-emerald-200">
                    <table className="min-w-full text-xs">
                      <thead className="bg-emerald-50 text-emerald-700">
                        <tr>
                          <th className="px-2 py-1 text-left">Outbound</th>
                          <th className="px-2 py-1 text-left">PO Number</th>
                          <th className="px-2 py-1 text-left">Load ID</th>
                          <th className="px-2 py-1 text-left">Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {updateIlsSummary.succeeded.map((row, index) => (
                          <tr key={`success-${index}`} className="odd:bg-white even:bg-emerald-50/60">
                            <td className="px-2 py-1 font-semibold text-emerald-800">{row.outboundCode || "-"}</td>
                            <td className="px-2 py-1 text-slate-700">{row.poNumber || "-"}</td>
                            <td className="px-2 py-1 text-slate-700">{row.loadId || "-"}</td>
                            <td className="px-2 py-1 text-slate-600">{row.message || "Updated"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">No rows were updated.</p>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-rose-700">Failed</h3>
                {updateIlsSummary.failed?.length ? (
                  <div className="mt-2 max-h-72 overflow-auto rounded border border-rose-200">
                    <table className="min-w-full text-xs">
                      <thead className="bg-rose-50 text-rose-700">
                        <tr>
                          <th className="px-2 py-1 text-left">Outbound</th>
                          <th className="px-2 py-1 text-left">PO Number</th>
                          <th className="px-2 py-1 text-left">Load ID</th>
                          <th className="px-2 py-1 text-left">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {updateIlsSummary.failed.map((row, index) => (
                          <tr key={`failed-${index}`} className="odd:bg-white even:bg-rose-50/60">
                            <td className="px-2 py-1 font-semibold text-rose-900">{row.outboundCode || "-"}</td>
                            <td className="px-2 py-1 text-slate-700">{row.poNumber || "-"}</td>
                            <td className="px-2 py-1 text-slate-700">{row.loadId || "-"}</td>
                            <td className="px-2 py-1 text-rose-600">{row.error || row.message || "Failed"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">No failures.</p>
                )}
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                onClick={handleCloseUpdateIlsSummary}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {fedexChooser && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCloseFedexChooser} />
          <div className="relative z-50 w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Existing FedEx Labels Found</h2>
                <p className="text-sm text-slate-500">
                  {fedexChooser.existingCount ?? 0} selected row(s) already have an active label.
                  {(fedexChooser.noExistingCount ?? 0) > 0 ? ` ${fedexChooser.noExistingCount} row(s) do not.` : ""}
                </p>
                <p className="text-xs text-slate-400">
                  Reuse the existing label when possible. FedEx label requests must choose one output format, so PortPilot now keeps 4x6 PDF as the primary browser print flow.
                </p>
              </div>
              <button
                type="button"
                className="mt-2 rounded border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 md:mt-0"
                onClick={handleCloseFedexChooser}
              >
                Cancel
              </button>
            </div>
            <div className="mt-4 max-h-72 overflow-auto rounded border border-slate-200">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-700">
                  <tr>
                    <th className="px-2 py-1 text-left">BOL</th>
                    <th className="px-2 py-1 text-left">PO Number</th>
                    <th className="px-2 py-1 text-left">Tracking</th>
                    <th className="px-2 py-1 text-left">Output</th>
                    <th className="px-2 py-1 text-left">Status</th>
                    <th className="px-2 py-1 text-left">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {fedexChooser.rows?.map((row, index) => (
                    <tr key={`fedex-chooser-${index}`} className="odd:bg-white even:bg-slate-50/60">
                      <td className="px-2 py-1 font-semibold text-slate-800">{row.bolNumber || "-"}</td>
                      <td className="px-2 py-1 text-slate-700">{row.poNumber || "-"}</td>
                      <td className="px-2 py-1 text-slate-700">{row.activeLabel?.trackingNumber || "-"}</td>
                      <td className="px-2 py-1 text-slate-700">
                        {row.activeLabel?.labelImageType === "PDF" ? "FedEx PDF -> 4x6 PDF" : row.activeLabel?.supportsDirectPrint ? "Legacy ZPL" : "-"}
                      </td>
                      <td className="px-2 py-1 text-slate-700">
                        {formatFedexLabelStatus(row.activeLabel?.labelStatus || (row.hasActiveLabel ? "active" : ""))}
                      </td>
                      <td className="px-2 py-1 text-slate-700">{row.activeLabel?.createdAt || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                onClick={() =>
                  executeFedexLabelAction("reprint_existing", fedexChooser.rowsPayload || [], {
                    suppressSummary: Boolean(fedexChooser?.originDetailsTarget),
                    reopenDetailsTarget: fedexChooser?.originDetailsTarget || null,
                    originDetailsTarget: fedexChooser?.originDetailsTarget || null,
                  })
                }
                disabled={creatingFedexLabels || voidingFedexLabelIds.size > 0}
              >
                {creatingFedexLabels ? "Working..." : "Use Existing Label"}
              </button>
              <button
                type="button"
                className="rounded border border-rose-300 px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                onClick={() =>
                    handleVoidFedexLabels(
                      (fedexChooser.rows || [])
                        .map((row) => row?.activeLabel?.labelId)
                        .filter(Boolean),
                    fedexChooser?.originDetailsTarget
                      ? {
                          closeChooser: true,
                          suppressSummary: true,
                          reopenDetailsTarget: fedexChooser.originDetailsTarget,
                          confirmationMessage: `Are you sure you want to void this FedEx label?\nTracking #: ${
                            String((fedexChooser.rows || [])[0]?.activeLabel?.trackingNumber || "").trim() || "-"
                          }\nThis action cannot be undone.`,
                        }
                      : { replaceSummary: true }
                  )
                }
                disabled={
                  creatingFedexLabels ||
                  voidingFedexLabelIds.size > 0 ||
                  !(fedexChooser.rows || []).some((row) => row?.activeLabel?.labelId)
                }
              >
                {voidingFedexLabelIds.size > 0 ? "Voiding..." : "Void Existing"}
              </button>
              <button
                type="button"
                className="rounded bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-60"
                onClick={() =>
                  executeFedexLabelAction("create_new", fedexChooser.rowsPayload || [], {
                    suppressSummary: Boolean(fedexChooser?.originDetailsTarget),
                    reopenDetailsTarget: fedexChooser?.originDetailsTarget || null,
                    originDetailsTarget: fedexChooser?.originDetailsTarget || null,
                  })
                }
                disabled={creatingFedexLabels || voidingFedexLabelIds.size > 0}
              >
                {creatingFedexLabels ? "Working..." : "Create New Label"}
              </button>
            </div>
          </div>
        </div>
      )}

      {fedexSummary && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCloseFedexSummary} />
          <div className="relative z-50 w-full max-w-6xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">FedEx Label Summary</h2>
                <p className="text-sm text-slate-500">
                  Attempted {fedexSummary.attempted ?? 0} | Succeeded {fedexSummary.successCount ?? 0} | Failed{" "}
                  {fedexSummary.failureCount ?? 0}
                </p>
                <p className="text-xs text-slate-400">
                  PortPilot requests the verified FedEx PDF source label, then rewrites it into a vector-preserving 4x6 PDF for browser preview and browser print. Download PDF remains available for archive, review, email, or office printing.
                </p>
              </div>
              <div className="mt-2 flex flex-wrap gap-2 md:mt-0">
                <button
                  type="button"
                  className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
                  onClick={handleCloseFedexSummary}
                >
                  Close
                </button>
              </div>
            </div>
            <div className="mt-4 space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-emerald-700">Succeeded</h3>
                {fedexSummary.succeeded?.length ? (
                  <div className="mt-2 max-h-72 overflow-auto rounded border border-emerald-200">
                    <table className="min-w-full text-xs">
                      <thead className="bg-emerald-50 text-emerald-700">
                        <tr>
                          <th className="px-2 py-1 text-left">BOL</th>
                          <th className="px-2 py-1 text-left">PO Number</th>
                          <th className="px-2 py-1 text-left">Tracking</th>
                          <th className="px-2 py-1 text-left">Output</th>
                          <th className="px-2 py-1 text-left">Status</th>
                          <th className="w-[360px] px-2 py-1 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fedexSummary.succeeded.map((row, index) => (
                          <tr key={`fedex-success-${index}`} className="odd:bg-white even:bg-emerald-50/60">
                            <td className="px-2 py-1 font-semibold text-emerald-800">{row.bolNumber || "-"}</td>
                            <td className="px-2 py-1 text-slate-700">{row.poNumber || "-"}</td>
                            <td className="px-2 py-1 text-slate-700">{row.trackingNumber || "-"}</td>
                            <td className="px-2 py-1 text-slate-700">
                              {row?.labelImageType === "PDF" ? "FedEx PDF -> 4x6 PDF" : row?.supportsDirectPrint ? "Legacy ZPL" : "-"}
                            </td>
                            <td className="px-2 py-1 text-slate-700">{formatFedexLabelStatus(row.labelStatus || "active")}</td>
                            <td className="min-w-[440px] px-2 py-1 text-slate-700">
                              <div className="flex flex-nowrap items-center gap-2 overflow-x-auto whitespace-nowrap">
                                {hasFedexPdfFallback(row) ? (
                                  <button
                                    type="button"
                                    onClick={() => handleDirectPrintFedexLabel(row)}
                                    className="rounded border border-orange-600 bg-orange-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-orange-700"
                                  >
                                    PDF Preview
                                  </button>
                                ) : null}
                                {hasFedexPdfFallback(row) ? (
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadFedexPdf(row)}
                                    className="rounded border border-sky-300 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 transition hover:bg-sky-100"
                                  >
                                    Download PDF
                                  </button>
                                ) : null}
                                {row.isVoidable && row.labelId ? (
                                  <button
                                    type="button"
                                    onClick={() => handleVoidFedexLabels([row.labelId])}
                                    className="rounded border border-rose-300 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60"
                                    disabled={voidingFedexLabelIds.has(String(row.labelId))}
                                  >
                                    {voidingFedexLabelIds.has(String(row.labelId)) ? "Voiding..." : "Void Label"}
                                  </button>
                                ) : null}
                                {row?.labelImageType === "PDF" && row?.labelStatus === "active" ? (
                                  <span className="text-slate-500">Browser print flow</span>
                                ) : null}
                                {!hasFedexPdfFallback(row) && !row.isVoidable ? (
                                  <span className="text-slate-500">
                                    {row.labelStatus === "voided"
                                      ? "Not printable after void"
                                      : row.labelStatus === "superseded"
                                        ? "Superseded"
                                        : "Not printable"}
                                  </span>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">No labels were created.</p>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-rose-700">Failed</h3>
                {fedexSummary.failed?.length ? (
                  <div className="mt-2 max-h-72 overflow-auto rounded border border-rose-200">
                    <table className="min-w-full text-xs">
                      <thead className="bg-rose-50 text-rose-700">
                        <tr>
                          <th className="px-2 py-1 text-left">BOL</th>
                          <th className="px-2 py-1 text-left">PO Number</th>
                          <th className="px-2 py-1 text-left">Load ID</th>
                          <th className="px-2 py-1 text-left">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fedexSummary.failed.map((row, index) => (
                          <tr key={`fedex-failed-${index}`} className="odd:bg-white even:bg-rose-50/60">
                            <td className="px-2 py-1 font-semibold text-rose-900">{row.bolNumber || "-"}</td>
                            <td className="px-2 py-1 text-slate-700">{row.poNumber || "-"}</td>
                            <td className="px-2 py-1 text-slate-700">{row.loadId || "-"}</td>
                            <td className="px-2 py-1 text-rose-600">
                              <div className="space-y-1">
                                <div>{getFedexLabelErrorMessage(row.errorCode, row.error || row.message || "Failed")}</div>
                                {(row.fedexErrorCode || row.fedexTransactionId || row.httpStatus) ? (
                                  <div className="text-[11px] text-rose-700">
                                    {row.fedexErrorCode ? `Code: ${row.fedexErrorCode}` : ""}
                                    {row.fedexErrorCode && row.httpStatus ? " | " : ""}
                                    {row.httpStatus ? `HTTP: ${row.httpStatus}` : ""}
                                    {(row.fedexErrorCode || row.httpStatus) && row.fedexTransactionId ? " | " : ""}
                                    {row.fedexTransactionId ? `Transaction ID: ${row.fedexTransactionId}` : ""}
                                  </div>
                                ) : null}
                                {row.diagnosticPayloadPath ? (
                                  <div className="text-[11px] text-rose-700">
                                    {me?.role?.toLowerCase?.() === "admin"
                                      ? `Diagnostic payload: ${row.diagnosticPayloadPath}`
                                      : "Diagnostic payload saved for follow-up."}
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">No failures.</p>
                )}
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              {fedexSummary?.succeeded?.length ? (
                <button
                  type="button"
                  className="mr-3 rounded bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handlePrintAllFedexLabels}
                  disabled={!fedexSummary.succeeded.some((row) => hasFedexPdfFallback(row))}
                >
                  Print All
                </button>
              ) : null}
              {fedexSummary?.succeeded?.length ? (
                <button
                  type="button"
                  className="mr-3 rounded border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={handleDownloadAllFedexLabels}
                  disabled={!fedexSummary.succeeded.some((row) => hasFedexPdfFallback(row))}
                >
                  Download All PDFs
                </button>
              ) : null}
              <button
                type="button"
                className="rounded bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700"
                onClick={handleCloseFedexSummary}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {(fedexDetailsLoading || fedexDetails) && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={fedexDetailsLoading ? undefined : handleCloseFedexDetails} />
          <div className="relative z-50 flex w-full max-w-6xl flex-col rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">FedEx Label Details</h2>
                <p className="text-sm text-slate-500">
                  Review routing linkage, label lifecycle, payloads, and full history without adding extra columns to the main table.
                </p>
              </div>
              <button
                type="button"
                className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
                onClick={handleCloseFedexDetails}
                disabled={fedexDetailsLoading}
              >
                Close
              </button>
            </div>
            {fedexDetailsLoading && !fedexDetails ? (
              <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-600">
                Loading FedEx label details...
              </div>
            ) : fedexDetails ? (
              <div className="mt-4 max-h-[78vh] space-y-5 overflow-y-auto pr-1">
                <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Routing Link Summary</h3>
                      <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-700 md:grid-cols-2 xl:grid-cols-3">
                        <div><span className="font-semibold text-slate-900">Cust Code:</span> {fedexDetails?.routingSummary?.custCode || "-"}</div>
                        <div><span className="font-semibold text-slate-900">PO Number:</span> {fedexDetails?.routingSummary?.poNumber || "-"}</div>
                        <div><span className="font-semibold text-slate-900">Load ID:</span> {fedexDetails?.routingSummary?.loadId || "-"}</div>
                        <div><span className="font-semibold text-slate-900">BOL Number:</span> {fedexDetails?.routingSummary?.bolNumber || "-"}</div>
                        <div><span className="font-semibold text-slate-900">Routing UID:</span> {fedexDetails?.routingSummary?.routingUid || "-"}</div>
                        <div><span className="font-semibold text-slate-900">Routing Row ID:</span> {fedexDetails?.routingSummary?.routingRowId || "-"}</div>
                        <div><span className="font-semibold text-slate-900">Outbound / Trucking #:</span> {fedexDetails?.routingSummary?.outboundCode || "-"}</div>
                      </div>
                    </div>
                    <FedexStatusBadge summary={fedexDetails?.labelSummary} />
                  </div>
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-4">
                  <h3 className="text-sm font-semibold text-slate-900">Label Lifecycle</h3>
                  <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-700 md:grid-cols-2 xl:grid-cols-3">
                    <div><span className="font-semibold text-slate-900">Label Status:</span> {fedexDetails?.lifecycle?.labelStatusLabel || "Not Created"}</div>
                    <div><span className="font-semibold text-slate-900">Tracking #:</span> {fedexDetails?.lifecycle?.trackingNumber || "-"}</div>
                    <div><span className="font-semibold text-slate-900">Created At:</span> {fedexDetails?.lifecycle?.createdAt || "-"}</div>
                    <div><span className="font-semibold text-slate-900">Created By:</span> {fedexDetails?.lifecycle?.createdBy || "-"}</div>
                    <div><span className="font-semibold text-slate-900">Is Active:</span> {fedexDetails?.lifecycle?.isActive ? "Yes" : "No"}</div>
                    <div><span className="font-semibold text-slate-900">Superseded At:</span> {fedexDetails?.lifecycle?.supersededAt || "-"}</div>
                    <div><span className="font-semibold text-slate-900">Superseded By ID:</span> {fedexDetails?.lifecycle?.supersededById || "-"}</div>
                    <div><span className="font-semibold text-slate-900">Voided At:</span> {fedexDetails?.lifecycle?.voidedAt || "-"}</div>
                    <div><span className="font-semibold text-slate-900">Voided By:</span> {fedexDetails?.lifecycle?.voidedBy || "-"}</div>
                  </div>
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-4">
                  <h3 className="text-sm font-semibold text-slate-900">Files / Actions</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {fedexDetails?.files?.downloadAvailable && fedexDetails?.files?.downloadLabelUrl ? (
                      <button
                        type="button"
                        className="rounded border border-sky-300 bg-sky-50 px-3 py-1.5 text-sm font-semibold text-sky-700 hover:bg-sky-100"
                        onClick={() =>
                          void handleDownloadFedexArtifactUrl(
                            fedexDetails.files.downloadLabelUrl,
                            fedexDetails?.lifecycle?.trackingNumber
                              ? `fedex-label-${fedexDetails.lifecycle.trackingNumber}.${String(fedexDetails?.lifecycle?.labelImageType || "PDF").toLowerCase() === "zplii" ? "zpl" : "pdf"}`
                              : "fedex-label.pdf"
                          )
                        }
                      >
                        Download Label
                      </button>
                    ) : (
                      <span className="rounded border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-500">
                        {fedexDetails?.files?.downloadUnavailableReason || "Label file is not available."}
                      </span>
                    )}
                    <button
                      type="button"
                      className="rounded border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                      onClick={() => void copyFedexText(fedexDetails?.lifecycle?.trackingNumber, "Unable to copy tracking number.")}
                    >
                      Copy Tracking #
                    </button>
                    <button
                      type="button"
                      className="rounded border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                      onClick={() => void copyFedexText(fedexDetails?.routingSummary?.routingUid, "Unable to copy routing UID.")}
                    >
                      Copy Routing UID
                    </button>
                    {canVoidFedexLabelFromDetails ? (
                      <button
                        type="button"
                        className="rounded border border-rose-300 bg-rose-50 px-3 py-1.5 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                        onClick={() => void handleVoidFedexLabelFromDetails()}
                        disabled={
                          creatingFedexLabels ||
                          voidingFedexLabelIds.has(String(currentFedexDetailsLifecycle?.labelId || ""))
                        }
                      >
                        {voidingFedexLabelIds.has(String(currentFedexDetailsLifecycle?.labelId || ""))
                          ? "Voiding..."
                          : "Void Label"}
                      </button>
                    ) : null}
                    {canManageFedexLabels && currentFedexDetailsRowsPayload.length > 0 ? (
                      <button
                        type="button"
                        className="rounded bg-orange-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
                        onClick={() => void handleCreateFedexLabelFromDetails()}
                        disabled={creatingFedexLabels || voidingFedexLabelIds.size > 0 || !canCreateFedexLabelFromDetails}
                      >
                        {creatingFedexLabels ? "Working..." : "Create New Label"}
                      </button>
                    ) : null}
                  </div>
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-slate-900">Payload Viewer</h3>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className={`rounded px-3 py-1.5 text-sm font-semibold ${
                          fedexDetailsPayloadTab === "create"
                            ? "bg-slate-900 text-white"
                            : "border border-slate-300 text-slate-700 hover:bg-slate-100"
                        }`}
                        onClick={() => setFedexDetailsPayloadTab("create")}
                      >
                        View Create Response
                      </button>
                      <button
                        type="button"
                        className={`rounded px-3 py-1.5 text-sm font-semibold ${
                          fedexDetailsPayloadTab === "void"
                            ? "bg-slate-900 text-white"
                            : "border border-slate-300 text-slate-700 hover:bg-slate-100"
                        }`}
                        onClick={() => setFedexDetailsPayloadTab("void")}
                      >
                        View Void Response
                      </button>
                      <button
                        type="button"
                        className={`rounded px-3 py-1.5 text-sm font-semibold ${
                          fedexDetailsPayloadTab === "voidFailureDiagnostic"
                            ? "bg-slate-900 text-white"
                            : "border border-slate-300 text-slate-700 hover:bg-slate-100"
                        }`}
                        onClick={() => setFedexDetailsPayloadTab("voidFailureDiagnostic")}
                      >
                        View Void Failure Diagnostic
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      className="rounded border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                      onClick={() =>
                        void copyFedexText(
                          formatFedexPayloadText(fedexDetails?.payloads?.[fedexDetailsPayloadTab]),
                          "Unable to copy payload."
                        )
                      }
                    >
                      Copy Payload
                    </button>
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 md:grid-cols-2">
                    <div>
                      <span className="font-semibold text-slate-900">Payload Status:</span>{" "}
                      {fedexDetails?.payloads?.[fedexDetailsPayloadTab]?.status || "UNAVAILABLE"}
                    </div>
                    <div>
                      <span className="font-semibold text-slate-900">Reason:</span>{" "}
                      {fedexDetails?.payloads?.[fedexDetailsPayloadTab]?.reason || "Payload file is not available."}
                    </div>
                    {me?.role?.toLowerCase?.() === "admin" && fedexDetails?.payloads?.[fedexDetailsPayloadTab]?.debug?.displayPath ? (
                      <div className="md:col-span-2 break-all">
                        <span className="font-semibold text-slate-900">Payload Path:</span>{" "}
                        {fedexDetails.payloads[fedexDetailsPayloadTab].debug.displayPath}
                      </div>
                    ) : null}
                    {me?.role?.toLowerCase?.() === "admin" && fedexDetails?.payloads?.[fedexDetailsPayloadTab]?.debug?.pathKind ? (
                      <div className="md:col-span-2">
                        <span className="font-semibold text-slate-900">Debug:</span>{" "}
                        {fedexDetails.payloads[fedexDetailsPayloadTab].debug.pathKind}
                        {fedexDetails?.payloads?.[fedexDetailsPayloadTab]?.debug?.resolvedSource
                          ? ` -> ${fedexDetails.payloads[fedexDetailsPayloadTab].debug.resolvedSource}`
                          : ""}
                      </div>
                    ) : null}
                  </div>
                  <div className="mt-3 max-h-80 overflow-auto rounded-lg border border-slate-200 bg-slate-950 p-3 text-xs text-slate-100">
                    <pre className="whitespace-pre-wrap break-words">
                      {formatFedexPayloadText(fedexDetails?.payloads?.[fedexDetailsPayloadTab])}
                    </pre>
                  </div>
                </section>

                <section className="rounded-xl border border-slate-200 bg-white p-4">
                  <h3 className="text-sm font-semibold text-slate-900">History</h3>
                  {fedexDetails?.history?.length ? (
                    <div className="mt-3 max-h-72 overflow-auto rounded-lg border border-slate-200">
                      <table className="min-w-full text-xs">
                        <thead className="bg-slate-50 text-slate-700">
                          <tr>
                            <th className="px-2 py-1 text-left">Label ID</th>
                            <th className="px-2 py-1 text-left">Status</th>
                            <th className="px-2 py-1 text-left">Tracking #</th>
                            <th className="px-2 py-1 text-left">Created At</th>
                            <th className="px-2 py-1 text-left">Created By</th>
                            <th className="px-2 py-1 text-left">Voided At</th>
                            <th className="px-2 py-1 text-left">Superseded At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fedexDetails.history.map((entry) => (
                            <tr key={`fedex-history-${entry.labelId || "none"}-${entry.createdAt || entry.trackingNumber || "na"}`} className="odd:bg-white even:bg-slate-50">
                              <td className="px-2 py-1 text-slate-700">{entry.labelId || "-"}</td>
                              <td className="px-2 py-1"><FedexStatusBadge summary={entry} compact /></td>
                              <td className="px-2 py-1 text-slate-700">{entry.trackingNumber || "-"}</td>
                              <td className="px-2 py-1 text-slate-700">{entry.createdAt || "-"}</td>
                              <td className="px-2 py-1 text-slate-700">{entry.createdBy || "-"}</td>
                              <td className="px-2 py-1 text-slate-700">{entry.voidedAt || "-"}</td>
                              <td className="px-2 py-1 text-slate-700">{entry.supersededAt || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-slate-500">No FedEx label history exists for this routing row.</p>
                  )}
                </section>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {bolModalOpen && (
        <BolPreviewModal
          open={bolModalOpen}
          bolDraft={bolDraft}
          loading={bolLoading}
          selectedIndex={bolSelectedIndex}
          routingStatusId={selectedRoutingStatusId}
          missingShipToNodes={bolMissingNodes}
          forceDraftView={bolForceDraftView}
          manualShipToOverrides={bolManualShipToOverrides}
          onManualShipToOverridesChange={setBolManualShipToOverrides}
          onResolveMissingShipTo={handleResolveMissingBolAddresses}
          onCancelMissingShipTo={() => {
            setBolModalOpen(false);
            setBolDraft(null);
            setBolMissingNodes([]);
            setBolForceDraftView(false);
            setBolManualShipToOverrides({});
            setError("BOL preview was cancelled.");
          }}
          onSelectIndex={setBolSelectedIndex}
          onClose={handleCloseBolPreview}
        />
      )}

      {holdModalOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCloseHoldModal} />
          <div className="relative z-50 w-full max-w-4xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Hold OB Create</h2>
              <button
                type="button"
                className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
                onClick={handleCloseHoldModal}
              >
                Close
              </button>
            </div>
            <div className="mt-4 space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-slate-700">Active Conditions</h3>
                <div className="mt-2 max-h-72 overflow-auto rounded border border-slate-200">
                  {loadingHoldRules ? (
                    <p className="px-3 py-2 text-xs text-slate-500">Loading hold rules...</p>
                  ) : holdRules.length ? (
                    <table className="min-w-full text-xs">
                      <thead className="bg-slate-50 text-slate-600">
                        <tr>
                          <th className="px-2 py-1 text-left">Field</th>
                          <th className="px-2 py-1 text-left">Operator</th>
                          <th className="px-2 py-1 text-left">Value</th>
                          <th className="px-2 py-1 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {holdRules.map((rule) => (
                          <tr key={rule.id} className="odd:bg-white even:bg-slate-50">
                            <td className="px-2 py-1 text-slate-700">{rule.label || rule.column}</td>
                            <td className="px-2 py-1 text-slate-700">{rule.operator}</td>
                            <td className="px-2 py-1 text-slate-600">{rule.value}</td>
                            <td className="px-2 py-1 text-right">
                              <button
                                type="button"
                                onClick={() => handleDeleteHoldRule(rule.id)}
                                disabled={importDisabled || deletingHoldRuleId === rule.id}
                                className="rounded border border-red-200 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {deletingHoldRuleId === rule.id ? "Removing..." : "Remove"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="px-3 py-2 text-xs text-slate-500">No hold rules configured.</p>
                  )}
                </div>
                {holdRulesError && <p className="mt-2 text-xs text-red-600">{holdRulesError}</p>}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-700">Add Condition</h3>
                <div className="mt-2 space-y-3">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Field
                    <select
                      value={holdForm.column}
                      onChange={(event) =>
                        setHoldForm({ column: event.target.value, operator: "=", value: "" })
                      }
                      className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {holdFields.map((field) => (
                        <option key={field.column} value={field.column}>
                          {field.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Operator
                    <select
                      value={holdForm.operator}
                      onChange={(event) => setHoldForm((prev) => ({ ...prev, operator: event.target.value }))}
                      className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      disabled={!currentHoldField}
                    >
                      {(currentHoldField?.type === "text" ? ["="] : ["=", ">", "<"]).map((operator) => (
                        <option key={operator} value={operator}>
                          {operator}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Value
                    <div className="mt-1">
                      {currentHoldField?.type === "date" ? (
                        <input
                          type="date"
                          value={toISODateInputValue(holdForm.value)}
                          onChange={(event) => setHoldForm((prev) => ({ ...prev, value: event.target.value }))}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      ) : currentHoldField?.type === "number" ? (
                        <input
                          type="number"
                          value={holdForm.value}
                          onChange={(event) => setHoldForm((prev) => ({ ...prev, value: event.target.value }))}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      ) : currentHoldField?.type === "text" &&
                        (holdFieldValueOptions[currentHoldField.column] || []).length ? (
                        <select
                          value={holdForm.value}
                          onChange={(event) => setHoldForm((prev) => ({ ...prev, value: event.target.value }))}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select value</option>
                          {holdFieldValueOptions[currentHoldField.column].map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={holdForm.value}
                          onChange={(event) => setHoldForm((prev) => ({ ...prev, value: event.target.value }))}
                          className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Enter value"
                        />
                      )}
                    </div>
                  </label>
                </div>
                {holdFormError && <p className="mt-2 text-xs text-red-600">{holdFormError}</p>}
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
                    onClick={handleCloseHoldModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveHoldRule}
                    disabled={savingHoldRule || importDisabled || !currentHoldField}
                    className="rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {savingHoldRule ? "Saving..." : "Save Rule"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            {loadHistoryRow && <LoadHistoryDialog row={loadHistoryRow} apiBase={API_BASE} headers={legacyHeaders} onClose={() => setLoadHistoryRow(null)} />}
            <h1 className="text-2xl font-semibold text-emerald-700">{pageLabel}</h1>
            <p className="text-sm text-slate-500">
              Import the latest Routing Status workbook or CSV, then filter, sort, and resize columns as needed.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <div className="inline-flex rounded-md border border-slate-300 bg-white p-1 text-sm shadow-sm">
                <button
                  type="button"
                  onClick={() => setIncludeSuperseded(false)}
                  className={`rounded px-3 py-1.5 font-medium ${
                    includeSuperseded ? "text-slate-700 hover:bg-slate-100" : "bg-slate-900 text-white"
                  }`}
                >
                  Active Only
                </button>
                <button
                  type="button"
                  onClick={() => setIncludeSuperseded(true)}
                  className={`rounded px-3 py-1.5 font-medium ${
                    includeSuperseded ? "bg-amber-100 text-amber-900" : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  Include Superseded
                </button>
              </div>
              <p className="text-xs text-slate-500">
                {includeSuperseded
                  ? "Superseded rows are historical only. They stay visible for reference, show a Superseded badge, and cannot be selected for active actions."
                  : "Default view shows active operational rows only."}
              </p>
            </div>
            {sandbox && (
              <div className="mt-2 w-full rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800">
                {sandboxBanner}
              </div>
            )}
            {fileMeta && (
              <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600">
                <span className="font-semibold text-slate-700">Workbook:</span>
                <span className="max-w-[200px] truncate" title={fileMeta.name}>
                  {fileMeta.name}
                </span>
                <span className="h-3 w-px bg-slate-300" />
                <span className="font-semibold text-slate-700">Sheet:</span>
                <span>{fileMeta.sheetName}</span>
                <span className="h-3 w-px bg-slate-300" />
                <span className="font-semibold text-slate-700">Loaded PST:</span>
                <span>{formatValueToPst(fileMeta.updatedAt) || ""}</span>
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <UserAccessSummary />
            <div className="flex flex-wrap items-center gap-2 justify-end">
              <button
                type="button"
                onClick={handleTriggerUpload}
                disabled={importDisabled || saving || !accessibleCustOptions.length}
                title={
                  importDisabled
                    ? "Import disabled for your role"
                    : saving
                      ? "Import disabled while saving"
                      : !accessibleCustOptions.length
                        ? "No accessible Cust Code to import"
                        : ""
                }
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Import
              </button>
              <button
                type="button"
                onClick={handleAllocateSelected}
                disabled={selectedRows.size === 0 || saving || allocating || !dataset || hasOutboundSelected}
                className="rounded-md border border-emerald-200 px-3 py-2 text-sm font-medium text-emerald-700 shadow-sm hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
                title={
                  selectedRows.size
                    ? hasOutboundSelected
                      ? "Cannot allocate rows that already have Outbound Code"
                      : "Run PO allocation with the selected routing rows"
                    : "Select rows to enable PO Allocate"
                }
              >
                {allocating ? "Allocating..." : "PO Allocate"}
              </button>
              <button
                type="button"
                onClick={handleStartUpdateIls}
                disabled={!canUpdateIls || saving || allocating || updatingIls}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
                title={
                  canUpdateIls
                    ? "Sync selected Update rows to iLS"
                    : "Select at least one row whose Status is Update to enable"
                }
              >
                {updatingIls ? "Updating..." : "Update iLS"}
              </button>
              <button
                type="button"
                onClick={handleOpenBolPreview}
                disabled={!canCreateBol || saving || allocating}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
                title={
                  selectedRows.size !== 1
                    ? "Select exactly one row to enable"
                    : !selectedRoutingStatusId
                      ? "Selected row is missing routing identifier"
                      : "Preview BOL draft"
                }
              >
                {bolLoading ? "Loading..." : "Create BOL"}
              </button>
              <button
                type="button"
                onClick={handleCreateFedexLabels}
                disabled={!canCreateFedexLabels || saving || allocating || bolLoading}
                className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
                title={
                  selectedRows.size === 0
                    ? "Select SSPI rows to enable"
                    : !industryScacHeaderKey
                      ? "Routing dataset is missing Industry SCAC"
                      : fedexEligibleRows.length !== selectedRows.size
                        ? "All selected rows must have Industry SCAC = SSPI"
                        : "Create FedEx PDF labels for the selected BOL rows"
                }
              >
                {creatingFedexLabels ? "Creating..." : "FedEx Label"}
              </button>
              <MultiSelectDropdown
                values={columnDropdownValues}
                options={columnOptions}
                onChange={handleVisibleColumnsChange}
                placeholder="Columns"
                disabled={!dataset?.headers?.length}
                buttonClassName="min-w-[160px]"
              />
              <Menu as="div" className="relative">
                <Menu.Button
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  title="More actions"
                >
                  ⋯
                </Menu.Button>
                <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-md border border-slate-200 bg-white p-1 shadow-xl focus:outline-none">
                  <div className="space-y-1">
                    <Menu.Item disabled={!canUndo || saving}>
                      {({ active, disabled }) => (
                        <button
                          type="button"
                          onClick={disabled ? undefined : handleUndoImport}
                          className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm ${
                            disabled
                              ? "cursor-not-allowed text-slate-400"
                              : active
                                ? "bg-slate-100 text-slate-900"
                                : "text-slate-700"
                          }`}
                        >
                          Undo Import
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item disabled={selectedRows.size === 0 || saving || !dataset}>
                      {({ active, disabled }) => (
                        <button
                          type="button"
                          onClick={disabled ? undefined : handleDeleteSelected}
                          className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm ${
                            disabled
                              ? "cursor-not-allowed text-slate-400"
                              : active
                                ? "bg-slate-100 text-slate-900"
                                : "text-slate-700"
                          }`}
                        >
                          Delete Selected
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          type="button"
                          onClick={handleOpenHoldModal}
                          className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm ${
                            active ? "bg-slate-100 text-slate-900" : "text-slate-700"
                          }`}
                        >
                          Hold OB Create
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item disabled={wmsUpdating || saving}>
                      {({ active, disabled }) => (
                        <button
                          type="button"
                          onClick={disabled ? undefined : handleWmsStatusUpdate}
                          className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm ${
                            disabled
                              ? "cursor-not-allowed text-slate-400"
                              : active
                                ? "bg-slate-100 text-slate-900"
                                : "text-slate-700"
                          }`}
                        >
                          {wmsUpdating ? "Updating WMS..." : "WMS Status Update"}
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item disabled={!dataset?.headers}>
                      {({ active, disabled }) => (
                        <button
                          type="button"
                          onClick={disabled ? undefined : resetColumnWidths}
                          className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm ${
                            disabled
                              ? "cursor-not-allowed text-slate-400"
                              : active
                                ? "bg-slate-100 text-slate-900"
                                : "text-slate-700"
                          }`}
                        >
                          Reset Widths
                        </button>
                      )}
                    </Menu.Item>
                    <Menu.Item disabled={!dataset?.headers || !sortedRows.length}>
                      {({ active, disabled }) => (
                        <button
                          type="button"
                          onClick={disabled ? undefined : handleExport}
                          className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm ${
                            disabled
                              ? "cursor-not-allowed text-slate-400"
                              : active
                                ? "bg-slate-100 text-slate-900"
                                : "text-slate-700"
                          }`}
                        >
                          Export
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Menu>
              {importDisabled && <span className="text-xs text-slate-500">Read only - import disabled</span>}
              {!importDisabled && saving && <span className="text-xs text-slate-500">{importPhase || "Saving..."}</span>}
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          </div>
        </div>
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {importCorrectionSummary && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-semibold">Routing Status import completed with correction protection</p>
              <p className="text-xs sm:text-sm">
                {importCorrectionSummary.protectedRowCount ?? 0} protected row(s) reviewed.
                {importCorrectionSummary.protectedRowsCasesPreserved
                  ? ` Cases preserved on ${importCorrectionSummary.protectedRowsCasesPreserved} mismatched protected row(s).`
                  : ""}
                {importCorrectionSummary.protectedRowsSkipped
                  ? ` ${importCorrectionSummary.protectedRowsSkipped} mismatched protected row(s) were skipped.`
                  : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setImportCorrectionSummary(null)}
              className="self-start rounded border border-current px-3 py-1 text-xs font-semibold sm:self-center"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      <RoutingImportWarning summary={workbookImportSummary} onDismiss={() => setWorkbookImportSummary(null)} />
      {showMissingPoBanner && (
        <div className="mb-3 flex items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p>
            Found {poFilterTokens.length - missingPoNumbers.length} of {poFilterTokens.length} PO numbers. Not found:{" "}
            {missingPoNumbers.join(", ")}
          </p>
          <button
            type="button"
            onClick={() => setMissingPoBannerDismissed(true)}
            className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded border border-amber-200 text-amber-700 hover:bg-amber-100"
            aria-label="Dismiss"
            title="Dismiss"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
      {wmsSummary && (
        <div
          className={`rounded-md border px-4 py-3 text-sm ${
            wmsSummary.failureCount
              ? "border-amber-200 bg-amber-50 text-amber-800"
              : "border-emerald-200 bg-emerald-50 text-emerald-800"
          }`}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-semibold">
                {wmsSummary.failureCount ? "WMS status updated with warnings" : "WMS status updated"}
              </p>
              <p className="text-xs sm:text-sm">
                Updated {wmsSummary.successCount ?? 0} of {wmsSummary.attempted ?? 0} rows
                {wmsSummary.skipped ? `, skipped ${wmsSummary.skipped}` : ""}.
              </p>
            </div>
            <button
              type="button"
              onClick={handleDismissWmsSummary}
              className="self-start rounded border border-current px-3 py-1 text-xs font-semibold sm:self-center"
            >
              Dismiss
            </button>
          </div>
          {Array.isArray(wmsSummary.failed) && wmsSummary.failed.length > 0 && (
            <div className="mt-2 border-t border-white/40 pt-2 text-xs">
              <p className="font-semibold">First few errors:</p>
              <ul className="mt-1 space-y-1">
                {wmsSummary.failed.slice(0, 3).map((row, index) => (
                  <li key={`wms-failed-${index}`} className="truncate">
                    <span className="font-semibold">{row.outboundCode || row.routingRowId || "Row"}:</span>{" "}
                    {row.error || row.message || "Failed"}
                  </li>
                ))}
                {wmsSummary.failed.length > 3 && (
                  <li className="text-slate-600">
                    +{wmsSummary.failed.length - 3} more failure{wmsSummary.failed.length - 3 === 1 ? "" : "s"}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {wmsUpdating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
            <p className="text-sm font-semibold text-slate-700">Refreshing WMS status…</p>
            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-slate-200">
              <div className="h-full w-full animate-pulse bg-emerald-500" />
            </div>
          </div>
        </div>
      )}

      {includeSuperseded && supersededVisibleCount > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Showing {supersededVisibleCount} superseded historical row{supersededVisibleCount === 1 ? "" : "s"}.
          Active rows remain selectable; superseded rows are read-only in this table view.
        </div>
      )}

      {dataset ? (
        <>
          <div
            ref={tableContainerRef}
            className="flex-1 min-h-0 overflow-auto rounded-lg border border-slate-300 shadow-md"
            onMouseDown={(event) => {
              const target = event.target;
              if (target && target.closest && target.closest(".po-copy-source")) {
                selectionModeRef.current = "PO_ONLY";
                return;
              }
              selectionModeRef.current = "NORMAL";
            }}
          >
            <table className="min-w-full border-collapse text-sm">
              <thead className="sticky top-0 z-30">
                <tr className="bg-emerald-900 text-emerald-50 text-xs uppercase tracking-wide">
                  <th
                    className="sticky top-0 bg-emerald-900 px-2 py-2 text-center font-semibold z-40"
                    style={{ width: selectColumnWidth, minWidth: selectColumnWidth, left: 0 }}
                  >
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      onChange={handleToggleSelectAll}
                      checked={allVisibleSelected && selectableVisibleRowIds.length > 0}
                    />
                    <span
                      title="Drag to resize"
                      className="ml-1 inline-flex h-6 w-2 cursor-col-resize select-none rounded bg-white/10 hover:bg-white/70"
                      onMouseDown={(event) => onDragStart(event, SELECT_COLUMN_KEY)}
                    />
                  </th>
                  {visibleHeaders.map((header) => {
                    const stickyLeft = stickyOffsets[header.key];
                    const headerTooltip = header.key === D2M_KEY ? "Days to MABD" : undefined;
                    return (
                      <th
                        key={header.key}
                        style={{
                          width: widths[header.key],
                          minWidth: widths[header.key],
                          left: stickyLeft !== undefined ? stickyLeft : undefined,
                        }}
                        className={`sticky top-0 bg-emerald-900 px-4 py-2 text-left font-semibold align-top ${
                          stickyLeft !== undefined ? "z-40 shadow-md" : ""
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <button
                            type="button"
                            disabled={header.key === EDI_STATUS_KEY}
                            onClick={() => toggleSort(header.key)}
                            className="flex items-center gap-2 text-left focus:outline-none"
                          >
                            <span
                              className="block whitespace-normal break-words leading-tight"
                              title={headerTooltip}
                            >
                              {canonicalizeHeaderLabel(header.label) ===
                              canonicalizeHeaderLabel(PO_NUMBER_LABEL) ? (
                                <span className="inline-flex items-center gap-1.5">
                                  <span>{PO_NUMBER_LABEL}</span>
                                  <span
                                    className="text-emerald-200/70 hover:text-emerald-100"
                                    title="Tip: Drag-select PO# and copy — paste will contain PO numbers only."
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <Info className="h-3.5 w-3.5" aria-hidden="true" />
                                  </span>
                                </span>
                              ) : (
                                (header.label === 'Status' ? 'Import Status' : header.label === 'Load ID' ? 'PortPilot Load ID' : header.label)
                              )}
                            </span>
                            <span className="inline-flex h-5 w-5 items-center justify-center">
                              {sortState.key === header.key ? (
                                sortState.direction === 1 ? (
                                  <ChevronUp className="h-4 w-4 text-white" aria-hidden="true" />
                                ) : sortState.direction === -1 ? (
                                  <ChevronDown className="h-4 w-4 text-white" aria-hidden="true" />
                                ) : (
                                  <ChevronsUpDown className="h-4 w-4 text-slate-300" aria-hidden="true" />
                                )
                              ) : (
                                <ChevronsUpDown className="h-4 w-4 text-slate-300" aria-hidden="true" />
                              )}
                            </span>
                          </button>
                          <span
                            title="Drag to resize"
                            className="ml-1 inline-flex h-6 w-2 cursor-col-resize select-none rounded bg-white/10 hover:bg-white/70"
                            onMouseDown={(event) => onDragStart(event, header.key)}
                          />
                        </div>
                      </th>
                    );
                  })}
                </tr>
                <tr className="bg-emerald-50 text-xs text-emerald-700">
                  <th
                    className="sticky top-[40px] bg-emerald-50 px-2 py-2 text-center z-30"
                    style={{ width: selectColumnWidth, minWidth: selectColumnWidth, left: 0 }}
                  >
                    <button
                      type="button"
                      onClick={handleResetFilters}
                      disabled={!dataset?.headers}
                      title="Reset filters"
                      className={`mx-auto flex h-7 w-7 items-center justify-center rounded-full border transition ${
                        hasActiveFilters
                          ? "border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600"
                          : "border-slate-300 bg-slate-200 text-slate-600 hover:bg-slate-300"
                      } ${!dataset?.headers ? "cursor-not-allowed opacity-60" : ""}`}
                    >
                      <RotateCw className="h-4 w-4" />
                    </button>
                  </th>
                   {visibleHeaders.map((header) => {
                     const widthStyle = { width: widths[header.key], minWidth: widths[header.key] };
                    const mode = getFilterModeForHeader(header);
                    const stickyLeft = stickyOffsets[header.key];
                    const filterClass = `sticky top-[40px] bg-emerald-50 px-3 py-2 align-top ${
                      stickyLeft !== undefined ? "z-30" : ""
                    }`;
                    const filterStyle = {
                      ...widthStyle,
                      left: stickyLeft !== undefined ? stickyLeft : undefined,
                    };
                    if (header.key === EDI_STATUS_KEY) {
                      return <th key={`${header.key}-filter`} style={filterStyle} className={filterClass} />;
                    }
                    if (mode === FILTER_MODES.date) {
                      const currentValue = filters[header.key] || { from: "", to: "" };
                      const pickerValue = {
                        from: toISODateInputValue(currentValue.from),
                        to: toISODateInputValue(currentValue.to),
                      };
                      return (
                        <th key={`${header.key}-filter`} style={filterStyle} className={filterClass}>
                          <DateRangeFilter
                            value={pickerValue}
                            onChange={({ from, to }) => setRangeFilter(header.key, { from, to })}
                            placeholder="Select range"
                          />
                        </th>
                      );
                    }
                    if (mode === FILTER_MODES.range) {
                      const currentValue = filters[header.key] || { from: "", to: "" };
                      return (
                        <th key={`${header.key}-filter`} style={filterStyle} className={filterClass}>
                          <NumberRangeFilter
                            value={currentValue}
                            onChange={(range) => setRangeFilter(header.key, range)}
                          />
                        </th>
                      );
                    }
                    if (mode === FILTER_MODES.multiSelect) {
                      return (
                        <th key={`${header.key}-filter`} style={filterStyle} className={filterClass}>
                          <MultiSelectDropdown
                            values={filters[header.key] || []}
                            options={multiSelectOptions[header.key] || []}
                            onChange={(nextValues) => updateFilter(header.key, nextValues)}
                          />
                        </th>
                      );
                    }
                    if (mode === FILTER_MODES.fedexStatus) {
                      return (
                        <th key={`${header.key}-filter`} style={filterStyle} className={filterClass}>
                          <MultiSelectDropdown
                            values={Array.isArray(filters[header.key]) ? filters[header.key] : []}
                            options={FEDEX_FILTER_OPTIONS}
                            placeholder="All"
                            onChange={(nextValues) => updateFilter(header.key, nextValues)}
                          />
                        </th>
                      );
                    }
                    if (mode === FILTER_MODES.fedexTracking) {
                      return (
                        <th key={`${header.key}-filter`} style={filterStyle} className={filterClass}>
                          <textarea
                            rows={2}
                            placeholder="Tracking # (space/comma)"
                            value={filters[header.key] || ""}
                            onChange={(event) => updateFilter(header.key, event.target.value)}
                            className="w-full resize-y rounded border border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </th>
                      );
                    }
                    return (
                      <th
                        key={`${header.key}-filter`}
                        style={filterStyle}
                        className={filterClass}
                      >
                        <input
                          type="text"
                          placeholder={mode === FILTER_MODES.multiText ? "PO# (space/comma separated)" : "Search"}
                          value={filters[header.key] || ""}
                          onChange={(event) => updateFilter(header.key, event.target.value)}
                          className="w-full rounded border border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {pagedRows.length ? (
                  pagedRows.map((record) => {
                    const statusValue = record[STATUS_KEY];
                    const statusClass = STATUS_ROW_CLASSES[statusValue] || "";
                    const rowSuperseded = isSupersededRoutingRow(record);
                    const supersededHint = getSupersededReplacementLabel(record);
                    const rowClassName = [
                      "border-t",
                      rowSuperseded
                        ? "bg-amber-50/80 text-slate-600 hover:bg-amber-100/70"
                        : "hover:bg-blue-50",
                      rowSuperseded ? "" : statusClass || "odd:bg-white even:bg-slate-50",
                    ]
                      .filter(Boolean)
                      .join(" ");
                    const rowOnHold = Boolean(record[ON_HOLD_KEY]);
                    const wmsUpdatedAt = record.__wmsUpdatedAt;
                    const fedexSummary = getFedexSummary(record);
                    return (
                      <tr
                        key={record._id}
                        className={rowClassName}
                        data-routing-row-id={String(record?.[ROUTING_ROW_ID_KEY] || "").trim()}
                        data-routing-uid={String(record?.[ROUTING_UID_KEY] || "").trim()}
                      >
                        <td
                          className="sticky left-0 z-20 px-2 py-2 text-center bg-inherit"
                          style={{ width: selectColumnWidth, minWidth: selectColumnWidth }}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            checked={selectedRows.has(record._id)}
                            onChange={() => toggleRowSelection(record._id)}
                            disabled={rowOnHold || rowSuperseded}
                            title={
                              rowSuperseded
                                ? "Superseded rows are historical only and cannot be selected for active actions"
                                : rowOnHold
                                  ? "Rows On Hold cannot be selected"
                                  : undefined
                            }
                          />
                        </td>
                        {visibleHeaders.map((header) => {
                          const diffMeta = record.__diff?.[header.key];
                          const tooltipValue =
                            diffMeta && Object.prototype.hasOwnProperty.call(diffMeta, "previous")
                              ? diffMeta.previous ?? ""
                              : record[header.key] ?? "";
                          const isStatusCell = header.key === STATUS_KEY;
                          const isOnHoldCell = header.key === ON_HOLD_KEY;
                          const isWmsCell = header.key === WMS_KEY;
                          const isD2mCell = header.key === D2M_KEY;
                          const isPoCell = header.key === poHeaderKey;
                          const isOutboundCell = header.key === outboundHeaderKey;
                          const isFedexStatusCell = header.key === FEDEX_STATUS_KEY;
                          const isFedexTrackingCell = header.key === FEDEX_TRACKING_KEY;
                          const isFleetLoadIdCell =
                            canonicalizeHeaderLabel(header.label) ===
                            canonicalizeHeaderLabel(FLEET_LOAD_ID_LABEL);
                          const isChangedCell = diffMeta && diffMeta.changed !== false;
                          const stickyLeft = stickyOffsets[header.key];
                          const cellClass = [
                            "px-4 py-2 text-sm text-slate-700 whitespace-nowrap",
                            isChangedCell ? CHANGED_CELL_CLASS : "",
                            stickyLeft !== undefined ? "sticky z-10 bg-inherit" : "",
                          ]
                            .filter(Boolean)
                            .join(" ");
                          return (
                            <td
                              key={`${record._id}-${header.key}`}
                              style={{
                                width: widths[header.key],
                                minWidth: widths[header.key],
                                left: stickyLeft !== undefined ? stickyLeft : undefined,
                              }}
                              className={cellClass}
                              title={tooltipValue}
                            >
                              {header.key === EDI_STATUS_KEY ? (
                                <EdiStatusBadges {...ediForRow(record[ROUTING_ROW_ID_KEY])}
                                  loading={loadingRemote || ediForRow(record[ROUTING_ROW_ID_KEY]).loading} />
                              ) : header.key === '__loadHistory' ? (
                                <button className="text-blue-700 underline" onClick={() => setLoadHistoryRow({custCode: record[custHeaderKey], poNumber: String(record[poHeaderKey]), routingUid: record.__routingUid})}>View history</button>
                              ) : header.key === '__ilsLoad' ? (
                                <span>{record[header.key]}{record.__loadFreshness === 'Stale' && <span className="ml-2 rounded border px-1 text-xs">Stale</span>}</span>
                              ) : header.key === '__loadSync' ? (
                                <span className="inline-flex rounded border px-2 py-1 text-xs">{record[header.key]}</span>
                              ) : isStatusCell ? (
                                <div className="flex flex-wrap items-center gap-2">
                                  <StatusBadge value={record[header.key]} />
                                  {rowSuperseded && (
                                    <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-900">
                                      Superseded
                                    </span>
                                  )}
                                </div>
                              ) : isOnHoldCell ? (
                                <OnHoldBadge value={record[header.key]} />
                              ) : isD2mCell ? (
                                renderD2mCellValue(record[header.key])
                              ) : isWmsCell ? (
                                renderWmsCellValue(record[header.key], wmsUpdatedAt)
                              ) : isFedexStatusCell ? (
                                <button
                                  type="button"
                                  className="text-left"
                                  onClick={() => void openFedexDetails({
                                    labelId: fedexSummary?.currentLabelId,
                                    routingRowId: fedexSummary?.routingRowId || record?.[ROUTING_ROW_ID_KEY],
                                    routingUid: fedexSummary?.routingUid || record?.[ROUTING_UID_KEY],
                                  })}
                                >
                                  <FedexStatusBadge summary={fedexSummary} compact />
                                </button>
                              ) : isFedexTrackingCell ? (
                                <button
                                  type="button"
                                  className="text-left"
                                  onClick={() => void openFedexDetails({
                                    labelId: fedexSummary?.currentLabelId,
                                    routingRowId: fedexSummary?.routingRowId || record?.[ROUTING_ROW_ID_KEY],
                                    routingUid: fedexSummary?.routingUid || record?.[ROUTING_UID_KEY],
                                  })}
                                >
                                  <FedexTrackingText summary={fedexSummary} />
                                </button>
                              ) : isFleetLoadIdCell ? (
                                formatFleetLoadIdDisplay(record[header.key])
                              ) : isOutboundCell && rowSuperseded ? (
                                <div className="flex flex-col gap-1 whitespace-normal">
                                  <span>{renderCellValue(record[header.key], header.type)}</span>
                                  <span className="text-xs font-medium text-amber-800">{supersededHint}</span>
                                </div>
                              ) : isPoCell ? (
                                <span
                                  className="po-text-select po-copy-source"
                                  data-po={record[header.key] ?? ""}
                                >
                                  {renderCellValue(record[header.key], header.type)}
                                </span>
                              ) : (
                                renderCellValue(record[header.key], header.type)
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={visibleHeaders.length + 1} className="px-4 py-8 text-center text-sm text-slate-500">
                      No data.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <span>Rows per page:</span>
              <select
                value={pageSize}
                onChange={(event) => {
                  const value = event.target.value;
                  setPageSize(value === "All" ? "All" : parseInt(value, 10));
                  setPage(1);
                }}
                className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {PAGE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="tabular-nums text-xs text-slate-500">
                {sortedRows.length === 0 ? 0 : startIdx + 1} - {endIdx} of {sortedRows.length}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => setPage(1)}
                  disabled={page <= 1}
                  aria-label="First page"
                >
                  <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page <= 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                </button>
                <span className="tabular-nums">
                  {page} / {totalPages}
                </span>
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page >= totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="rounded border border-slate-300 px-2 py-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  aria-label="Last page"
                >
                  <ChevronsRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          {isMrpShippedDrilldown && (
            <section className="mt-4 rounded-lg border border-slate-300 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Allocation Breakdown (Shipped Qty Source)</h2>
                  <p className="text-sm text-slate-600">
                    Shipped Qty is computed from routing_x_pos_items.alloc_qty (allocation).
                  </p>
                </div>
              </div>

              {breakdownWarning && (
                <div className="mt-3">
                  <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <p className="font-semibold">{breakdownWarning.title}</p>
                    <p>{breakdownWarning.body}</p>
                  </div>
                </div>
              )}

              <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-700 md:grid-cols-4">
                <div className="rounded-md bg-slate-50 px-3 py-2">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Total Shipped Qty</div>
                  <div className="font-semibold">{formatQty(shippedBreakdown?.totalAllocQty)}</div>
                </div>
                <div className="rounded-md bg-slate-50 px-3 py-2">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Total Routing Rows</div>
                  <div className="font-semibold">{(shippedBreakdown?.routingRowCount || 0).toLocaleString()}</div>
                </div>
                <div className="rounded-md bg-slate-50 px-3 py-2">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Total PO Lines</div>
                  <div className="font-semibold">{(shippedBreakdown?.poLineCount || 0).toLocaleString()}</div>
                </div>
                <div className="rounded-md bg-slate-50 px-3 py-2">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Total Load IDs</div>
                  <div className="font-semibold">{(shippedBreakdown?.loadIdCount || 0).toLocaleString()}</div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <div className="inline-flex rounded-md border border-slate-300 bg-white p-1 text-sm">
                  <button
                    type="button"
                    onClick={() => setBreakdownView("sku")}
                    className={`rounded px-3 py-1.5 ${
                      breakdownView === "sku" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    Summary by SKU
                  </button>
                  <button
                    type="button"
                    onClick={() => setBreakdownView("details")}
                    className={`rounded px-3 py-1.5 ${
                      breakdownView === "details" ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                    }`}
                  >
                    Details
                  </button>
                </div>
                <input
                  type="text"
                  value={breakdownSearch}
                  onChange={(event) => setBreakdownSearch(event.target.value)}
                  placeholder="Search SKU / PO / Outbound"
                  className="w-full max-w-xs rounded border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {drilldownXPosLoading ? (
                <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  Loading allocation breakdown...
                </div>
              ) : drilldownXPosError ? (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {drilldownXPosError}
                </div>
              ) : breakdownView === "sku" ? (
                <div className="mt-4 max-h-[420px] overflow-auto rounded-md border border-slate-200">
                  <table className="min-w-[420px] w-full border-collapse text-sm">
                    <thead className="bg-slate-100 text-slate-700">
                      <tr>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold">SKU (walmart_item_no)</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-right font-semibold">Total Alloc Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdownSkuRows.length ? (
                        breakdownSkuRows.map((row) => (
                          <tr key={row.sku} className="odd:bg-white even:bg-slate-50">
                            <td className="border-t border-slate-200 px-3 py-2 text-slate-800">{row.sku}</td>
                            <td className="border-t border-slate-200 px-3 py-2 text-right font-mono text-slate-800">
                              {formatQty(row.totalAllocQty)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={2} className="px-3 py-6 text-center text-slate-500">
                            No allocation rows.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-4 max-h-[420px] overflow-auto rounded-md border border-slate-200">
                  <table className="min-w-[860px] w-full border-collapse text-sm">
                    <thead className="bg-slate-100 text-slate-700">
                      <tr>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold">SKU (walmart_item_no)</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold">PO Number</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold">Outbound Code</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-left font-semibold">Shipped Date</th>
                        <th className="border-b border-slate-200 px-3 py-2 text-right font-semibold">Alloc Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakdownDetailRows.length ? (
                        breakdownDetailRows.map((row) => (
                          <tr key={row.rowKey} className="odd:bg-white even:bg-slate-50">
                            <td className="border-t border-slate-200 px-3 py-2 text-slate-800">{row.sku}</td>
                            <td className="border-t border-slate-200 px-3 py-2 text-slate-800">{row.poNumber || "-"}</td>
                            <td className="border-t border-slate-200 px-3 py-2 text-slate-800">{row.outboundCode || "-"}</td>
                            <td className="border-t border-slate-200 px-3 py-2 text-slate-800">{row.shippedDate || "-"}</td>
                            <td className="border-t border-slate-200 px-3 py-2 text-right font-mono text-slate-800">
                              {formatQty(row.allocQty)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                            No allocation rows.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </>
      ) : loadingRemote ? (
        <div className="flex-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-slate-500">
          <p className="text-sm font-medium">Loading Routing Status data...</p>
          <p className="mt-1 text-xs">Fetching the latest workbook from the server.</p>
        </div>
      ) : (
        <div className="flex-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-slate-500">
          <p className="text-sm font-medium">No Routing Status data loaded yet.</p>
          <p className="mt-1 text-xs">
            Use the <span className="font-semibold text-slate-700">Import</span> button above to select a workbook or CSV file.
          </p>
        </div>
      )}
      </div>
    </>
  );
}


